import { Component, EventEmitter, Input, OnDestroy, OnInit, DoCheck, Output, ChangeDetectorRef, ElementRef, ViewChild, inject } from '@angular/core';
import { Subject, Subscription, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MapService } from '../../../../../shared/services/map.service';
import { MapPopupService } from '../../../../../shared/services/map-popup.service';
import { GraphicFactoryService } from '../../../../../shared/services/graphic-factory.service';
import { DateFormatPipe } from '../../../../../shared/pipes';
import { debounceTime, distinctUntilChanged, filter, takeUntil, startWith, take, withLatestFrom } from 'rxjs/operators';
import { issueTypesFilter, projectDetails } from '../../../core/projects.selectors';
import { Project } from '../../../../../shared/models';
import Extent from '@arcgis/core/geometry/Extent';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { ImageProcessingService } from '../../../../../shared/services/image-processing.service';
import { MapSymbolFactory } from '../../../../../shared/services/map-symbol-factory.service';
import { GeometryService } from '../../../../../shared/services/geometry.service';
import { UnifiedMarkerService, MarkerItem, UnifiedLayerConfig, MarkerClickContext, MarkerHoverContext, PictureHoverData } from '../../../../../shared/services/unified-marker.service';
import { DrilldownModeService, DrilldownConfig, DrilldownContext } from '../../../../../shared/services/drilldown-mode.service';
import { StationRangeMapGeometryService } from '../../../../../shared/services/station-range-map-geometry.service';
import { clampZoomToPublishedMax, ProjectMapManifestService } from '../../../../../shared/services/project-map-manifest.service';
import { CapturedHereRow } from '../../../../../shared/models/map-feature-link';
import { CAPTURED_HERE_ROWS, capturedHereCountText, CapturedHereItem, capturedHereItem, capturedHereMoreText,
  LINKED_ONLY_FILTER, markerIsLinked } from '../../../../../shared/helpers/design-context';
import { PictureMarkerService, PictureLayerConfig, PictureClickContext } from '../../../../../shared/services/picture-marker.service';
import { MapSwipeService } from '../../../../../shared/services/map-swipe.service';
import { MapSpatialSelectService } from '../../../../../shared/services/map-spatial-select.service';
import { ProjectSpatialSummaryService } from '../../../../../shared/services/project-spatial-summary.service';
import { MapHeatmapContributionsService } from '../../../../../shared/services/map-heatmap-contributions.service';
import { MapReferenceLayerService } from '../../../../../shared/services/map-reference-layer.service';
import { ProjectMapInspectionService } from '../../../../../shared/services/project-map-inspection.service';
import { boundsIntersect, distanceText, featureHighlightSymbol, featureIssueData, featureLabel, geoJsonToArcGis, InspectionQuery, inspectionTolerance, MapBounds,
  layerDrawsAtScale, MapFeatureCandidate, titleLookAlikes, validMapBounds } from '../../../../../shared/helpers/project-map-inspection';
import { IssueDetailsWrapperComponent } from '../../../pages/site-details-page/tab-screens/daily-report/daily-report-components/detail-components/issue-details/issue-details-wrapper/issue-details-wrapper.component';
import { AuthService } from '../../../../../shared/services/auth.service';
import { layerPreferenceKey, loadBasemapPreference, resolveBasemap, resolveLayerState, saveBasemapPreference, LayerViewState } from '../../../../../shared/helpers/map-layer-view-state';
import { Ability, subject } from '@casl/ability';
import { ProjectReferenceLayerService } from '../../../../../shared/services/project-reference-layer.service';
import { ClusterableMarker, MapPointClusterService, MarkerCluster } from '../../../../../shared/services/map-point-cluster.service';
import { ProjectsService } from '../../../core/projects.service';
import {
  CreateOrEditMapLayerComponent,
} from '../../../pages/projects-details-page/components/tab-screens/settings/map-layers/create-or-edit-map-layer/create-or-edit-map-layer.component';
import { MapStore } from '../state/map.store';
import { MAP_VIEW_BY_COLORS, WARRANTY_CLAIM_STATUS_COLORS, getLayerDefaults } from '../map.constants';
import { PreviewPicture } from '../../../../../shared/components/picture-hover-preview/picture-hover-preview.component';
import {
  MapDateRange,
  MapLayerSettings,
  MapLabelConfig,
  LabelByOption,
  ScreenPoint,
  SiteMarker,
  PictureMarker,
  BidItemMarker,
  ActivityMarker,
  DeliveryMarker,
  ReportGeometry,
  MapComponentInstance,
  MultiItemLocationGroup,
  DEFAULT_MAP_TOOLBAR_STATE,
  MapToolbarState,
  RenderMode,
  SwipePresetId,
} from '../../../../../shared/types';
import { computeOutlierReferences, rejectOutlierLocation } from '../../../../../shared/helpers/geo-outlier';
import { projectAlignments } from '../../../../../shared/helpers/alignment-stationing';
import {
  isForegroundHit, ReferenceClickResult, referencePanelWins, showAlignmentChip,
} from '../../../../../shared/helpers/stationing-layer';
import { StationingPreferenceService } from '../../../../../shared/services/stationing-preference.service';
import { StationingMapLayer } from '../stationing-map-layer';
import { hasViewSize, isUsableFitTarget, openingFitAction, OpeningFitTrigger } from '../map-opening-fit';

/** Features listed for one click; the rest are counted, not listed (as in the layer drawer's preview). */
const MAX_REFERENCE_CHOICES = 6;

import {
  IssueMapResult,
  issueHasNoLocation,
  issueNoLocationLabel,
  issueNoLocationPopup,
  noLocationIssueOf,
} from '../../../../../shared/helpers/issues-tab';
import { IssueService } from '../../../../issue/issue.service';
/** Pushes every [lon, lat] position of a GeoJSON coordinates array (any nesting) onto `out`. */
function collectLonLats(coords: unknown, out: { geometry: { longitude: number; latitude: number } }[]): void {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    out.push({ geometry: { longitude: coords[0], latitude: coords[1] } });
    return;
  }
  coords.forEach(c => collectLonLats(c, out));
}

/** Header map: boundary colour for sites with no reports in the date range. */
const MUTED_SITE_COLOR = '#7A8496';

/** A site boundary the header map builds from the site's own geometry. */
interface HeaderSiteGeometry {
  type: string;
  coordinates: unknown;
  siteId: number;
  isSiteGeometry: true;
  isHeaderSiteGeometry: true;
  muted: boolean;
  attributes: Record<string, unknown>;
}

@Component({
    selector: 'app-sites-location-map',
    templateUrl: './sites-location-map.component.html',
    styleUrls: ['./sites-location-map.component.scss'],
    providers: [MapStore, MapReferenceLayerService],
    standalone: false
})
export class SitesLocationMapComponent implements OnInit, OnDestroy, DoCheck {
  @Input() fullscreenElement: HTMLElement;
  /** Set by hosts whose View panel carries the Design tab; the right-rail control then shows only the basemap. */
  @Input() projectLayersInSidenav = false;
  @Input() set hoveredSiteId(siteId: string | null) {
    this._hoveredSiteId = siteId;
    // Always call - applyHoverFadeEffect handles null mapView internally
    this.applyHoverFadeEffect(siteId);
  }
  get hoveredSiteId() {
    return this._hoveredSiteId;
  }
  private _hoveredSiteId: string | null = null;

  @Input() set hoveredBidItemId(bidItemId: string | null) {
    this._hoveredBidItemId = bidItemId;
    // Apply hover fade effect for bid items
    this.applyBidItemHoverFadeEffect(bidItemId);
    // Embedded Form 266 viewer only: show the hovered bid item's quantity as a
    // centroid label (main project map keeps its own labelConfig untouched).
    if (this.showEmbeddedControls) {
      this.updateHoveredBidItemQuantityLabel(bidItemId);
    }
  }
  get hoveredBidItemId() {
    return this._hoveredBidItemId;
  }
  private _hoveredBidItemId: string | null = null;

  @Input() set hoveredDate(date: string | null) {
    this._hoveredDate = date;
    // Apply hover fade effect for dates (day view)
    this.applyDateHoverFadeEffect(date);
  }
  get hoveredDate() {
    return this._hoveredDate;
  }
  private _hoveredDate: string | null = null;

  @Input() set viewBy(mode: 'site' | 'bid_item' | 'day' | 'inspector') {
    const changed = this._viewBy !== mode;
    this._viewBy = mode;
    if (changed && this.mapComponentInstance) {
      // Rebuild color maps and redraw when view mode changes
      // Only skip zoom if we're in fullscreen (actual view mode change while viewing map)
      // When exiting fullscreen, viewBy resets to default and we DO want zoom to happen
      const shouldSkipZoom = this.isFullScreen;
      if (shouldSkipZoom) {
        this.isRecoloringOnly = true;
      }
      this.rebuildColorMapsForViewMode();
      this.drawReportGeometries(false, shouldSkipZoom); // skipSiteGeometries=false, skipZoom based on fullscreen
      // Also redraw unified markers to apply new coloring
      this.drawUnifiedMarkers(this.currentBidItemFilter).finally(() => {
        this.isRecoloringOnly = false;
      });
    }
  }
  get viewBy() {
    return this._viewBy;
  }
  private _viewBy: 'site' | 'bid_item' | 'day' | 'inspector' = 'site';

  // Bid items with geometries (sorted by item number) - used for consistent color assignment
  @Input() set bidItemsWithGeometries(items: Array<{ id: string, item: string, description: string }>) {
    this._bidItemsWithGeometries = items || [];
    // Rebuild color map when bid items change (ensures same colors as sidenav)
    if (this._viewBy === 'bid_item') {
      this.rebuildColorMapsForViewMode();
    }
  }
  get bidItemsWithGeometries() {
    return this._bidItemsWithGeometries;
  }
  private _bidItemsWithGeometries: Array<{ id: string, item: string, description: string }> = [];

  // Single day mode flag (for daily report map)
  @Input() isSingleDayMode: boolean = false;

  // Surface the floating map controls (basemaps + zoom + layers + type) outside
  // fullscreen — used by the embedded Form 266 viewer map pane. Scoped so the
  // regular single-day site daily-report map keeps controls fullscreen-only.
  @Input() showEmbeddedControls: boolean = false;

  // Available inspectors (for inspector view mode in single day mode)
  @Input() set availableInspectors(inspectors: Array<{ id: string, name: string, email?: string, color?: string }>) {
    const hadInspectors = this._availableInspectors.length > 0;
    this._availableInspectors = inspectors || [];
    // Rebuild color map when inspectors change (ensures same colors as sidenav)
    if (this._viewBy === 'inspector') {
      this.rebuildColorMapsForViewMode();
      // If inspectors just became available and we have geometries, redraw to apply colors
      if (!hadInspectors && this._availableInspectors.length > 0 && this.mapComponentInstance && this.reportGeometries.length > 0) {
        this.drawReportGeometries(false, true); // skipSiteGeometries=false, skipZoom=true
      }
    }
  }
  get availableInspectors() {
    return this._availableInspectors;
  }
  private _availableInspectors: Array<{ id: string, name: string, email?: string, color?: string }> = [];

  // Hovered inspector ID (for hover fade effect)
  @Input() set hoveredInspectorId(inspectorId: string | null) {
    this._hoveredInspectorId = inspectorId;
    // Apply hover fade effect for inspectors
    this.applyInspectorHoverFadeEffect(inspectorId);
  }
  get hoveredInspectorId() {
    return this._hoveredInspectorId;
  }
  private _hoveredInspectorId: string | null = null;

  // Sticky selection inputs (multi-select) - used for persistent highlighting
  @Input() set selectedSiteIds(ids: Set<string>) {
    this._selectedSiteIds = ids || new Set();
    // Apply selection highlight effect (reuse hover fade logic)
    this.applySelectionFadeEffect();
  }
  get selectedSiteIds() {
    return this._selectedSiteIds;
  }
  private _selectedSiteIds: Set<string> = new Set();

  @Input() set selectedBidItemIds(ids: Set<string>) {
    this._selectedBidItemIds = ids || new Set();
    // Apply selection highlight effect
    this.applySelectionFadeEffect();
  }
  get selectedBidItemIds() {
    return this._selectedBidItemIds;
  }
  private _selectedBidItemIds: Set<string> = new Set();

  @Input() set selectedInspectorIds(ids: Set<string>) {
    this._selectedInspectorIds = ids || new Set();
    // Apply selection highlight effect
    this.applySelectionFadeEffect();
  }
  get selectedInspectorIds() {
    return this._selectedInspectorIds;
  }
  private _selectedInspectorIds: Set<string> = new Set();

  @Input() set selectedDates(dates: Set<string>) {
    this._selectedDates = dates || new Set();
    // Apply selection highlight effect
    this.applySelectionFadeEffect();
  }
  get selectedDates() {
    return this._selectedDates;
  }
  private _selectedDates: Set<string> = new Set();

  // Dates with reports (sorted chronologically) - used for consistent color assignment in day view
  @Input() set datesWithReports(dates: string[]) {
    this._datesWithReports = dates || [];
    // Rebuild color map when dates change (ensures same colors as sidenav)
    if (this._viewBy === 'day') {
      this.rebuildColorMapsForViewMode();
      // Also redraw geometries and markers with new colors (skipZoom=true, just re-coloring)
      if (this.mapComponentInstance) {
        this.drawReportGeometries(false, true);
        this.drawUnifiedMarkers(this.currentBidItemFilter);
      }
    }
  }
  get datesWithReports() {
    return this._datesWithReports;
  }
  private _datesWithReports: string[] = [];

  // Dynamic control-form group toggles (passed through to map-controls so the
  // floating controls show the same per-group toggles as the sidenav Types tab).
  @Input() availableFormGroups: Array<{ id: string; label: string; icon: string | null }> = [];

  @Input() set dateRange(range: any) {
    if (range && range.start && range.end) {
      const rangeChanged = !this._dateRange ||
        !this._dateRange.start ||
        !this._dateRange.end ||
        this._dateRange.start.getTime() !== range.start.getTime() ||
        this._dateRange.end.getTime() !== range.end.getTime();

      this._dateRange = range;

      if (rangeChanged && this.isFullScreen) {
        // Trigger map refresh with new date range
        this.refreshMapData();
      }
    }
  }
  get dateRange() {
    return this._dateRange;
  }
  private _dateRange: any;
  private _currentLabelConfig: MapLabelConfig = { enabled: false, labelBy: 'bid_item_name' };
  private _currentColoredGeometries: any[] = [];

  @Input() set labelConfig(config: MapLabelConfig) {
    if (config) {
      this.mapStore.setLabelConfig(config);
    }
  }

  @Output() fullscreenChange = new EventEmitter<boolean>();
  @Output() layerChange = new EventEmitter<{ siteBoundary: boolean; picture: boolean; asBuilt: boolean }>();
  @Output() typeFilterChange = new EventEmitter<string[]>();
  @Output() geometryClick = new EventEmitter<{ report_bid_item_id: string; bid_item_id: string; gps_feature_index: number | null; siteId: string; reportId: string }>();

  isFullScreen = false;
  private wasFullScreen = false; // Track previous fullscreen state to detect transitions
  // First meaningful extent zoom — flipped true after the initial animateToExtent
  // succeeds so subsequent data refreshes obey the "don't re-zoom in fullscreen"
  // guards. Without this, direct-URL loads (where fullscreen is true from tick 0
  // and data arrives AFTER the first initializeMap call) get stuck at world view
  // because the fullscreen skip guard suppresses every subsequent zoom attempt.
  private hasPerformedInitialZoom = false;

  // Map advanced widgets toolbar state
  toolbarState: MapToolbarState = { ...DEFAULT_MAP_TOOLBAR_STATE };
  // Render mode read from the URL that should be applied after markers first draw.
  pendingRenderMode?: RenderMode | null = null;

  // Spatial summary state is owned by ProjectSpatialSummaryService — map-sidenav
  // subscribes directly. This component just pushes results in.
  // Swipe preset read from the URL that should be applied once the map view is ready.
  pendingSwipePreset?: SwipePresetId | null = null;
  mapComponentInstance: any;
  markersList: any[] = [];
  sites: any[] = [];
  filteredSites: any[] = [];
  projectData: any = null;
  showTooltip = false;
  tooltipContent = '';
  screenPoint: ScreenPoint = {
    x: 0,
    y: 0,
  };
  reportGeometries: any[] = [];
  sitesWithGeometries: string[] = [];
  siteColorMap: { [siteId: string]: string } = {};
  bidItemColorMap: { [bidItemId: string]: string } = {}; // Color map for bid items (used in bid_item view mode)
  inspectorColorMap: { [inspectorId: string]: string } = {}; // Color map for inspectors (used in inspector view mode)
  dateColorMap: { [date: string]: string } = {}; // Color map for dates (used in day view mode)
  private siteGeometriesDrawn = false; // Track if site geometries have been drawn at least once
  private isRecoloringOnly = false; // Track when we're just changing colors (view mode change), skip zoom

  // Color palette (shared across all View By modes)
  private readonly colorPalette = MAP_VIEW_BY_COLORS;

  // Track previous state to detect actual changes
  private previousSitesWithGeometries: string[] = [];
  private previousLayerSettings: MapLayerSettings = getLayerDefaults('fullscreen');
  private previousReportGeometrySignature = '';
  private isSliderDragging = false;
  private isPlaying = false;
  private geometryDataReceived = false; // Track if geometry data has been loaded at least once
  private hasZoomedToUnifiedMarkers = false; // Track if we've zoomed to include unified markers in single day mode
  /** Set once the user drags or zooms; an opening refit must never fight that. */
  private userMovedMapSinceOpen = false;
  /** Hardening (world-zoom report): the opening fit and the view width it was made at, redone once if that was narrow */
  private openingFit: { extent: Extent; width: number; height: number } | null = null;
  private openingFitRedone = false;
  private openingFitWaiting = false;
  private openingFitWatches: { remove(): void }[] = [];
  /** The opening goTo still animating, joined by a repeat of the same fit instead of restarting it */
  private openingGoToInFlight: { extent: Extent; done: Promise<unknown> } | null = null;
  /** The opening fit has reached its target (or the user has taken the map): until then a wheel does not zoom */
  private openingFitLanded = false;

  // Sequence numbers to prevent race conditions in async hover operations
  private siteHoverSequence = 0;
  private pictureFadeSequence = 0;
  private bidItemHoverSequence = 0;
  private inspectorHoverSequence = 0;

  // Layer settings property - initialized with fullscreen defaults
  layerSettings: MapLayerSettings = getLayerDefaults('fullscreen');

  // Picture layer properties
  pictureMarkers: any[] = [];
  pictureFeatureLayer: FeatureLayer | null = null;
  // Dedicated, drilldown-only layer for the selected item's station range.
  // Always visible (NOT gated by the asBuilt toggle), sits just below the unified
  // markers layer. Driven entirely off mapService.selectedItemData — never holds
  // aggregate ranges, so there is no filtering/temp-layer/race/exit-leftover.
  stationRangeLayer: GraphicsLayer | null = null;
  /** 7.2: Stationing on the map (stationing-map-layer.ts) */
  private stationing: StationingMapLayer | null = null;
  private stationingSub: Subscription | null = null;
  private stationingProjectId: string | null = null;
  private readonly stationingPreference = inject(StationingPreferenceService);
  /** Spec §3a: the clicked point's station line in a record's popup and in a design feature's details */
  itemPopupStationLine: string | null = null;
  /** §3c: the active alignment chip (projects with two or more alignments, Stationing on) */
  @ViewChild('stationingChipHost', { static: true }) private stationingChipHost?: ElementRef<HTMLElement>;
  stationingAlignmentName: string | null = null;
  stationingAlignmentLocked = false;
  stationingAlignmentNames: string[] = [];
  private stationingAlignmentSub: Subscription | null = null;
  referenceStationLine: string | null = null;
  /** The clicked points those lines were read at, so they follow a change of active alignment (§3c) */
  private itemPopupMapPoint: __esri.Point | null = null;
  private referenceMapPoint: __esri.Point | null = null;

  // Monotonic token bumped on every drilldown enter/exit. enterDrilldownMode
  // awaits the layerView settle, so a rapid second drilldown (or an exit) must be
  // able to invalidate the in-flight one before it draws/zooms a stale range.
  private drilldownToken = 0;

  pictureGraphicsLayer: GraphicsLayer | null = null; // TEMP: Use GraphicsLayer instead of FeatureLayer for debugging
  sameLocationGraphicsLayer: any = null; // GraphicsLayer for same-location picture groups
  // Map to store pictures for each marker UUID (to avoid losing data in clustering)
  private markerPicturesMap: Map<string, any[]> = new Map();

  // Map to store all items (pictures, bid items, activities, deliveries) for multi-item locations
  private multiItemLocationsMap: Map<string, { pictures: any[], bidItems: any[], activities: any[], deliveries: any[], forms: any[] }> = new Map();
  private previousPictureMarkersHash: string = ''; // Track if picture data has changed

  // Bid item layer properties
  bidItemMarkers: any[] = [];
  bidItemNoLocationCount: number = 0;
  bidItemNoLocationItems: any[] = []; // Actual bid items without location for popup (filtered)
  bidItemFeatureLayer: FeatureLayer | null = null;
  private previousBidItemMarkersHash: string = ''; // Track if bid item data has changed
  private currentBidItemFilter: string[] | undefined = undefined; // Current bid item filter (undefined = show all)

  // Original (unfiltered) no-location items - used as source for filtering
  private originalBidItemNoLocationItems: any[] = [];
  private originalActivityNoLocationItems: any[] = [];
  private originalDeliveryNoLocationItems: any[] = [];

  // Bid item popup state
  // Generic popup for all item types (bid items, activities, deliveries)
  private _showItemPopup = false;
  get showItemPopup(): boolean { return this._showItemPopup; }
  set showItemPopup(value: boolean) {
    this._showItemPopup = value;
    this.syncLabelSources();
  }
  itemPopupContent: any[] = [];
  itemPopupScreenPoint: ScreenPoint;

  // Picture hover preview state
  showPictureHoverPreview = false;
  pictureHoverPreviewData: PreviewPicture[] = [];
  pictureHoverScreenPoint: { x: number; y: number } = { x: 0, y: 0 };

  // Activity layer properties
  activityMarkers: any[] = [];
  activityNoLocationCount: number = 0;
  activityNoLocationItems: any[] = []; // Actual activities without location for popup
  activityFeatureLayer: FeatureLayer | null = null;
  private previousActivityMarkersHash: string = ''; // Track if activity data has changed

  // Delivery layer properties
  deliveryMarkers: any[] = [];
  deliveryNoLocationCount: number = 0;
  deliveryNoLocationItems: any[] = []; // Actual deliveries without location for popup
  deliveryFeatureLayer: FeatureLayer | null = null;
  private previousDeliveryMarkersHash: string = ''; // Track if delivery data has changed

  // Control form layer properties
  formMarkers: any[] = [];
  formNoLocationCount: number = 0;
  formNoLocationItems: any[] = []; // Actual forms without location for popup
  private originalFormNoLocationItems: any[] = [];

  // Warranty claim layer properties
  warrantyClaimMarkers: any[] = [];
  warrantyClaimFeatureLayer: GraphicsLayer | null = null;
  private warrantyClaimClickHandler: any = null;
  private warrantyClaimHoverHandler: any = null;

  // Zoom-driven re-clustering for the claim/punchlist-item and issue GraphicsLayers.
  // One watcher serves both layers; graphics are rebuilt (not the layers or their
  // hit-test handlers) once the zoom settles after a wheel/pinch/button change.
  private markerClusterZoomWatchHandle: __esri.WatchHandle | null = null;
  private markerClusterRedrawTimeout: any = null;
  private markerClusterLastZoom: number | null = null;

  // Warranty claim hover/click popup state
  showClaimPopup = false;
  claimPopupData: any = null;
  claimPopupClaims: any[] | null = null;
  claimPopupScreenPoint: { x: number; y: number } = { x: 0, y: 0 };
  private claimPopupHideTimeout: any = null;
  private hoveredClaimUuid: string | null = null;

  // Warranty issue layer properties (potential-warranty issues shown alongside claims)
  warrantyIssueMarkers: any[] = [];
  warrantyIssueFeatureLayer: GraphicsLayer | null = null;
  private warrantyIssueClickHandler: any = null;
  private warrantyIssueHoverHandler: any = null;

  // Warranty issue hover/click popup state
  showIssuePopup = false;
  issuePopupData: any = null;
  issuePopupIssues: any[] | null = null;
  /** Issues tab: matching issues with no location (GET /v1/issue/for-map meta); 0 hides the badge. */
  issuesNoLocationCount = 0;
  issuesNoLocationLabel = '';
  /** The for-map result the badge counts (its query lists the issues on click). */
  private issuesMapResult: IssueMapResult | null = null;
  private issuesNoLocationLoading = false;
  private readonly issueService = inject(IssueService);
  /** Issue category → label (the project's issue types), for the no-location cards. */
  private issueCategoryLabels: Record<string, string> = {};
  issuePopupScreenPoint: { x: number; y: number } = { x: 0, y: 0 };
  private issuePopupHideTimeout: any = null;
  private hoveredIssueUuid: string | null = null;

  // Unified clustering layer (combines pictures, bid items, activities, deliveries, and eventually site geometries/as-builts)
  unifiedMarkersLayer: FeatureLayer | null = null;
  private previousUnifiedMarkersHash: string = ''; // Track if unified data has changed
  private previousUnifiedSymbolsHadCIM: boolean = false; // Track if previous render had CIM symbols
  private previousUnifiedMarkerUuids: Set<string> = new Set(); // Track UUIDs for diff-based updates (Phase 4.1)

  // Drilldown mode - no-location pictures
  drilldownNoLocationPictures: any[] = []; // Pictures without lat/lon in drilldown mode
  drilldownNoLocationCount: number = 0;

  // Main mode - no-location pictures
  mainNoLocationPictures: any[] = []; // Pictures without lat/lon in main mode
  mainNoLocationPicturesCount: number = 0;

  // Click handler reference for cleanup
  private unifiedLayerClickHandler: any = null;

  prevTab = '';

  private readonly onDestroy: Subject<any> = new Subject<any>();
  private overlayLoadGeneration = 0;
  /** Published layer ids from the last catalog load, so a publish can be spotted. */
  private knownPublishedLayerIds: Set<string> | null = null;
  private knownPublishedScope = '';
  /** Catalog revision behind knownPublishedLayerIds; only a newer revision can be a publish. */
  private knownCatalogRevision: number | null = null;
  private overlayPreferenceKey: string | null = null;
  /** The per-user, per-project basemap (shared with the design-context Location picker); null = Default. */
  basemapId: string | null = null;
  /** `account:project` whose saved basemap has been applied, so it is restored once per scope. */
  private restoredBasemapScope: string | null = null;
  private overlayOverrides: Record<string, Partial<LayerViewState>> = {};
  private selectedBidItemMarkerId: string | null = null;
  selectedItemData: any = null; // Generic: bid items, activities, deliveries, etc. (public for template access)
  private highlightGraphic: any = null; // Store the highlight graphic
  private previousMapExtent: any = null; // Store previous extent before detail mode
  private previousMapZoom: number | null = null; // Store previous zoom before detail mode
  private hiddenAsBuiltGraphics: any[] = []; // Store hidden as-built graphics during filtering
  private tempFilteredAsBuiltLayer: any = null; // Temporary layer for filtered as-builts

  // Esri watcher handles for proper cleanup (memory leak prevention)
  private zoomWatchHandle: __esri.WatchHandle | null = null;
  private interactingWatchHandle: __esri.WatchHandle | null = null;
  private clusteringZoomWatchHandle: __esri.WatchHandle | null = null;

  // Animation coordination - prevents marker layer creation during zoom animations
  // This fixes the race condition where clusters get positioned at wrong extent
  private initialAnimationPromise: Promise<void> | null = null;

  // Basemaps properties
  organizationBasemaps: any[] = [];
  mapScreenshotUrl?: string;

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private mapService: MapService,
    private mapPopupService: MapPopupService,
    private graphicFactory: GraphicFactoryService,
    private dateFormatPipe: DateFormatPipe,
    private imageProcessingService: ImageProcessingService,
    private mapSymbolFactory: MapSymbolFactory,
    private geometryService: GeometryService,
    private unifiedMarkerService: UnifiedMarkerService,
    private drilldownModeService: DrilldownModeService,
    private pictureMarkerService: PictureMarkerService,
    private mapStore: MapStore,
    private cdr: ChangeDetectorRef,
    private mapSwipeService: MapSwipeService,
    private mapSpatialSelectService: MapSpatialSelectService,
    private mapHeatmapContributionsService: MapHeatmapContributionsService,
    private mapReferenceLayerService: MapReferenceLayerService,
    private mapInspectionService: ProjectMapInspectionService,
    private projectSpatialSummaryService: ProjectSpatialSummaryService,
    private projectsService: ProjectsService,
    private refLayerService: ProjectReferenceLayerService,
    private stationRangeMapGeometry: StationRangeMapGeometryService,
    private mapPointClusterService: MapPointClusterService,
    private authService: AuthService,
    private ability: Ability,
  ) {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        const newFullScreen = 'fs' in qp;
        // Track fullscreen transitions before updating state
        const isEnteringFullScreen = !this.wasFullScreen && newFullScreen;
        const isExitingFullScreen = this.wasFullScreen && !newFullScreen;
        this.wasFullScreen = newFullScreen;
        this.isFullScreen = newFullScreen;
        this.mapStore.setFullScreen(this.isFullScreen);
        this.stationing?.setLabelsShown(this.isFullScreen);

        // Hide map-wrapper buttons when in fullscreen (we use our own controls)
        this.mapService.showWrapperButtons.next(!this.isFullScreen);

        // In single day mode, handle fullscreen transitions with unified marker zoom
        if (this.isSingleDayMode && (isEnteringFullScreen || isExitingFullScreen)) {
          // Delay to allow CSS transition and map container resize to complete
          const delay = isExitingFullScreen ? 800 : 500;
          setTimeout(() => {
            this.zoomToIncludeUnifiedMarkers();
          }, delay);
          return;
        }

        if (qp.tab !== 'sites' && qp.tab !== this.prevTab) {
          this.filteredSites = this.sites;
          // Don't zoom during play mode or if drilldown exit is in progress
          if (!this.isPlaying && !this.drilldownModeService.isExitInProgress) {
            this.initializeMap();
          }
        }

        if (this.prevTab !== qp.tab) {
          this.prevTab = qp.tab;
        }
      });
  }

  referenceInspectionOpen = false;
  referenceInspectionLoading = false;
  referenceInspectionMessage = '';
  referenceInspectionIncomplete = false;
  referenceCandidates: MapFeatureCandidate[] = [];
  selectedReferenceCandidate: MapFeatureCandidate | null = null;
  /** As in the layer drawer's preview: several hits start as a list, one opens its details. */
  referenceFeatureView: 'list' | 'details' = 'list';
  referenceCandidatesHidden = 0;
  private referenceHighlightLayer: GraphicsLayer | null = null;
  private inspectionView: __esri.MapView | null = null;
  private inspectionHandles: { remove(): void }[] = [];
  private inspectionController?: AbortController;
  private inspectionGeneration = 0;
  private mapClickSequence = 0;
  private inspectionQuery?: InspectionQuery;
  private inspectionCurrent?: () => boolean;

  /** Spec §7.3 "Captured here": the newest records linked to the open feature. */
  capturedHere: CapturedHereRow[] = [];
  capturedHereItems: CapturedHereItem[] = [];
  capturedHereTotal = 0;
  capturedHereLoading = false;
  capturedHereError = '';
  /** The user may not read map-layer links (403): the section is left out, not shown as an error. */
  capturedHereHidden = false;
  private capturedHereController?: AbortController;
  private manifestApi = inject(ProjectMapManifestService);
  /** account:project pairs that answered 403, so the section does not flash on every feature. */
  private capturedHereForbidden = new Set<string>();

  ngDoCheck(): void {
    // Includes account changes while an already-populated card is idle.
    if (this.inspectionCurrent && !this.inspectionCurrent()) this.closeReferenceInspection();
  }

  closeReferenceInspection(): void {
    this.inspectionGeneration++;
    this.inspectionController?.abort();
    this.inspectionController = undefined;
    this.inspectionCurrent = undefined;
    this.inspectionQuery = undefined;
    this.referenceInspectionOpen = false;
    this.referenceStationLine = null;
    this.referenceInspectionLoading = false;
    this.referenceInspectionMessage = '';
    this.referenceInspectionIncomplete = false;
    this.referenceCandidates = [];
    this.selectedReferenceCandidate = null;
    this.referenceFeatureView = 'list';
    this.referenceCandidatesHidden = 0;
    this.resetCapturedHere();
    this.highlightReferenceCandidate(null);
  }

  private resetCapturedHere(): void {
    this.capturedHereController?.abort();
    this.capturedHereController = undefined;
    this.capturedHere = [];
    this.capturedHereItems = [];
    this.capturedHereTotal = 0;
    this.capturedHereLoading = false;
    this.capturedHereError = '';
    this.capturedHereHidden = false;
  }

  get capturedHereCount(): string {
    return this.capturedHereLoading ? '…' : capturedHereCountText(this.capturedHereTotal);
  }

  get capturedHereMore(): string {
    return capturedHereMoreText(this.capturedHereTotal, this.capturedHere.length);
  }

  /**
   * Spec §7.3: the six newest records linked to the open feature, then "n more". A response for a
   * feature that is no longer open is dropped (the request is aborted and re-checked on arrival).
   * Errors stay inside the section: a 403 hides it, anything else shows a quiet note.
   */
  async loadCapturedHere(candidate: MapFeatureCandidate | null): Promise<void> {
    this.resetCapturedHere();
    const query = this.inspectionQuery;
    if (!candidate || !query) return;
    const forbiddenKey = `${this.authService.getCurrentUser()?.id ?? ''}:${query.projectId}`;
    if (this.capturedHereForbidden.has(forbiddenKey)) { this.capturedHereHidden = true; return; }
    const controller = new AbortController();
    this.capturedHereController = controller;
    const stale = () => controller.signal.aborted || this.capturedHereController !== controller
      || this.selectedReferenceCandidate !== candidate || this.inspectionQuery !== query;
    this.capturedHereLoading = true;
    try {
      const page = await this.manifestApi.links(query.projectId, candidate.layer.id, candidate.featureId, {
        catalogRevision: query.catalogRevision, sourceVersionId: candidate.layer.sourceVersionId,
        sublayerKey: candidate.layer.sublayerKey, start: 0, total: CAPTURED_HERE_ROWS,
      }, controller.signal);
      if (stale()) return;
      this.capturedHere = page.data.slice(0, CAPTURED_HERE_ROWS);
      this.capturedHereItems = this.capturedHere.map(capturedHereItem);
      this.capturedHereTotal = Math.max(page.meta.totalRecords, this.capturedHere.length);
    } catch (error) {
      if (stale()) return;
      if (error?.status === 403) {
        this.capturedHereForbidden.add(forbiddenKey);
        this.capturedHereHidden = true;
      } else {
        this.capturedHereError = 'Linked records could not be loaded.';
      }
    } finally {
      if (!stale()) {
        this.capturedHereLoading = false;
        if (this.inspectionView && !this.inspectionView.destroyed) this.cdr.detectChanges();
      }
    }
  }

  private setupReferenceInspection(view: __esri.MapView): void {
    this.closeReferenceInspection();
    this.inspectionHandles.forEach(handle => handle.remove());
    this.inspectionView = view;
    this.referenceHighlightLayer = null;
    this.inspectionHandles = [
      view.on('click', event => { void this.onMapClick(event); }),
      reactiveUtils.watch(() => view.stationary, stationary => {
        if (!stationary) { this.closeReferenceInspection(); this.cdr.detectChanges(); }
      }),
    ];
  }

  /**
   * Spec §3a: one owner per map click. The design-layer inspection runs first; a feature within its tolerance keeps
   * its card (with the station line) and no station callout draws, otherwise Stationing answers the click.
   */
  private async onMapClick(event: { x: number; y: number; mapPoint?: __esri.Point | null }): Promise<void> {
    const sequence = ++this.mapClickSequence;
    const result = await this.inspectReferenceClick(event);
    // A later click owns the map
    if (sequence !== this.mapClickSequence) return;
    const stationingAnswers = !!this.stationing?.answersClicks;
    if (referencePanelWins(result, stationingAnswers)) {
      this.stationing?.dismiss();
      return;
    }
    if (result !== 'none') {
      // An empty or viewing-only panel gives way to the station callout
      this.closeReferenceInspection();
      this.cdr.detectChanges();
    }
    await this.stationing?.onClick(event);
  }

  private async inspectReferenceClick(event: { x: number; y: number }): Promise<ReferenceClickResult> {
    this.closeReferenceInspection();
    const view = this.inspectionView;
    const scope = this.mapReferenceLayerService.getPublishedInspectionScope();
    if (!view || !scope || this.toolbarState.spatialSelectActive || !view.stationary
      || !view.spatialReference.isWebMercator || !Number.isFinite(event.x) || !Number.isFinite(event.y)) return 'none';
    const positions = () => new Map(view.map.layers.toArray().map((layer, index) => [layer, index]));
    const visibleEntries = (order: Map<__esri.Layer, number>) => this.mapReferenceLayerService.getActiveLayers().filter(entry => entry.published
      && entry.status === 'ready' && entry.layer.visible && entry.layer.opacity > 0
      // Clickable wherever it is drawn, not the published zoom range
      && order.has(entry.layer) && layerDrawsAtScale(entry.layer, view.scale)
      && this.mapSwipeService.isLayerVisibleAt(entry.layer, event.x, event.y, view));
    const initialOrder = positions();
    const entries = visibleEntries(initialOrder);
    if (!entries.length) return 'none';
    const entryOrder = new Map(entries.map(entry => [entry, initialOrder.get(entry.layer)]));
    const generation = this.inspectionGeneration;
    const overlayCurrent = this.captureOverlayOperation();
    const controller = new AbortController();
    this.inspectionController = controller;
    const current = () => {
      const active = this.mapReferenceLayerService.getPublishedInspectionScope();
      if (controller.signal.aborted || generation !== this.inspectionGeneration || !overlayCurrent()
        || this.authService.getCurrentUser()?.id !== scope.accountId || active?.projectId !== scope.projectId
        || active.accountId !== scope.accountId || active.catalogRevision !== scope.catalogRevision || !view.stationary) return false;
      // One linear snapshot also catches newly visible overlaps and changed draw order.
      const order = positions();
      const visible = visibleEntries(order);
      return visible.length === entries.length && visible.every(entry => entryOrder.has(entry)
        && entryOrder.get(entry) === order.get(entry.layer));
    };
    this.inspectionCurrent = current;
    try {
      // Existing capture/work interactions have priority over reference context.
      const hits = await view.hitTest(event);
      if (!current()) { if (generation === this.inspectionGeneration) this.closeReferenceInspection(); return 'none'; }
      const references = this.mapReferenceLayerService.getActiveLayers();
      const isReference = (node: unknown) => {
        let layer = node as __esri.Layer;
        for (let depth = 0; layer && depth < 16; depth++, layer = layer.parent as __esri.Layer) {
          if (references.some(entry => entry.layer === layer)) return true;
        }
        return false;
      };
      // Stationing's line, ticks and labels and a site's boundary are background here too, as for the station callout
      const background = this.stationing ? [this.stationing.layer, this.stationing.pickLayer] : [];
      if (hits.results.some(hit => hit.type === 'graphic' && isForegroundHit(hit, background) && !isReference(hit.graphic.layer))) return 'none';
      const point = view.toMap(event);
      this.referenceMapPoint = point;
      this.stationing?.focusPoint(point);
      this.referenceStationLine = this.stationing?.lineAt(point) ?? null;
      const radius = Math.max(...entries.map(entry => inspectionTolerance(entry.published)));
      const corners = [[-radius, -radius], [radius, -radius], [radius, radius], [-radius, radius]]
        .map(([dx, dy]) => view.toMap({ x: event.x + dx, y: event.y + dy }));
      if (!point || corners.some(corner => !corner)) return 'none';
      const bbox: MapBounds = [Math.min(...corners.map(corner => corner.longitude)), Math.min(...corners.map(corner => corner.latitude)),
        Math.max(...corners.map(corner => corner.longitude)), Math.max(...corners.map(corner => corner.latitude))];
      const nearby = entries.filter(entry => validMapBounds(entry.published.bounds) && boundsIntersect(entry.published.bounds, bbox));
      if (!nearby.length) return 'none';
      this.referenceInspectionOpen = true;
      this.referenceInspectionLoading = true;
      this.referenceInspectionMessage = '';
      this.cdr.detectChanges();
      if (!nearby.some(entry => entry.published.inspect === 'full')) {
        this.referenceInspectionMessage = 'This reference layer supports viewing only. Feature details are unavailable.';
        return 'viewing-only';
      }
      const query: InspectionQuery = { projectId: scope.projectId, catalogRevision: scope.catalogRevision,
        point: [point.longitude, point.latitude], bbox, resolution: view.resolution,
        layers: nearby.map(entry => ({ layer: entry.published, drawOrder: initialOrder.get(entry.layer) })) };
      this.inspectionQuery = query;
      const result = await this.mapInspectionService.inspect(query, controller.signal);
      if (!current()) { if (generation === this.inspectionGeneration) this.closeReferenceInspection(); return 'none'; }
      this.showReferenceCandidates(result.candidates, view.resolution * Math.cos(point.latitude * Math.PI / 180));
      this.referenceInspectionIncomplete = result.incomplete;
      if (!result.candidates.length) this.referenceInspectionMessage = 'No reference features found here.';
      return result.candidates.length ? 'candidates' : 'no-candidates';
    } catch (error) {
      if (!current()) { if (generation === this.inspectionGeneration) this.closeReferenceInspection(); return 'none'; }
      if ([401, 403, 410].includes(error?.status)) {
        this.mapReferenceLayerService.deactivate(view);
        this.closeReferenceInspection();
        this.toolbarState = { ...this.toolbarState, overlayError: 'Reference layer access has changed. Reload the map to continue.' };
        return 'none';
      }
      this.referenceInspectionOpen = true;
      this.referenceInspectionMessage = 'Feature details could not be loaded. Zoom in and click the layer to retry.';
      if (this.inspectionQuery && (!validMapBounds(this.inspectionQuery.bbox)
        || this.inspectionQuery.bbox[2] - this.inspectionQuery.bbox[0] > 1 || this.inspectionQuery.bbox[3] - this.inspectionQuery.bbox[1] > 1)) {
        this.referenceInspectionMessage = 'Zoom in to inspect reference features.';
      }
      return 'error';
    } finally {
      if (generation === this.inspectionGeneration) this.referenceInspectionLoading = false;
      // The view can be torn down while hitTest or the network request is pending.
      if (this.inspectionView === view) this.cdr.detectChanges();
    }
  }

  /** Topmost layer first, then nearest; each row named by its first popup field, with its layer and distance under it. */
  private showReferenceCandidates(candidates: MapFeatureCandidate[], metresPerPixel: number): void {
    this.referenceCandidatesHidden = Math.max(0, candidates.length - MAX_REFERENCE_CHOICES);
    this.referenceCandidates = candidates.slice(0, MAX_REFERENCE_CHOICES);
    this.referenceCandidates.forEach((candidate, position) => { candidate.label = featureLabel(candidate.fields, position); });
    titleLookAlikes(this.referenceCandidates, candidate => candidate.layer.id);
    for (const candidate of this.referenceCandidates) {
      candidate.meta = [candidate.layer.name, distanceText(candidate.distance * metresPerPixel)].filter(Boolean).join(' · ');
    }
    this.selectedReferenceCandidate = this.referenceCandidates.length === 1 ? this.referenceCandidates[0] : null;
    this.referenceFeatureView = this.selectedReferenceCandidate ? 'details' : 'list';
    this.highlightReferenceCandidate(this.selectedReferenceCandidate);
    void this.loadCapturedHere(this.selectedReferenceCandidate);
  }

  selectReferenceCandidate(candidate: MapFeatureCandidate): void {
    if (!this.inspectionCurrent?.() || !this.referenceCandidates.includes(candidate)) { this.closeReferenceInspection(); return; }
    this.selectedReferenceCandidate = candidate;
    this.referenceFeatureView = 'details';
    this.highlightReferenceCandidate(candidate);
    void this.loadCapturedHere(candidate);
  }

  backToReferenceCandidates(): void {
    this.resetCapturedHere();
    this.selectedReferenceCandidate = null;
    this.referenceFeatureView = 'list';
    this.highlightReferenceCandidate(null);
  }

  hoverReferenceCandidate(candidate: MapFeatureCandidate | null): void {
    this.highlightReferenceCandidate(candidate || this.selectedReferenceCandidate);
  }

  /** Outlines the hovered or open feature from the geometry the lookup returned (the layer itself is tiles). */
  private highlightReferenceCandidate(candidate: MapFeatureCandidate | null): void {
    this.referenceHighlightLayer?.removeAll();
    const view = this.inspectionView;
    const geometry = candidate && geoJsonToArcGis(candidate.geometry);
    if (!geometry || !view || view.destroyed) return;
    if (!this.referenceHighlightLayer) {
      this.referenceHighlightLayer = new GraphicsLayer({ listMode: 'hide' });
      view.map.add(this.referenceHighlightLayer);
    }
    this.referenceHighlightLayer.add(new Graphic({ geometry: geometry as unknown as __esri.GeometryUnion,
      symbol: featureHighlightSymbol(geometry.type) as unknown as __esri.SymbolUnion }));
  }

  get canCreateReferenceIssue(): boolean {
    return !!this.inspectionQuery && !!this.selectedReferenceCandidate && !!this.inspectionCurrent?.()
      && this.ability.can('create', subject('Issue', { project_id: this.inspectionQuery.projectId }));
  }

  createIssueAtReference(): void {
    if (!this.canCreateReferenceIssue) return;
    const project = this.projectData?.id === this.inspectionQuery.projectId ? this.projectData : null;
    const data = {
      ...featureIssueData(this.inspectionQuery, this.selectedReferenceCandidate),
      issuesScopeOptions: [
        { value: this.inspectionQuery.projectId, label: project?.name ? `Project ${project.name}` : 'Current project', type: 'project' },
        ...(project?.sites || []).map(site => ({ value: site.id, label: `Site ${site.name}`, type: 'site' })),
      ],
    };
    this.closeReferenceInspection();
    this.projectsService.openRightPanel.next({ component: IssueDetailsWrapperComponent, data });
  }

  ngOnInit() {
    // Issues tab: the no-location badge follows the map's for-map result (null off the tab).
    this.store.select(issueTypesFilter).pipe(takeUntil(this.onDestroy)).subscribe(types => {
      this.issueCategoryLabels = Object.fromEntries(((types || []) as { type?: string; label?: string }[])
        .filter(t => t?.type)
        .map(t => [t.type, t.label || t.type]));
    });
    this.projectsService.issuesMapResult.pipe(takeUntil(this.onDestroy)).subscribe(result => {
      this.issuesMapResult = result;
      this.issuesNoLocationCount = result?.meta?.no_location_count || 0;
      this.issuesNoLocationLabel = issueNoLocationLabel(this.issuesNoLocationCount);
      // An open list of no-location issues belongs to the previous filters.
      if (this.showItemPopup && this.itemPopupContent.some(p => p?.data?.markerData?._kind === 'issue')) {
        this.showItemPopup = false;
      }
    });
    this.mapReferenceLayerService.changed.pipe(takeUntil(this.onDestroy))
      .subscribe(() => { this.closeReferenceInspection(); this.updateOverlayRows(); });
    // Restore advanced-widgets toolbar state from URL query params synchronously.
    // Reading snapshot (rather than subscribing to route.queryParams) avoids a
    // race where drawUnifiedMarkers / onViewLoaded could fire before the async
    // emission lands, which would drop the pending state on the floor.
    const params = this.route.snapshot.queryParams;
    const rm = params['renderMode'] as RenderMode | undefined;
    if (rm === 'clustered' || rm === 'heatmap') {
      this.toolbarState = { ...this.toolbarState, renderMode: rm };
      this.pendingRenderMode = rm;
    }
    const sw = params['swipe'] as SwipePresetId | undefined;
    if (sw === 'cip-vs-existing-streets' || sw === 'design-vs-bike-trails') {
      this.toolbarState = { ...this.toolbarState, swipePreset: sw };
      this.pendingSwipePreset = sw;
    }

    // Rebuild overlay layers whenever the project's reference-layer list
    // changes — fires from Settings → Overlays add/delete as well as the
    // map toolbar's own `+` button, keeping both entry points in sync.
    //
    // debounceTime(300) coalesces bursty emissions (e.g. delete-then-add) so
    // we don't kick off two overlapping activateForProject() calls — that
    // would corrupt the service's `activeLayers` map and leak blob URLs
    // because the second `deactivate()` runs while the first activation's
    // awaits are still in flight.
    this.refLayerService.listChanged
      .pipe(debounceTime(300), takeUntil(this.onDestroy))
      .subscribe(() => this.refreshOverlays());

    // "Show on map" from a placed drawing. A layer published moments ago may not be in the loaded catalog yet.
    this.projectsService.showMapLayer
      .pipe(takeUntil(this.onDestroy))
      .subscribe(layerId => { void this.showDesignLayer(layerId); });

    // Forward Sketch-polygon spatial selections into the shared bid-item
    // multi-select stream. The side nav and map-sidenav both already
    // subscribe to `mapService.selectedBidItemIds`, so pushing there is all
    // we need to light up the existing highlight / filter UI.
    this.mapSpatialSelectService.selectionChange
      .pipe(takeUntil(this.onDestroy))
      .subscribe(bidItemIds => {
        this.mapService.selectedBidItemIds.next(bidItemIds);
      });

    // When the drawn polygon changes (first complete or vertex edit), fetch
    // the spatial summary. `null` means the tool was cancelled/deactivated.
    this.mapSpatialSelectService.polygonChange
      .pipe(takeUntil(this.onDestroy))
      .subscribe(polygon => {
        if (!polygon) {
          // Tool cancelled/deactivated — keep the active flag as-is; the toggle
          // handler manages the `active` transition.
          return;
        }
        this.fetchSpatialSummary(polygon);
      });

    // The sidenav's "Download PDF" button asks for an export; we own the
    // MapView, so capture the screenshot here and hand it to the service.
    this.projectSpatialSummaryService.exportRequest$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.exportSpatialSummaryPdf());

    // Subscribe to slider dragging state
    this.mapService.isSliderDragging
      .pipe(takeUntil(this.onDestroy))
      .subscribe(dragging => {
        this.isSliderDragging = dragging;
      });

    // Subscribe to play animation state
    this.mapService.isPlaying
      .pipe(takeUntil(this.onDestroy))
      .subscribe(playing => {
        const wasPlaying = this.isPlaying;
        this.isPlaying = playing;

        // When play stops, refresh markers to remove any stale markers for sites with geometries
        // Skip if drilldown exit is in progress
        if (wasPlaying && !playing && this.mapComponentInstance && !this.drilldownModeService.isExitInProgress) {
          this.initializeMap();
        }
      });

    // Subscribe to zoom in trigger
    this.mapService.triggerZoomIn
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.performZoomIn();
      });

    // Subscribe to zoom out trigger
    this.mapService.triggerZoomOut
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.performZoomOut();
      });

    // NOTE: Fade effect is NOT used when entering detail mode
    // It's only used for hover interaction on line items

    // Subscribe to selected bid item marker ID
    this.mapService.selectedBidItemMarkerId
      .pipe(takeUntil(this.onDestroy))
      .subscribe(markerId => {
        this.selectedBidItemMarkerId = markerId;
      });

    // Subscribe to selected item data (bid items, activities, deliveries, etc.)
    this.mapService.selectedItemData
      .pipe(takeUntil(this.onDestroy))
      .subscribe(itemData => {
        this.selectedItemData = itemData;
        if (itemData) {
          if (itemData._lightweight) {
            // Lightweight mode: fade non-matching geometries
            this.applyLightweightFade(itemData);
          } else {
            // Entering drilldown mode - store current state and apply filters
            this.enterDrilldownMode(itemData);
          }
        } else {
          // Exiting any mode - restore previous state
          this.resetLightweightFade();
          this.exitDrilldownMode();
        }
      });

    // Subscribe to hovered line item for highlight effect
    this.mapService.hoveredLineItemId
      .pipe(takeUntil(this.onDestroy))
      .subscribe(lineItemId => {
        // Skip line-item-level hover when GPS feature-level hover is active
        const gpsFeatureIdx = this.mapService.hoveredGpsFeatureIndex.getValue();
        if (gpsFeatureIdx !== null && gpsFeatureIdx !== undefined) return;
        this.applyLineItemHoverEffect(lineItemId);
      });

    // Open line accordion in the bid item panel: fade the bid item's other lines
    this.mapService.openLineItemId
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const gpsFeatureIdx = this.mapService.hoveredGpsFeatureIndex.getValue();
        if (gpsFeatureIdx !== null && gpsFeatureIdx !== undefined) return;
        this.applyLineItemHoverEffect(this.mapService.hoveredLineItemId.getValue());
      });

    // Subscribe to GPS feature index for per-feature highlight on main map
    this.mapService.hoveredGpsFeatureIndex
      .pipe(takeUntil(this.onDestroy))
      .subscribe(featureIndex => {
        const lineItemId = this.mapService.hoveredLineItemId.getValue();
        this.applyGpsFeatureHoverEffect(lineItemId, featureIndex);
      });

    // Subscribe to persistent GPS feature selection (click-to-select in detail view)
    this.mapService.selectedGpsFeatureIndex
      .pipe(takeUntil(this.onDestroy))
      .subscribe(featureIndex => {
        const lineItemId = this.mapService.selectedGpsLineItemId.getValue();
        this.applySelectedGpsFeatureHighlight(lineItemId, featureIndex);
      });

    // Subscribe to bid item filter changes
    this.mapService.selectedBidItemIds
      .pipe(takeUntil(this.onDestroy))
      .subscribe(selectedBidItemIds => {
        this.applyBidItemFilter(selectedBidItemIds);
      });

    this.mapService.initiateSiteLocationsMap
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        let update = false;

        if (JSON.stringify(this.filteredSites) !== JSON.stringify(d)) {
          update = true;
        }

        if (d.length) {
          this.filteredSites = d;
        } else {
          this.filteredSites = this.sites;
        }
        this.mapStore.setFilteredSites(this.filteredSites);

        if (update) {
          // Don't zoom during play mode - let user control zoom
          const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;

          if (!this.isPlaying && !this.isSliderDragging && !this.drilldownModeService.isExitInProgress) {
            this.initializeMap();
          }
          // NOTE: During play/drag, we do NOT call drawReportGeometries here.
          // The siteReportGeometries subscription handles geometry updates during drag.
          // Calling it here caused site markers to flash because sitesWithGeometries
          // hadn't been updated yet from the geometry fetch.
        }
      });

    // Subscribe to report geometries
    this.mapService.siteReportGeometries
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const geoCount = data?.geometries?.length || 0;
        const isExitInProgress = this.drilldownModeService.isExitInProgress;

        // During drilldown exit: Skip EMPTY geometry emissions but accept valid data
        // This prevents the "clear then fetch" pattern from wiping restored layers
        if (isExitInProgress && geoCount === 0) {
          return;
        }

        // Mark that we've received geometry data (even if empty)
        this.geometryDataReceived = true;

        // Always update data model - this ensures drawReportGeometries() has correct data
        let newLayerSettings;
        if (data && data.geometries) {
          this.reportGeometries = data.geometries;
          this.sitesWithGeometries = data.sitesWithGeometries || [];
          this.siteColorMap = data.siteColorMap || {};
          // Merge incoming settings with CURRENT settings to preserve state from other subscriptions
          // This prevents race conditions when layerSettings is updated from multiple sources
          // (siteReportGeometries for Layers tab, layerSettingsUpdated for Types tab)
          newLayerSettings = { ...this.layerSettings, ...(data.layerSettings || {}) };
        } else {
          // Handle legacy format - but skip if exit in progress to preserve data
          if (!isExitInProgress) {
            this.reportGeometries = data || [];
            this.sitesWithGeometries = [];
            this.siteColorMap = {};
          }
          newLayerSettings = this.layerSettings;
        }

        // Refresh heatmap contributions layer when report geometries change.
        // update() is a no-op when heatmap mode is inactive, so this is safe
        // to call unconditionally here.
        const mvForHeatmap = this.mapComponentInstance?.mapViewInstance;
        if (mvForHeatmap) {
          this.mapHeatmapContributionsService.update(mvForHeatmap, this.reportGeometries || []);
          // update() rebuilds the layer as visible by default; re-apply the
          // gating so a subscription refresh doesn't un-hide a toggled-off layer.
          this.mapHeatmapContributionsService.setVisible(this.layerSettings.asBuilt);
          // The heatmap layer is torn down and rebuilt inside update(), so if
          // the Swipe widget was referencing the old instance, it's now stale.
          this.refreshSwipeIfActive(mvForHeatmap);
        }

        // Check if layer settings changed
        const layerSettingsChanged = JSON.stringify(this.previousLayerSettings) !== JSON.stringify(newLayerSettings);

        // Update layer settings
        this.layerSettings = newLayerSettings;
        this.mapStore.updateLayerSettings(newLayerSettings);

        // Skip rendering during drilldown exit - layers already restored by drilldown service
        if (isExitInProgress) {
          return;
        }

        // Check if the set of sites with geometries has actually changed
        const sitesChanged = JSON.stringify(this.previousSitesWithGeometries.sort()) !==
          JSON.stringify([...this.sitesWithGeometries].sort());

        if (this.mapComponentInstance) {
          const hasAnyGeometries = this.sitesWithGeometries.length > 0 || this.reportGeometries.length > 0;
          if (hasAnyGeometries) {
            const geometrySignature = this.getReportGeometrySignature();
            const geometriesChanged = geometrySignature !== this.previousReportGeometrySignature;

            // Only redraw everything (including zoom) if sites changed AND not dragging AND not playing
            // Note: isExitInProgress check already done above with early return
            if (sitesChanged && !this.isSliderDragging && !this.isPlaying) {
              this.previousSitesWithGeometries = [...this.sitesWithGeometries];
              this.previousLayerSettings = { ...this.layerSettings };
              this.previousReportGeometrySignature = geometrySignature;
              this.initializeMap();
            } else if (this.isSliderDragging || this.isPlaying) {
              // During play/drag: ONLY update as-built geometries (date-dependent)
              // Site geometries are permanent and should NOT be redrawn to avoid marker flash
              const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;

              // Update previous state
              this.previousSitesWithGeometries = [...this.sitesWithGeometries];
              this.previousLayerSettings = { ...this.layerSettings };
              this.previousReportGeometrySignature = geometrySignature;

              if (!isRecreatingUnifiedLayer) {
                // Always skip site geometries during drag - they're already drawn and permanent
                this.drawReportGeometries(true); // skipSiteGeometries = true
              }
            } else if (geometriesChanged || !this.siteGeometriesDrawn) {
              // Not playing/dragging, sites haven't changed, but geometries changed or were never drawn
              const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;

              this.previousLayerSettings = { ...this.layerSettings };
              this.previousReportGeometrySignature = geometrySignature;

              if (!isRecreatingUnifiedLayer) {
                this.drawReportGeometries(false);
              } else {
              }
            } else if (layerSettingsChanged) {
              // Layer settings changed but sites didn't - just update visibility
              // Picture toggle: Update definitionExpression instead of recreating layer
              if (this.layerSettings.picture !== this.previousLayerSettings.picture) {

                // CRITICAL: If pictures are being ENABLED and layer exists without CIM symbols,
                // set the flag NOW before drawReportGeometries can be called
                if (this.layerSettings.picture && !this.previousLayerSettings.picture) {
                  const currentlyHasCIM = this.previousUnifiedSymbolsHadCIM || false;
                  const hasPictureData = this.pictureMarkers && this.pictureMarkers.length > 0;


                  if (this.unifiedMarkersLayer && !currentlyHasCIM && hasPictureData) {
                    (this as any).isRecreatingLayer = true;
                  } else {
                  }
                }

                // Check if unified layer is being recreated (skip geometry redraw, it handles its own zoom)
                const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;

                if (this.unifiedMarkersLayer) {
                  this.applyUnifiedDefinitionExpression();
                  this.previousLayerSettings = { ...this.layerSettings };
                } else if (!isRecreatingUnifiedLayer) {
                  // No layer yet AND not recreating, create it
                  this.previousLayerSettings = { ...this.layerSettings };
                  this.drawReportGeometries(false);
                } else {
                  // Layer is being recreated, just update settings
                  this.previousLayerSettings = { ...this.layerSettings };
                }
              } else {
                // Other layer settings changed
                // Check if unified layer is being recreated (skip geometry redraw, it handles its own zoom)
                const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;

                this.previousLayerSettings = { ...this.layerSettings };

                // Re-apply marker-type filter expression so Types tab toggles
                // (bidItem / activity / delivery) affect the heatmap renderer too.
                this.applyUnifiedDefinitionExpression();

                if (!isRecreatingUnifiedLayer) {
                  this.drawReportGeometries(false);
                } else {
                }
              }
            }
          } else {
            // No geometries
            this.previousSitesWithGeometries = [];
            this.previousReportGeometrySignature = '';
            // Only reset flag if NOT playing/dragging (during play, geometries may temporarily be empty between frames)
            // Also skip if drilldown exit is in progress - layers were already restored by drilldown service
            if (!this.isSliderDragging && !this.isPlaying && !this.drilldownModeService.isExitInProgress) {
              this.siteGeometriesDrawn = false; // Reset flag when no geometries
              this.previousLayerSettings = { ...this.layerSettings };
              this.initializeMap(); // Creates markers for sites without geometry
            }
            // During play/drag or drilldown exit: preserve the flag and don't redraw
          }
        }
      });

    // Subscribe to layer settings updates (without geometry changes)
    this.mapService.layerSettingsUpdated
      .pipe(takeUntil(this.onDestroy))
      .subscribe(newLayerSettings => {
        if (newLayerSettings) {
          // Merge with defaults to ensure all properties exist
          const defaultLayerSettings = getLayerDefaults('nonFullscreen');
          this.layerSettings = { ...defaultLayerSettings, ...newLayerSettings };
          this.mapStore.updateLayerSettings(this.layerSettings);
          this.syncLabelSources();
        }
      });

    // Subscribe to project details to get all sites when not on sites tab
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data).length !== 0) {
          this.projectData = data;
          this.syncStationing();
          if (data.sites) {
            this.sites = data.sites.map(site => ({
              ...site,
              site_id: site.id,
              site_name: site.name,
              isFiltered: true // Default to filtered when showing all sites
            })).filter(site => site.longitude && site.latitude);
            this.mapStore.setSites(this.sites);
          }
          // Extract organization basemaps from project data
          if (data.base_maps) {
            this.organizationBasemaps = data.base_maps;
          }
          // A saved organization basemap can only be resolved once the project's base_maps are here.
          this.restoreBasemapPreference();
        }
      });

    // Subscribe to ALL marker types together (pictures, bid items, activities, deliveries)
    // This ensures we only redraw when ALL have emitted, preventing race conditions
    // Use startWith to provide initial empty values so combineLatest fires immediately
    combineLatest([
      this.mapService.dailyReportPictures.pipe(startWith([])),
      this.mapService.dailyReportBidItems.pipe(startWith({ markers: [], noLocationCount: 0, noLocationItems: [] })),
      this.mapService.dailyReportActivities.pipe(startWith({ markers: [], noLocationCount: 0, noLocationItems: [] })),
      this.mapService.dailyReportDeliveries.pipe(startWith({ markers: [], noLocationCount: 0, noLocationItems: [] })),
      this.mapService.dailyReportForms.pipe(startWith({ markers: [], noLocationCount: 0, noLocationItems: [] }))
    ])
      .pipe(
        debounceTime(200), // Increased debounce to wait for all data sources to stabilize
        takeUntil(this.onDestroy)
      )
      .subscribe(([pictures, bidItemData, activityData, deliveryData, formData]) => {
        const timestamp = new Date().toISOString();
        const zoom = this.mapComponentInstance?.mapViewInstance?.zoom || 'N/A';
        const interacting = this.mapComponentInstance?.mapViewInstance?.interacting || false;


        // DEBUG: Log first few bid item marker details to verify site filtering
        if (bidItemData.markers && bidItemData.markers.length > 0) {
        }


        // CRITICAL: Don't redraw during map interactions (zoom/pan) to prevent flickering
        // BUT in single day mode, always proceed since drawUnifiedMarkers waits for animation
        if (interacting && !this.isSingleDayMode) {
          // Store the data for later (sanitize to null out outlier coordinates)
          this.pictureMarkers = this.sanitizePictureCoordinates(pictures || []);
          this.bidItemMarkers = bidItemData.markers || [];
          this.originalBidItemNoLocationItems = bidItemData.noLocationItems || [];
          this.activityMarkers = activityData.markers || [];
          this.originalActivityNoLocationItems = activityData.noLocationItems || [];
          this.deliveryMarkers = deliveryData.markers || [];
          this.originalDeliveryNoLocationItems = deliveryData.noLocationItems || [];
          this.formMarkers = formData.markers || [];
          this.originalFormNoLocationItems = formData.noLocationItems || [];
          // Apply filtering to no-location items
          this.applyNoLocationFilter();
          // Calculate main mode no-location pictures
          this.calculateMainNoLocationPictures();
          // Trigger change detection to ensure no-location badges update
          this.cdr.detectChanges();
          return;
        }

        // Update all marker data (sanitize to null out outlier coordinates)
        this.pictureMarkers = this.sanitizePictureCoordinates(pictures || []);
        this.bidItemMarkers = bidItemData.markers || [];
        this.originalBidItemNoLocationItems = bidItemData.noLocationItems || [];
        this.activityMarkers = activityData.markers || [];
        this.originalActivityNoLocationItems = activityData.noLocationItems || [];
        this.deliveryMarkers = deliveryData.markers || [];
        this.originalDeliveryNoLocationItems = deliveryData.noLocationItems || [];
        this.formMarkers = formData.markers || [];
        this.originalFormNoLocationItems = formData.noLocationItems || [];
        // Apply filtering to no-location items (handles both filtered and unfiltered states)
        this.applyNoLocationFilter();

        // Calculate main mode no-location pictures
        this.calculateMainNoLocationPictures();

        // Trigger change detection to ensure no-location badges update
        this.cdr.detectChanges();

        // Sync marker data to MapStore
        this.mapStore.setPictureMarkers(this.pictureMarkers);
        this.mapStore.setBidItemMarkers(this.bidItemMarkers);
        this.mapStore.setActivityMarkers(this.activityMarkers);
        this.mapStore.setDeliveryMarkers(this.deliveryMarkers);
        this.mapStore.setBidItemNoLocationItems(this.bidItemNoLocationItems);
        this.mapStore.setActivityNoLocationItems(this.activityNoLocationItems);
        this.mapStore.setDeliveryNoLocationItems(this.deliveryNoLocationItems);

        if (this.mapComponentInstance) {
          // Draw unified markers with current filter (if any)
          // In single day mode, also zoom to include all markers after drawing
          this.drawUnifiedMarkers(this.currentBidItemFilter).then(() => {
            // In single day mode, zoom to include all unified markers (activities, bid items, deliveries, pictures)
            // This ensures the map extent covers all markers, not just the site location
            // Only do this once when markers first arrive to avoid re-zooming on every update
            // Embedded viewer fits the geometry extent in drawReportGeometries;
            // skip the marker-inclusive (site-centered) zoom so it doesn't fight.
            if (this.isSingleDayMode && !this.hasZoomedToUnifiedMarkers && !this.showEmbeddedControls) {
              const hasUnifiedMarkers = this.activityMarkers.length > 0 ||
                                         this.bidItemMarkers.length > 0 ||
                                         this.deliveryMarkers.length > 0 ||
                                         this.pictureMarkers.length > 0;
              if (hasUnifiedMarkers) {
                this.hasZoomedToUnifiedMarkers = true;
                this.zoomToIncludeUnifiedMarkers();
              }
            }
          });
        }
      });

    // Subscribe to warranty claim markers
    this.mapService.warrantyClaimMarkers$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(payload => {
        const markers = payload?.markers || [];
        this.warrantyClaimMarkers = markers;
        this.drawWarrantyClaimMarkers(markers);
        const combined = [...markers, ...(this.warrantyIssueMarkers || [])];
        if (combined.length > 0 && this.mapComponentInstance?.mapViewInstance) {
          this.mapService.setExtent(this.mapComponentInstance, combined, 1.4);
        } else if (this.isRecordSitesFallback(payload?.fit)) {
          this.zoomToSitesOrProject();
        }
      });

    // Subscribe to warranty issue markers
    this.mapService.warrantyIssueMarkers$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(payload => {
        const markers = payload?.markers || [];
        this.warrantyIssueMarkers = markers;
        this.drawWarrantyIssueMarkers(markers);
        const combined = [...(this.warrantyClaimMarkers || []), ...markers];
        // `fit: false`: a refetch for new filters (Issues tab) keeps the view.
        if (payload?.fit === 'sites' && combined.length === 0 && this.mapComponentInstance?.mapViewInstance) {
          // Issues tab with nothing to pin (every matching issue has no location):
          // show the project's sites, else the project location, not an empty view.
          this.zoomToSitesOrProject();
        } else if (payload?.fit !== false && payload?.fit !== 'sites' && combined.length > 0
          && this.mapComponentInstance?.mapViewInstance) {
          this.mapService.setExtent(this.mapComponentInstance, combined, 1.4);
        } else if (combined.length === 0 && this.isRecordSitesFallback(payload?.fit)) {
          this.zoomToSitesOrProject();
        }
      });

    // Initialize MapStore with component's initial state
    this.mapStore.updateLayerSettings(this.layerSettings);
    this.mapStore.setFullScreen(this.isFullScreen);

    // Subscribe to label config changes
    this.mapStore.labelConfig$.pipe(
      distinctUntilChanged((a, b) => a.enabled === b.enabled && a.labelBy === b.labelBy),
      takeUntil(this.onDestroy)
    ).subscribe(labelConfig => {
      this._currentLabelConfig = labelConfig;
      this.syncLabelSources();
    });

  }

  initializeMap() {
    // In fullscreen mode, wait until the geometry response has arrived before
    // drawing anything — this prevents the flash where site markers appear and
    // are then replaced by boundary polygons. Once the response is in (even
    // if it's empty), proceed: projects with zero site boundaries should still
    // get site markers drawn as a fallback so the map can zoom to them.
    if (this.isFullScreen && !this.geometryDataReceived && !this.siteGeometriesDrawn) {
      return;
    }

    // Header map: sites drawn as boundaries don't also get a marker
    const headerGeometrySiteIds = new Set(this.buildHeaderSiteGeometries().map(geometry => geometry.siteId));

    // while creating marker, add level = 2
    // this is needed for the esri map component to show tooltip
    this.markersList = this.filteredSites?.map(site => {
      const siteId = site.site_id || site.id;

      // Logic for marker creation based on Sites layer setting
      // Markers are a fallback for sites without geometry, so they follow the same layer toggle
      if (!this.layerSettings.siteBoundary) {
        // Sites layer is OFF: Don't show any markers (same as hiding geometries)
        return null;
      }

      // Sites layer is ON: Skip marker only if site HAS geometry
      if (this.sitesWithGeometries.includes(siteId) || headerGeometrySiteIds.has(siteId)) {
        return null; // Geometry will be shown instead of marker
      }
      // If site has NO geometry, continue to create marker as fallback below

      const location = {
        longitude: site.longitude,
        latitude: site.latitude,
      };

      const data = {
        ...site,
        level: 2,
      };

      // Use the isFiltered flag to determine marker style
      const isFiltered = site.isFiltered !== undefined ? site.isFiltered : true;

      // Get site color from color map (only in fullscreen mode)
      const siteColor = this.isFullScreen ? (this.siteColorMap[siteId] || null) : null;

      return this.mapService.createMarker(location, data, 'site', isFiltered, siteColor);
    }).filter(marker => marker !== null)
      .filter(marker => {
        // Hide grey markers (unfiltered sites) - only show blue markers (filtered sites)
        const isFiltered = marker.data?.isFiltered !== undefined ? marker.data.isFiltered : true;
        return isFiltered === true;
      });

    this.drawPointsOnMap();
  }

  onMarkerClick($event: any) {

  }

  onShowTooltip($event: any) {

  }

  onMapLoaded($event: any) {

  }

  onMapComponentLoaded($event: any) {
    this.mapComponentInstance = $event;
    this.drawPointsOnMap();
    this.drawReportGeometries();
    this.setupGeometryInteractionHandlers();
    if (this.warrantyClaimMarkers?.length) {
      this.drawWarrantyClaimMarkers(this.warrantyClaimMarkers);
    }
    if (this.warrantyIssueMarkers?.length) {
      this.drawWarrantyIssueMarkers(this.warrantyIssueMarkers);
    }
    const combined = [...(this.warrantyClaimMarkers || []), ...(this.warrantyIssueMarkers || [])];
    if (combined.length > 0) {
      this.mapService.setExtent(this.mapComponentInstance, combined, 1.4);
    }
  }

  onViewLoaded($event: any) {
    // $event is the mapView instance directly from the map component
    const mapView = $event;
    if (mapView) this.setupReferenceInspection(mapView);

    // Expose for e2e video recording when test mode is enabled.
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('__test_mode__') === 'true') {
        (window as any).__mapView = mapView;
      }
    } catch { /* ignore */ }

    // Set up early hover handler and context IMMEDIATELY when map view is ready
    // This allows hover to work as soon as the layer is created, not after all image processing
    if (mapView) {
      this.unifiedMarkerService.setupEarlyHoverHandler(mapView);
      const hoverContext: MarkerHoverContext = {
        onPictureHover: (data) => this.onPictureHover(data)
      };
      this.unifiedMarkerService.setHoverContext(hoverContext);

      // Create the dedicated station-range layer once the map view is ready.
      // Always visible; positioned just below the unified markers at draw time.
      this.ensureStationRangeLayer(mapView);
      // Hardening: an opening fit made before the view settled on its size is redone once
      // The camera is the user's after any pan or zoom of theirs (the marker code's interacting watch is not always set)
      this.openingFitWatches.forEach(handle => handle.remove());
      const userMoved = () => { this.userMovedMapSinceOpen = true; this.openingFitLanded = true; };
      this.openingFitLanded = false;
      // A map with nothing to fit to (no sites) gets its wheel back, and every map does after 15 s
      const wheelFallbacks = [
        setTimeout(() => { if (!this.openingFit) this.openingFitLanded = true; }, 6000),
        setTimeout(() => { this.openingFitLanded = true; }, 15000),
      ];
      this.openingFitWatches = [
        { remove: () => wheelFallbacks.forEach(timer => clearTimeout(timer)) },
        reactiveUtils.watch(() => [mapView.width, mapView.height],
          ([width, height]) => this.redoOpeningFit(mapView, { kind: 'resize', width, height })),
        reactiveUtils.when(() => mapView.interacting, userMoved),
        // Reproduced: scrolling the page with the pointer over the header map while it loads zooms out from world
        // zoom (no visible change), interrupts the opening fit and marks the map as the user's, so no retry runs and it
        // stays at world view. Until the opening fit lands the wheel is not a map zoom.
        mapView.on('mouse-wheel', event => {
          if (!this.openingFitLanded) { event.stopPropagation(); return; }
          userMoved();
        }),
        mapView.on(['drag', 'double-click'], userMoved),
        mapView.on('key-down', event => { if (/^(Arrow|\+$|-$|=$)/.test(event.key)) userMoved(); }),
      ];
      // 7.2: Stationing along the project's alignments, under everything else
      this.stationing?.destroy();
      this.stationing = new StationingMapLayer(mapView);
      this.stationing.setLabelsShown(this.isFullScreen);
      // A new layer starts visible: syncStationing subscribes again so it gets the user's Stationing toggle and lock
      this.stationingProjectId = null;
      this.stationing.holdsLine = () => (this.showItemPopup && !!this.itemPopupStationLine) || (this.referenceInspectionOpen && !!this.referenceStationLine);
      this.stationing.onActiveChange = (name, locked) => {
        this.stationingAlignmentName = name;
        this.stationingAlignmentLocked = locked;
        this.stationingAlignmentNames = this.stationing?.alignmentNames ?? [];
        // An open popup's or feature panel's station line is read again on the new active alignment
        if (this.showItemPopup && this.itemPopupMapPoint) this.itemPopupStationLine = this.stationing?.lineAt(this.itemPopupMapPoint) ?? null;
        if (this.referenceInspectionOpen && this.referenceMapPoint) this.referenceStationLine = this.stationing?.lineAt(this.referenceMapPoint) ?? null;
        this.cdr.detectChanges();
      };
      if (this.stationingChipHost) mapView.ui.add(this.stationingChipHost.nativeElement, 'bottom-left');
      this.syncStationing();

      // Apply any swipe preset captured from URL query params now that the map
      // view is ready. Swipe only needs a live MapView (no markers required).
      if (this.pendingSwipePreset && this.pendingSwipePreset !== 'off') {
        this.mapSwipeService.activate(mapView, this.pendingSwipePreset);
        this.pendingSwipePreset = null;
      }

      // Apply heatmap contributions layer from URL param NOW — drawUnifiedMarkers
      // early-returns when there are no point markers (common for projects that
      // are all as-builts), so we can't rely on it to fire the activation.
      // The contributions service's wantsActive flag will re-build the layer
      // when siteReportGeometries fires later via update().
      if (this.pendingRenderMode === 'heatmap') {
        this.mapHeatmapContributionsService.activate(mapView, this.reportGeometries || []);
        // Apply initial visibility based on layer settings — activate() builds
        // the layer as visible by default, which would ignore the As-builts /
        // bidItem toggles on first load.
        this.mapHeatmapContributionsService.setVisible(this.layerSettings.asBuilt);
        // Don't clear pendingRenderMode here — drawUnifiedMarkers still needs
        // it to call setRenderMode('heatmap') on the point layer if markers arrive.
      }

      // Prefer the route param — on direct-URL fullscreen loads, projectData
      // can still be null when onViewLoaded fires, which would silently skip
      // reference-layer activation and leave the Overlays popover empty.
      const refProjectId =
        this.route.snapshot.params['projectId']
        || this.route.snapshot.parent?.params['projectId']
        || this.projectData?.id;
      if (refProjectId) void this.refreshOverlays(mapView);
      this.restoreBasemapPreference();
    }

    // Track zoom changes to detect if zoom triggers redraws
    if (this.mapComponentInstance?.mapViewInstance) {
      // Clean up existing watcher before creating new one (prevents memory leaks)
      if (this.zoomWatchHandle) {
        this.zoomWatchHandle.remove();
        this.zoomWatchHandle = null;
      }
      this.zoomWatchHandle = this.mapComponentInstance.mapViewInstance.watch('zoom', (newZoom: number) => {

        // Skip clustering changes during drilldown exit (service handles its own restoration)
        if (this.drilldownModeService.isExitInProgress) return;

        // Disable/enable clustering on unified markers layer based on zoom level
        if (this.unifiedMarkersLayer) {
          if (newZoom >= 19) {
            // Zoom level 19+: Disable clustering to show individual markers
            this.unifiedMarkersLayer.featureReduction = null;
          } else if (!this.unifiedMarkersLayer.featureReduction) {
            // Re-enable clustering when zooming back out
            this.unifiedMarkersLayer.featureReduction = {
              type: 'cluster',
              clusterRadius: '120px',
              clusterMinSize: '24px',
              clusterMaxSize: '60px',
              clusterTransitionDuration: 200,
              clusterTransitionDistance: 10,
              filter: {
                where: "markerType <> 'multi_item_location'"
              } as any,
              symbol: {
                type: 'simple-marker',
                style: 'circle',
                size: '32px',
                color: '#1036D6',
                outline: {
                  color: 'transparent',
                  width: 0
                }
              } as any,
              clusterProperties: {
                total_count: {
                  onStatisticField: 'count',
                  statisticType: 'sum'
                },
                picture_count: {
                  onStatisticField: 'count',
                  onStatisticExpression: "IIF($feature.markerType == 'picture', $feature.count, 0)",
                  statisticType: 'sum'
                },
                bid_item_count: {
                  onStatisticField: 'count',
                  onStatisticExpression: "IIF($feature.markerType == 'bid_item', $feature.count, 0)",
                  statisticType: 'sum'
                }
              } as any,
              labelingInfo: [{
                deconflictionStrategy: 'none',
                labelExpressionInfo: {
                  expression: 'Text($feature.cluster_count, "#,###")'
                },
                symbol: {
                  type: 'text',
                  color: [255, 255, 255, 255],
                  haloColor: [0, 0, 0, 0],
                  haloSize: 0,
                  font: {
                    size: 14,
                    family: 'Montserrat',
                    weight: 'bold'
                  },
                  xoffset: 0,
                  yoffset: 0
                },
                labelPlacement: 'center-center',
                minScale: 0,
                maxScale: 0
              }],
              popupTemplate: {
                title: 'Cluster Details',
                content: [
                  {
                    type: 'text',
                    text: '<b>{total_count}</b> total items<br>' +
                      '{picture_count} pictures<br>' +
                      '{bid_item_count} bid items'
                  }
                ]
              } as any
            } as any;
          }
        }
      });

      // Clean up existing watcher before creating new one (prevents memory leaks)
      if (this.interactingWatchHandle) {
        this.interactingWatchHandle.remove();
        this.interactingWatchHandle = null;
      }
      this.interactingWatchHandle = this.mapComponentInstance.mapViewInstance.watch('interacting', (isInteracting: boolean) => {
        if (isInteracting) this.userMovedMapSinceOpen = true;
        // Skip during drilldown exit (service handles layer restoration)
        if (this.drilldownModeService.isExitInProgress) return;

        // When interaction ends, draw any markers that were stored during interaction
        if (!isInteracting && this.isSingleDayMode) {
          const hasMarkers = this.activityMarkers?.length > 0 ||
                             this.bidItemMarkers?.length > 0 ||
                             this.deliveryMarkers?.length > 0 ||
                             this.pictureMarkers?.length > 0;
          if (hasMarkers) {
            this.drawUnifiedMarkers(this.currentBidItemFilter);
          }
        }
      });
    }
  }

  onFullscreenChange(isFullscreen: boolean): void {
    this.isFullScreen = isFullscreen;
    this.stationing?.setLabelsShown(isFullscreen);

    // Clear geometries and hide bid items when exiting fullscreen mode
    // BUT in single day mode (daily report), keep layers visible
    if (!isFullscreen && this.mapComponentInstance && !this.isSingleDayMode) {
      if (this.mapComponentInstance.mapProjectGraphicsLayer) {
        this.mapComponentInstance.mapProjectGraphicsLayer.removeAll();
      }
      if (this.mapComponentInstance.mapMultiGeoGraphicsLayer) {
        this.mapComponentInstance.mapMultiGeoGraphicsLayer.removeAll();
      }
      // Hide unified markers layer (bid items) in non-fullscreen mode (except daily report)
      if (this.unifiedMarkersLayer) {
        this.unifiedMarkersLayer.visible = false;
      }
    }
    // Show unified markers layer when entering fullscreen mode OR in single day mode
    else if ((isFullscreen || this.isSingleDayMode) && this.unifiedMarkersLayer) {
      this.unifiedMarkersLayer.visible = true;
    }

    this.fullscreenChange.emit(isFullscreen);
  }

  drawPointsOnMap() {
    if (this.mapComponentInstance) {
      this.mapService.removeAllMarkers(this.mapComponentInstance);
      // Don't clear geometries here - they are managed separately in drawReportGeometries()
    }

    if (this.mapComponentInstance && this.markersList.length) {
      // Clear all markers previously drawn on the map
      this.mapService.removeAllMarkers(this.mapComponentInstance);

      // Draw markers on the map
      this.mapComponentInstance?.drawProjectSiteMarkers(this.markersList);
    }

    // Handle zoom extent calculation - works with markers OR geometries
    if (this.mapComponentInstance) {
      // Determine which markers to use for zoom extent
      // Priority: filtered sites (blue markers) > all sites
      const filteredMarkers = this.markersList?.filter(marker =>
        marker.data?.isFiltered === true
      ) || [];

      const markersForZoom = filteredMarkers.length > 0 ? filteredMarkers : (this.markersList || []);

      // Animate to the appropriate extent with smooth transition
      // Include geometries in fullscreen mode OR when we have site geometries with no markers
      // Skip zoom during play or drag mode (let user control zoom)
      // CRITICAL: Skip zoom in fullscreen if unified layer has content (pictures/activities/bid items)
      // to prevent zooming away from unified markers to site marker
      const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;
      // In single-day mode, skip zoom here — zoomToIncludeUnifiedMarkers handles it
      // to avoid double-zoom (first to site geometry, then to include all markers)
      // On direct-URL loads, fullscreen is already true before the first site
      // data arrives, so the fullscreen skip would suppress the very first zoom
      // and leave the map at world view. Bypass the skip until we've zoomed
      // once; from then on the guards behave normally.
      const shouldSkipZoomForUnifiedContent = this.hasPerformedInitialZoom && (
        this.isSingleDayMode
        || (this.isFullScreen && this.unifiedMarkersLayer && !isRecreatingUnifiedLayer)
      );

      // Include geometries in extent calculation if:
      // 1. We're in fullscreen mode (original behavior), OR
      // 2. We have site geometries and site boundary layer is ON (for non-fullscreen with geometries)
      const hasSiteGeometries = this.reportGeometries?.some(g => g.isSiteGeometry);
      const shouldIncludeGeometries = this.isFullScreen || (hasSiteGeometries && this.layerSettings?.siteBoundary);

      // Only zoom if we have markers OR site geometries to zoom to
      const hasContentToZoomTo = markersForZoom.length > 0 || (hasSiteGeometries && this.layerSettings?.siteBoundary);

      // Record modes (layerSettings.warrantyClaim) leave the zoom to their pins,
      // or to drawReportGeometries' sites fallback when there are none.
      if (!this.layerSettings.warrantyClaim
        && !this.isSliderDragging && !this.isPlaying && !shouldSkipZoomForUnifiedContent && hasContentToZoomTo) {
        // Store the animation promise so drawUnifiedMarkers can wait for it
        // This prevents marker layer creation during zoom animations (race condition fix)
        this.initialAnimationPromise = this.animateToExtent(markersForZoom, shouldIncludeGeometries)
          .finally(() => {
            // Clear the promise once animation completes (success or failure)
            this.initialAnimationPromise = null;
            this.hasPerformedInitialZoom = true;
          });
      }
    }

    // Draw report geometries after drawing markers
    this.drawReportGeometries();

    // NOTE: Unified markers are now drawn automatically via combineLatest subscription
    // Don't call drawUnifiedMarkers() here to avoid race conditions
  }

  private animateToExtent(markers: any[], includeGeometries: boolean = false, extraGeometries: HeaderSiteGeometry[] = []): Promise<void> {
    // Skip zoom if we're just re-coloring (view mode change)
    if (this.isRecoloringOnly) {
      return Promise.resolve();
    }
    if (!this.mapComponentInstance?.mapViewInstance) {
      return Promise.resolve();
    }

    const mapView = this.mapComponentInstance.mapViewInstance;

    // Account for left sidenav panel in fullscreen mode
    // Sidenav: 40px (left offset) + 372px (width) + 40px (right gap) = 452px
    const leftPadding = this.isFullScreen ? 452 : 0;

    const goToOptions = {
      duration: 2000,
      easing: 'ease-in-out',
      animate: true,
      padding: { top: 120, left: leftPadding, right: 0, bottom: 0 }
    };

    // Calculate bounding extent from markers
    const coordinates = markers.map(marker => ({
      longitude: marker.geometry.longitude,
      latitude: marker.geometry.latitude,
    }));

    // If including geometries, add geometry extents to the calculation
    if (includeGeometries) {
      // Add extent points from site geometries (stored in reportGeometries)
      if (this.reportGeometries.length > 0) {
        this.reportGeometries.forEach(geometry => {
          if (geometry.isSiteGeometry && geometry.coordinates) {
            // Extract extent from polygon coordinates
            const rings = geometry.type === 'MultiPolygon' ? geometry.coordinates[0] : geometry.coordinates;
            rings.forEach(ring => {
              ring.forEach(coord => {
                coordinates.push({
                  longitude: coord[0],
                  latitude: coord[1]
                });
              });
            });
          }
        });
      }

      // Site boundaries drawn from the sites themselves (header map), every polygon part
      if (extraGeometries.length > 0) {
        const points: { geometry: { longitude: number; latitude: number } }[] = [];
        extraGeometries.forEach(geometry => collectLonLats(geometry.coordinates, points));
        points.forEach(point => coordinates.push(point.geometry));
      }

      // Also include as-builts from mapMultiGeoGraphicsLayer (visible as-built geometries)
      if (this.mapComponentInstance?.mapMultiGeoGraphicsLayer?.visible) {
        const asBuiltGraphics = this.mapComponentInstance.mapMultiGeoGraphicsLayer.graphics?.toArray() || [];
        asBuiltGraphics.forEach((graphic: any) => {
          if (graphic.geometry) {
            // Extract extent based on geometry type
            if (graphic.geometry.type === 'polygon' && graphic.geometry.rings) {
              graphic.geometry.rings.forEach((ring: number[][]) => {
                ring.forEach((coord: number[]) => {
                  coordinates.push({
                    longitude: coord[0],
                    latitude: coord[1]
                  });
                });
              });
            } else if (graphic.geometry.type === 'polyline' && graphic.geometry.paths) {
              graphic.geometry.paths.forEach((path: number[][]) => {
                path.forEach((coord: number[]) => {
                  coordinates.push({
                    longitude: coord[0],
                    latitude: coord[1]
                  });
                });
              });
            } else if (graphic.geometry.type === 'point') {
              coordinates.push({
                longitude: graphic.geometry.longitude || graphic.geometry.x,
                latitude: graphic.geometry.latitude || graphic.geometry.y
              });
            }
          }
        });
      }
    }

    if (coordinates.length === 0) {
      return Promise.resolve();
    }

    let extent = this.geometryService.calculateBoundingExtent(coordinates);

    // If extent is null (happens when only one point), create a minimal extent around that point
    if (!extent && coordinates.length === 1) {
      const { longitude, latitude } = coordinates[0];
      const bufferLon = 0; // keep horizontal tight
      const bufferLat = 0.0005;  // taller vertical buffer to ensure visibility below header
      extent = new Extent({
        xmin: longitude - bufferLon,
        xmax: longitude + bufferLon,
        ymin: latitude - bufferLat,
        ymax: latitude + bufferLat,
      });
    }

    if (extent) {
      extent = this.includeVisibleDesignLayers(extent);
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          try {
            if (mapView) {
              mapView.when(() => {
                let expansionFactor = 1.5;
                if (markers.length === 1 && !includeGeometries) {
                  expansionFactor = 1.8;
                } else if (markers.length <= 3 && !includeGeometries) {
                  expansionFactor = 1.3;
                }
                const targetExtent = extent.expand(expansionFactor);

                // Computed when the view has a size: the shift divides by its width
                const shifted = () => {
                  // Shift extent to center in the visible area (right of side panel)
                  // Side panel is 372px, we need to convert pixels to map units
                  const sidePanelWidth = 400; // 372px + margin
                  const viewWidth = mapView.width;
                  const extentWidth = targetExtent.width;

                  // Calculate how much to shift in map units
                  // The side panel covers (sidePanelWidth / viewWidth) portion of the view
                  // We want to center the extent in the remaining visible area, so shift by half
                  const shiftAmount = (sidePanelWidth / viewWidth) * extentWidth * 0.5;

                  // Create a new extent shifted to the left by half the panel width
                  const shiftedExtent = targetExtent.clone();
                  shiftedExtent.xmin -= shiftAmount;
                  shiftedExtent.xmax -= shiftAmount;
                  return shiftedExtent;
                };

                this.openingGoTo(mapView, shifted, goToOptions)
                  .then(() => resolve())
                  .catch(() => resolve());
              });
            } else {
              resolve();
            }
          } catch (error) {
            console.error('Error in extent-based animation block:', error);
            resolve();
          }
        }, 100);
      });
    }
    return Promise.resolve();
  }

  /**
   * Zoom map to include all unified markers (site, activities, bid items, deliveries, pictures).
   * Note: Picture outliers are already sanitized upstream via sanitizePictureCoordinates().
   */
  private zoomToIncludeUnifiedMarkers(): void {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }

    const mapView = this.mapComponentInstance.mapViewInstance;

    // Collect all coordinates from site markers and unified markers
    const coordinates: Array<{ longitude: number; latitude: number }> = [];

    // Add site marker coordinates (from filteredSites)
    this.filteredSites?.forEach(site => {
      if (site.longitude && site.latitude) {
        coordinates.push({
          longitude: parseFloat(site.longitude),
          latitude: parseFloat(site.latitude)
        });
      }
    });

    // Add activity marker coordinates
    this.activityMarkers?.forEach(marker => {
      if (marker.geometry?.longitude && marker.geometry?.latitude) {
        coordinates.push({
          longitude: marker.geometry.longitude,
          latitude: marker.geometry.latitude
        });
      }
    });

    // Add bid item marker coordinates
    this.bidItemMarkers?.forEach(marker => {
      if (marker.geometry?.longitude && marker.geometry?.latitude) {
        coordinates.push({
          longitude: marker.geometry.longitude,
          latitude: marker.geometry.latitude
        });
      }
    });

    // Add delivery marker coordinates
    this.deliveryMarkers?.forEach(marker => {
      if (marker.geometry?.longitude && marker.geometry?.latitude) {
        coordinates.push({
          longitude: marker.geometry.longitude,
          latitude: marker.geometry.latitude
        });
      }
    });

    // Add form marker coordinates
    this.formMarkers?.forEach(marker => {
      if (marker.geometry?.longitude && marker.geometry?.latitude) {
        coordinates.push({
          longitude: marker.geometry.longitude,
          latitude: marker.geometry.latitude
        });
      }
    });

    // Add picture marker coordinates (outliers already have null coords from sanitization)
    this.pictureMarkers?.forEach(picture => {
      if (picture.longitude && picture.latitude) {
        coordinates.push({
          longitude: parseFloat(picture.longitude),
          latitude: parseFloat(picture.latitude)
        });
      }
    });

    // Add site and as-built geometry coordinates to ensure they're included in the extent
    this.reportGeometries?.forEach(geometry => {
      if (geometry.coordinates) {
        this.extractCoordinatesFromGeometry(geometry.coordinates, coordinates);
      }
    });

    if (coordinates.length === 0) {
      return;
    }

    // Filter out invalid coordinates (NaN, null, undefined, 0,0)
    const validCoordinates = coordinates.filter(coord => {
      return coord.longitude && coord.latitude &&
             !isNaN(coord.longitude) && !isNaN(coord.latitude) &&
             coord.longitude !== 0 && coord.latitude !== 0;
    });

    if (validCoordinates.length === 0) {
      return;
    }

    // Calculate bounding extent
    let extent = this.geometryService.calculateBoundingExtent(validCoordinates);

    // If extent is null (single point), create a minimal extent
    if (!extent && validCoordinates.length === 1) {
      const { longitude, latitude } = validCoordinates[0];
      const bufferLon = 0.001;
      const bufferLat = 0.001;
      extent = new Extent({
        xmin: longitude - bufferLon,
        xmax: longitude + bufferLon,
        ymin: latitude - bufferLat,
        ymax: latitude + bufferLat,
      });
    }

    if (extent) {
      extent = this.includeVisibleDesignLayers(extent);
      mapView.when(async () => {
        // Wait for any existing animation to complete first
        if (mapView.animation) {
          try {
            await mapView.animation.when();
          } catch (e) {
            // Animation was cancelled, which is fine
          }
        }

        // Use larger expansion so markers aren't at the edge of the screen
        const expansionFactor = 2.0;
        const targetExtent = extent.expand(expansionFactor);

        // Account for left sidenav panel in fullscreen mode
        const leftPadding = this.isFullScreen ? 452 : 0;

        // An opening fit too: recorded, and retried once if interrupted
        this.openingGoTo(mapView, () => targetExtent, {
          duration: 1500,
          easing: 'ease-in-out',
          animate: true,
          padding: { top: 120, left: leftPadding, right: 0, bottom: 0 }
        }).catch(err => {
          // Ignore navigation errors (e.g., if user interacts during zoom)
        });
      });
    }
  }

  // Whether the embedded viewer has already framed the geometry once. Avoids
  // re-fitting (and fighting the user's manual zoom) on later data refreshes.
  private hasFittedEmbeddedGeometry = false;

  /**
   * Embedded viewer (Form 266 map pane) only: fit the union extent of the drawn
   * as-built / bid-item geometry graphics (mapMultiGeoGraphicsLayer,
   * mapPolygonGraphicsLayer, mapProjectGraphicsLayer) with modest padding so the
   * form's geometry is fully framed on load. Returns true if a fit was issued.
   */
  private fitEmbeddedToReportGeometries(): boolean {
    if (this.hasFittedEmbeddedGeometry) {
      return false;
    }
    const mapInstance: any = this.mapComponentInstance;
    const mapView = mapInstance?.mapViewInstance;
    if (!mapView) {
      return false;
    }

    const layers = [
      mapInstance.mapMultiGeoGraphicsLayer,
      mapInstance.mapPolygonGraphicsLayer,
      mapInstance.mapProjectGraphicsLayer,
    ].filter(Boolean);

    const coordinates: Array<{ longitude: number; latitude: number }> = [];
    layers.forEach((layer: any) => {
      const graphics = layer?.graphics?.toArray?.() || [];
      graphics.forEach((graphic: any) => {
        const geometry = graphic?.geometry;
        if (!geometry) {
          return;
        }
        if (geometry.type === 'polygon' && geometry.rings) {
          this.extractCoordinatesFromGeometry(geometry.rings, coordinates);
        } else if (geometry.type === 'polyline' && geometry.paths) {
          this.extractCoordinatesFromGeometry(geometry.paths, coordinates);
        } else if (geometry.type === 'point') {
          const longitude = geometry.longitude ?? geometry.x;
          const latitude = geometry.latitude ?? geometry.y;
          if (longitude != null && latitude != null) {
            coordinates.push({ longitude, latitude });
          }
        }
      });
    });

    const validCoordinates = coordinates.filter(coord =>
      coord.longitude && coord.latitude &&
      !isNaN(coord.longitude) && !isNaN(coord.latitude) &&
      coord.longitude !== 0 && coord.latitude !== 0
    );

    if (validCoordinates.length === 0) {
      return false;
    }

    let extent = this.geometryService.calculateBoundingExtent(validCoordinates);
    if (!extent && validCoordinates.length === 1) {
      const { longitude, latitude } = validCoordinates[0];
      const buffer = 0.0005;
      extent = new Extent({
        xmin: longitude - buffer,
        xmax: longitude + buffer,
        ymin: latitude - buffer,
        ymax: latitude + buffer,
        spatialReference: { wkid: 4326 },
      });
    }
    if (!extent) {
      return false;
    }

    this.hasFittedEmbeddedGeometry = true;
    const targetExtent = this.includeVisibleDesignLayers(extent).expand(1.3);
    mapView.when(() => {
      mapView.goTo(targetExtent, {
        duration: 800,
        easing: 'ease-in-out',
        animate: true,
        padding: { top: 24, left: 24, right: 24, bottom: 24 },
      }).catch(() => {
        // Ignore navigation interruptions (e.g. user interacts mid-animation)
      });
    });
    return true;
  }

  /**
   * Extract all coordinate points from a GeoJSON geometry's coordinates array.
   * Handles nested arrays for Polygon, MultiPolygon, LineString, MultiLineString, etc.
   */
  private extractCoordinatesFromGeometry(
    coords: any,
    result: Array<{ longitude: number; latitude: number }>
  ): void {
    if (!Array.isArray(coords)) {
      return;
    }

    // Check if this is a coordinate pair [longitude, latitude]
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      result.push({
        longitude: coords[0],
        latitude: coords[1]
      });
      return;
    }

    // Otherwise, recurse into nested arrays
    coords.forEach(item => {
      if (Array.isArray(item)) {
        this.extractCoordinatesFromGeometry(item, result);
      }
    });
  }

  /**
   * Build the unified markers layer's definitionExpression from the current
   * layer settings so that the Pictures toggle AND the Types tab (bidItem,
   * activity, delivery) both gate which markers render. Also used under the
   * heatmap renderer to exclude unwanted types from density contributions.
   * multi_item_location markers are always kept — they're aggregates that
   * still represent selected types.
   */
  private applyUnifiedDefinitionExpression(): void {
    if (!this.unifiedMarkersLayer) {
      return;
    }
    // Always include bid_item / activity / delivery / multi_item_location —
    // the Types tab filters those at the data-fetch level in map-layer-container,
    // not at the definition-expression level. Picture visibility is the only
    // one gated here, mirroring production behavior.
    const newExpression = this.layerSettings.picture
      ? "markerType = 'bid_item' OR markerType = 'activity' OR markerType = 'delivery' OR markerType = 'form' OR markerType = 'picture' OR markerType = 'multi_item_location'"
      : "markerType = 'bid_item' OR markerType = 'activity' OR markerType = 'delivery' OR markerType = 'form' OR markerType = 'multi_item_location'";
    this.unifiedMarkersLayer.definitionExpression = newExpression;
    this.unifiedMarkersLayer.refresh();
  }

  drawReportGeometries(skipSiteGeometries: boolean = false, skipZoom: boolean = false) {
    if (!this.mapComponentInstance) {
      return;
    }

    // Reset flag if report geometries are empty AND not during play/drag
    // During play/drag, geometries may temporarily clear while loading, but we don't want to reset
    if ((!this.reportGeometries || this.reportGeometries.length === 0) && !this.isSliderDragging && !this.isPlaying) {
      this.siteGeometriesDrawn = false;
    }

    // Always separate site geometries and as-built geometries
    const siteGeometries = [];
    const otherGeometries = [];

    if (this.reportGeometries && this.reportGeometries.length > 0) {
      this.reportGeometries.forEach(geometry => {
        if (geometry.isSiteGeometry) {
          // Find the corresponding site data for tooltip
          const site = this.filteredSites.find(s =>
            (s.site_id || s.id) === geometry.siteId
          );

          // Always add geometry, even if site data isn't available yet
          // This fixes timing issues where geometries arrive before site data
          siteGeometries.push({
            ...geometry,
            attributes: {
              data: site || null,
              category: 'site',
              level: 2,
              isSiteGeometry: true  // Mark as site geometry for hover effect exclusion
            }
          });
        } else {
          otherGeometries.push(geometry);
        }
      });
    }

    // Header map on a multi-site project: draw every site's boundary, muting the
    // ones without reports in the range (no-op when boundaries were pushed in)
    const headerSiteGeometries = this.buildHeaderSiteGeometries();
    siteGeometries.push(...headerSiteGeometries);

    // Record modes (Issues / Punch list / Claims tabs): their pins replace the
    // daily-report layers, but the site boundaries stay when the Sites layer is
    // on (fullscreen) so the pins have context. Zoom belongs to the pins: frame
    // the sites only while there are none.
    if (this.layerSettings.warrantyClaim) {
      const showSites = !!this.layerSettings.siteBoundary;
      if (this.mapComponentInstance.mapProjectGraphicsLayer) {
        this.mapComponentInstance.mapProjectGraphicsLayer.removeAll();
        if (showSites) {
          this.drawSiteGeometryGraphics(siteGeometries);
          this.siteGeometriesDrawn = siteGeometries.length > 0;
        }
        this.mapComponentInstance.mapProjectGraphicsLayer.visible = showSites;
      }
      if (this.mapComponentInstance.mapMultiGeoGraphicsLayer) {
        this.mapComponentInstance.mapMultiGeoGraphicsLayer.removeAll();
        this.mapComponentInstance.mapMultiGeoGraphicsLayer.visible = false;
      }
      if (this.mapComponentInstance.mapSiteMarkersGraphicsLayer) {
        if (!showSites) this.mapComponentInstance.mapSiteMarkersGraphicsLayer.removeAll();
        this.mapComponentInstance.mapSiteMarkersGraphicsLayer.visible = showSites;
      }
      if (this.mapComponentInstance.mapPolygonGraphicsLayer) {
        this.mapComponentInstance.mapPolygonGraphicsLayer.removeAll();
        this.mapComponentInstance.mapPolygonGraphicsLayer.visible = false;
      }
      if (this.unifiedMarkersLayer) this.unifiedMarkersLayer.visible = false;
      if (showSites && this.isFullScreen && !skipZoom && !this.hasRecordPins()) {
        this.zoomToSitesOrProject();
      }
      return;
    }

    // Handle site boundary layer (mapProjectGraphicsLayer)
    if (this.mapComponentInstance.mapProjectGraphicsLayer) {
      // Skip site geometry redraw during play/drag (they're permanent, not date-dependent)
      if (!skipSiteGeometries) {
        // Always clear and redraw
        this.mapComponentInstance.mapProjectGraphicsLayer.removeAll();
        if (siteGeometries.length > 0) {
          this.drawSiteGeometryGraphics(siteGeometries);
          // Mark that site geometries have been drawn
          this.siteGeometriesDrawn = true;
        }
      }

      // ALWAYS update visibility based on layer settings (moved outside skipSiteGeometries check)
      this.mapComponentInstance.mapProjectGraphicsLayer.visible = this.layerSettings.siteBoundary;
    }

    // Handle as-built layer (mapMultiGeoGraphicsLayer)
    if (this.mapComponentInstance.mapMultiGeoGraphicsLayer) {
      // Always clear and redraw
      this.mapComponentInstance.mapMultiGeoGraphicsLayer.removeAll();

      if (otherGeometries.length > 0) {
        // Apply colors based on view mode
        const coloredGeometries = otherGeometries.map(geometry => {
          if (this._viewBy === 'bid_item') {
            // In bid_item view mode, color by bid item
            const bidItemId = geometry.bidItemId || geometry.bid_item_id;
            if (bidItemId && this.bidItemColorMap[bidItemId]) {
              return {
                ...geometry,
                color: this.bidItemColorMap[bidItemId],
                bidItemId: bidItemId // Ensure bidItemId is set for hover effect
              };
            } else {
              // No bid item association - use gray color
              return {
                ...geometry,
                color: '#9CA3AF' // Gray for unassociated geometries
              };
            }
          } else if (this._viewBy === 'day') {
            // In day view mode, color by date
            const rawDate = geometry.report_date || geometry.reportDate;
            // Normalize date to YYYY-MM-DD format (dateColorMap keys are normalized)
            const geometryDate = rawDate ? rawDate.split('T')[0] : null;
            if (geometryDate && this.dateColorMap[geometryDate]) {
              return {
                ...geometry,
                color: this.dateColorMap[geometryDate],
                normalizedDate: geometryDate // Store for hover effect
              };
            } else {
              // No date - use gray color (site boundaries and undated geometries)
              return {
                ...geometry,
                color: '#9CA3AF' // Gray for undated geometries
              };
            }
          } else if (this._viewBy === 'inspector') {
            // In inspector view mode, color by creator/inspector
            const inspectorId = geometry.created_by_id;
            // Ensure string comparison for ID matching
            const inspectorIdStr = inspectorId ? String(inspectorId) : null;
            if (inspectorIdStr && this.inspectorColorMap[inspectorIdStr]) {
              return {
                ...geometry,
                color: this.inspectorColorMap[inspectorIdStr],
                created_by_id: inspectorIdStr // Ensure created_by_id is set for hover effect
              };
            } else {
              // No inspector association - use gray color
              return {
                ...geometry,
                color: '#9CA3AF' // Gray for unassociated geometries
              };
            }
          } else {
            // In site view mode, color by site (existing behavior)
            const geometrySiteId = geometry.siteId || geometry.site_id;
            if (geometrySiteId && this.siteColorMap[geometrySiteId]) {
              return {
                ...geometry,
                color: this.siteColorMap[geometrySiteId]
              };
            }
            return geometry;
          }
        });

        this._currentColoredGeometries = coloredGeometries;
        this.mapService.drawGeoJsonGeometries(this.mapComponentInstance, coloredGeometries, true);
      }

      // Update visibility based on layer settings, but don't override drilldown's hidden state
      const isInDrilldownMode = this.selectedItemData !== null;
      if (!isInDrilldownMode) {
        this.mapComponentInstance.mapMultiGeoGraphicsLayer.visible = this.layerSettings.asBuilt;
        // Contributions heatmap is derived from bid-item as-built geometries,
        // so require BOTH the As-builts layer AND the bidItem type to be on.
        this.mapHeatmapContributionsService.setVisible(this.layerSettings.asBuilt);
      }
      this.syncLabelSources();
    }

    // After drawing geometries, update the zoom to include them
    // Only zoom to filtered sites (sites with reports)
    const filteredMarkers = this.markersList.filter(marker =>
      marker.data?.isFiltered === true
    );

    // Use filtered markers if available, otherwise fall back to all markers
    const markersForZoom = filteredMarkers.length > 0 ? filteredMarkers : this.markersList;

    // Header site boundaries: frame the sites with reports in the range; if
    // there are none at all, frame every site
    const reportedHeaderGeometries = headerSiteGeometries.filter(geometry => !geometry.muted);
    const headerGeometriesForZoom = (reportedHeaderGeometries.length || filteredMarkers.length)
      ? reportedHeaderGeometries
      : headerSiteGeometries;

    // Include geometries in zoom if their layer is enabled
    const hasVisibleSiteGeometries = this.layerSettings.siteBoundary && siteGeometries.length > 0;
    const hasVisibleAsBuilts = this.layerSettings.asBuilt && otherGeometries.length > 0;
    const hasAnyVisibleGeometries = hasVisibleSiteGeometries || hasVisibleAsBuilts;

    // Only zoom if not dragging or playing (let user control zoom during these interactions)
    // Also skip if we're recreating the unified layer (it handles its own zoom logic)
    // Also skip if explicitly requested (e.g., when just re-coloring for view mode change)
    // In single-day mode, skip zoom here — zoomToIncludeUnifiedMarkers handles it
    const isRecreatingUnifiedLayer = (this as any).isRecreatingLayer || false;
    // Bypass the skip on the very first zoom — see the companion flag in
    // drawUnifiedMarkers' zoom path for the rationale (direct-URL load race).
    const shouldSkipZoom = this.hasPerformedInitialZoom
      && (isRecreatingUnifiedLayer || skipZoom || this.isSingleDayMode);


    // Embedded viewer (Form 266 map pane): fit the union extent of the drawn
    // as-built/bid-item geometry graphics so the form's geometry is fully framed
    // on load, rather than the site-centered marker view (which the site center
    // feed and zoomToIncludeUnifiedMarkers would otherwise produce). Scoped to
    // the embedded single-day context so fullscreen/main-map zoom is unchanged.
    if (this.showEmbeddedControls && !this.isSliderDragging && !this.isPlaying) {
      const fitted = this.fitEmbeddedToReportGeometries();
      if (fitted) {
        this.hasPerformedInitialZoom = true;
        return;
      }
    }

    if (!this.isSliderDragging && !this.isPlaying && !shouldSkipZoom) {
      if (markersForZoom.length > 0 || hasAnyVisibleGeometries) {
        // Store the animation promise so drawUnifiedMarkers can wait for it
        this.initialAnimationPromise = this.animateToExtent(markersForZoom, hasAnyVisibleGeometries, headerGeometriesForZoom)
          .finally(() => {
            this.initialAnimationPromise = null;
            this.hasPerformedInitialZoom = true;
          });
      } else if (this.projectData?.latitude && this.projectData?.longitude) {
        // Fallback: If no filtered sites, zoom to project location with wider view
        this.zoomToProjectLocation();
        this.hasPerformedInitialZoom = true;
      }
    }
  }

  /**
   * The header (non-fullscreen) map only receives site boundaries for
   * single-site projects. For every other project draw them from the sites we
   * already hold, so the map shows the whole project even when no site has
   * reports in the date range.
   */
  private usesHeaderSiteGeometries(): boolean {
    return !this.isFullScreen
      && !this.layerSettings?.warrantyClaim
      && !(this.reportGeometries || []).some(geometry => geometry.isSiteGeometry);
  }

  /**
   * Site boundaries for the header map, built from the sites' own geometry.
   * Sites without reports in the range are `muted`; they're listed first so
   * the reported sites draw on top.
   */
  private buildHeaderSiteGeometries(): HeaderSiteGeometry[] {
    if (!this.usesHeaderSiteGeometries()) {
      return [];
    }
    return (this.filteredSites || [])
      .filter(site => site?.geometry?.type && site.geometry.coordinates)
      .map(site => {
        const muted = site.isFiltered === false;
        return {
          type: site.geometry.type,
          coordinates: site.geometry.coordinates,
          siteId: site.site_id || site.id,
          isSiteGeometry: true as const,
          isHeaderSiteGeometry: true as const,
          muted,
          attributes: {
            data: { ...site, level: 2 },
            category: 'site',
            level: 2,
            isSiteGeometry: true,
          },
        };
      })
      .sort((a, b) => Number(b.muted) - Number(a.muted));
  }

  /** "26 Sep – 11 Oct" for the current date range, or '' when there isn't one. */
  private formatDateRangeLabel(): string {
    const start = this._dateRange?.start;
    const end = this._dateRange?.end;
    if (!(start instanceof Date) || !(end instanceof Date)) {
      return '';
    }
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${fmt(start)} – ${fmt(end)}`;
  }

  /** Draws the site boundary graphics (the Sites layer) for the given site geometries. */
  private drawSiteGeometryGraphics(siteGeometries: any[]): void {
    siteGeometries.forEach(siteGeometry => {
      const siteId = siteGeometry.siteId;
      // Sites without reports in the range: grey, faint fill, thin outline
      const muted = !!siteGeometry.muted;
      const siteColor = muted ? MUTED_SITE_COLOR : (this.siteColorMap[siteId] || '#0091FF');
      const polygonOpacity = muted ? { fill: 0.12, outline: 0.6 } : null;
      const strokeWidth = muted ? 1.5 : null;
      const lineOpacity = muted ? 0.6 : null;

      let geometryGraphic = null;
      switch (siteGeometry.type) {
        case 'Polygon':
          geometryGraphic = this.graphicFactory.createPolygonGraphic(siteGeometry.coordinates, false, false, siteGeometry.attributes, siteColor, strokeWidth, polygonOpacity);
          break;
        case 'MultiPolygon':
          geometryGraphic = this.graphicFactory.createPolygonGraphic(siteGeometry.coordinates, true, false, siteGeometry.attributes, siteColor, strokeWidth, polygonOpacity);
          break;
        case 'LineString':
          geometryGraphic = this.graphicFactory.createPolylineGraphic(siteGeometry.coordinates, false, siteColor, siteGeometry.attributes, muted ? 2 : null, null, lineOpacity);
          break;
        case 'MultiLineString':
          geometryGraphic = this.graphicFactory.createPolylineGraphic(siteGeometry.coordinates, true, siteColor, siteGeometry.attributes, muted ? 2 : null, null, lineOpacity);
          break;
      }

      if (geometryGraphic) {
        this.mapComponentInstance.drawGeoMultipleGraphicLayer(geometryGraphic, false);
      }
    });
  }

  private getReportGeometrySignature(): string {
    return JSON.stringify((this.reportGeometries || []).map(geometry => ({
      type: geometry.type,
      coordinates: geometry.coordinates,
      siteId: geometry.siteId || geometry.site_id,
      reportBidItemId: geometry.report_bid_item_id,
      reportActivityId: geometry.report_activity_id,
      reportDeliveryId: geometry.report_material_delivery_id,
      isStationRange: geometry.is_station_range,
      lineStyle: geometry.lineStyle,
      strokeWidth: geometry.strokeWidth,
      opacity: geometry.opacity,
    })));
  }

  private refreshMapData() {
    // In fullscreen mode, we need to manually trigger a data refresh
    // The parent component would normally handle this through the project details subscription
    // but in fullscreen we need to directly update based on the date range

    if (this._dateRange && this._dateRange.start && this._dateRange.end) {
      // The router navigation will trigger the parent project component to reload data
      // which will then flow through the mapService.initiateSiteLocationsMap observable
      // This is already handled by the existing query param subscription in the parent
      // so we just need to make sure this component redraws when filtered sites change

      // The actual data refresh happens automatically through the query param
      // subscription in the parent component, which watches startDate/endDate
      // and calls the API to get filtered site data
    }
  }

  /**
   * Fullscreen record mode with no pins (and the payload doesn't ask to keep
   * the view): show the sites rather than whatever the map last framed.
   */
  private isRecordSitesFallback(fit: unknown): boolean {
    return fit !== false && this.isFullScreen && !!this.layerSettings?.warrantyClaim
      && !this.hasRecordPins() && !!this.mapComponentInstance?.mapViewInstance;
  }

  /** True while a record mode (Issues / Punch list / Claims) has pins on the map. */
  private hasRecordPins(): boolean {
    return (this.warrantyClaimMarkers?.length || 0) + (this.warrantyIssueMarkers?.length || 0) > 0;
  }

  /**
   * The sites (their markers, plus their boundaries when that layer is on),
   * else the project location. Frames like the record pins (setExtent), since
   * those modes have no side panel to offset for.
   */
  private zoomToSitesOrProject(): void {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }
    const points: { geometry: { longitude: number; latitude: number } }[] = (this.markersList || [])
      .map(marker => ({ geometry: { longitude: Number(marker.geometry?.longitude), latitude: Number(marker.geometry?.latitude) } }));
    if (this.layerSettings?.siteBoundary) {
      (this.reportGeometries || [])
        .filter(geometry => geometry.isSiteGeometry)
        .forEach(geometry => collectLonLats(geometry.coordinates, points));
    }
    const located = points.filter(p => isFinite(p.geometry.longitude) && isFinite(p.geometry.latitude));
    if (located.length) {
      this.mapService.setExtent(this.mapComponentInstance, located, 1.4);
    } else if (this.projectData?.latitude && this.projectData?.longitude) {
      this.zoomToProjectLocation();
    }
  }

  private isZoomingToProject = false;

  private zoomToProjectLocation(): void {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }

    // Prevent multiple simultaneous zoom calls
    if (this.isZoomingToProject) {
      return;
    }

    this.isZoomingToProject = true;

    const mapView = this.mapComponentInstance.mapViewInstance;
    const latitude = parseFloat(this.projectData.latitude);
    const longitude = parseFloat(this.projectData.longitude);

    // Create a wider extent around the project location (zoomed out more)
    // 0.02 degrees is roughly 2.2 km at this latitude, giving a good overview
    const buffer = 0.02;
    const extent = this.includeVisibleDesignLayers(new Extent({
      xmin: longitude - buffer,
      xmax: longitude + buffer,
      ymin: latitude - buffer,
      ymax: latitude + buffer,
      spatialReference: { wkid: 4326 }
    }));

    setTimeout(() => {
      try {
        mapView.when(() => {
          // Computed when the view has a size: the shift divides by its width
          const target = () => {
            // Expand the extent slightly for better overview
            let targetExtent = extent.expand(1.5);

            // Shift extent to center in the visible area (right of side panel) if in fullscreen
            if (this.isFullScreen) {
              const sidePanelWidth = 400;
              const viewWidth = mapView.width;
              const extentWidth = targetExtent.width;
              const shiftAmount = (sidePanelWidth / viewWidth) * extentWidth * 0.5;

              // Create a new extent with the shifted coordinates
              targetExtent = new Extent({
                xmin: targetExtent.xmin + shiftAmount,
                xmax: targetExtent.xmax + shiftAmount,
                ymin: targetExtent.ymin,
                ymax: targetExtent.ymax,
                spatialReference: targetExtent.spatialReference
              });
            }
            return targetExtent;
          };

          this.openingGoTo(mapView, target, {
            duration: 2000,
            easing: 'ease-in-out'
          }).catch(error => {
            // Suppress "goto-interrupted" errors as they're expected when animations overlap
            if (error?.name !== 'view:goto-interrupted') {
              console.error('Error animating to project location:', error);
            }
          }).finally(() => {
            // Reset the flag after animation completes or fails
            this.isZoomingToProject = false;
          });
        });
      } catch (error) {
        console.error('Error zooming to project location:', error);
        this.isZoomingToProject = false;
      }
    }, 100);
  }

  private applyHoverFadeEffect(siteId: string | null): void {
    if (this.mapComponentInstance) {
      // Check if we have selections (used when hover is null)
      const hasSelections = this._selectedSiteIds.size > 0;

      // Note: Accessing private properties via any cast to fix layer targeting
      const mapInstance = this.mapComponentInstance as any;
      const graphicsLayers = [
        mapInstance.mapSiteMarkersGraphicsLayer,
        mapInstance.mapProjectGraphicsLayer,
        mapInstance.mapMultiGeoGraphicsLayer,
        mapInstance.mapPolygonGraphicsLayer,
        mapInstance.mapStationMarkersGraphicsLayer // Add station markers layer
      ].filter(layer => layer);

      graphicsLayers.forEach(layer => {
        if (layer && layer.graphics) {
          layer.graphics.forEach(graphic => {
            const graphicSiteId = graphic.attributes?.data?.site_id || graphic.attributes?.data?.id || graphic.attributes?.siteId;

            // Store original symbol if not already stored
            if (!graphic.attributes._originalSymbol && graphic.symbol) {
              graphic.attributes._originalSymbol = graphic.symbol.clone();
            }

            if (siteId) {
              // If hovering, show hovered site AND any selected sites
              // Convert to string for consistent comparison (IDs may be stored as different types)
              const graphicSiteIdStr = graphicSiteId ? String(graphicSiteId) : null;
              const isHoveredOrSelected = graphicSiteIdStr === siteId || this._selectedSiteIds.has(graphicSiteIdStr);
              if (graphicSiteId && isHoveredOrSelected) {
                // Keep hovered/selected site at full opacity (restore original)
                if (graphic.attributes._originalSymbol) {
                  const newSymbol = graphic.attributes._originalSymbol.clone();
                  graphic.symbol = newSymbol;
                }
              } else if (graphicSiteId && !isHoveredOrSelected) {
                // Fade this graphic (not hovered and not selected)
                if (graphic.attributes._originalSymbol) {
                  const newSymbol = graphic.attributes._originalSymbol.clone();
                  this.setSymbolOpacity(newSymbol, 0.3); // 30% opacity relative to original
                  graphic.symbol = newSymbol;
                }
              }
            } else if (hasSelections) {
              // No hover but has selections - show only selected sites
              const graphicSiteIdStr = graphicSiteId ? String(graphicSiteId) : null;
              if (graphicSiteIdStr && this._selectedSiteIds.has(graphicSiteIdStr)) {
                // Selected site - full opacity
                if (graphic.attributes._originalSymbol) {
                  const newSymbol = graphic.attributes._originalSymbol.clone();
                  graphic.symbol = newSymbol;
                }
              } else if (graphicSiteId) {
                // Not selected - fade
                if (graphic.attributes._originalSymbol) {
                  const newSymbol = graphic.attributes._originalSymbol.clone();
                  this.setSymbolOpacity(newSymbol, 0.3);
                  graphic.symbol = newSymbol;
                }
              }
            } else {
              // No hover and no selections - restore all to full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            }
          });
        }
      });

      // Force layer refresh

      // Force layer refresh
      if (this.mapComponentInstance.mapViewInstance) {
        graphicsLayers.forEach(layer => {
          if (layer) {
            layer.refresh && layer.refresh();
          }
        });
      }

      // Handle unified markers layer (FeatureLayer with pictures, bid items, activities, deliveries)
      // Use opacity visual variables instead of featureEffect (works WITH clustering!)
      if (this.unifiedMarkersLayer) {
        // Increment sequence number to track this request
        const currentSequence = ++this.siteHoverSequence;

        // Query all features and update their is_hovered_site attribute
        this.unifiedMarkersLayer.queryFeatures({
          where: '1=1',
          returnGeometry: false,
          outFields: ['*']  // Get all fields to properly clone features
        }).then(result => {
          // Check if this is still the latest request - ignore stale results
          if (currentSequence !== this.siteHoverSequence) {
            return; // Stale request, ignore
          }

          // Build update features array - must be Graphic objects
          const updates = result.features.map(feature => {
            const featureSiteId = feature.attributes.site_id;
            let isVisible: number;

            // Convert to string for consistent comparison (IDs may be stored as different types)
            const featureSiteIdStr = featureSiteId ? String(featureSiteId) : null;

            if (siteId) {
              // Show hovered site AND any selected sites
              isVisible = (featureSiteIdStr === siteId || this._selectedSiteIds.has(featureSiteIdStr)) ? 1 : 0;
            } else if (hasSelections) {
              // No hover but has selections
              isVisible = this._selectedSiteIds.has(featureSiteIdStr) ? 1 : 0;
            } else {
              // No hover and no selections - show all
              isVisible = 1;
            }

            // Create a new Graphic with updated attributes
            const graphic = feature.clone();
            graphic.attributes.is_hovered_site = isVisible;
            return graphic;
          });

          // Apply updates to the layer (check for null - layer may have been removed during async operation)
          if (!this.unifiedMarkersLayer) {
            return;
          }
          this.unifiedMarkersLayer.applyEdits({
            updateFeatures: updates as any
          }).then(editResult => {
            // Check again before refresh - another request may have come in
            if (currentSequence === this.siteHoverSequence && this.unifiedMarkersLayer) {
              // Force layer refresh to apply opacity visual variable
              this.unifiedMarkersLayer.refresh();
            }
          }).catch(error => {
            console.error('❌ Failed to update is_hovered_site attributes:', error);
          });
        }).catch(error => {
          console.error('❌ Failed to query features:', error);
        });
      }
    }
  }

  /**
   * Recursively set opacity for a symbol and its nested layers/graphics
   * Handles CIMSymbol, SimpleMarkerSymbol, SimpleFillSymbol, SimpleLineSymbol
   */
  private setSymbolOpacity(symbol: any, opacity: number): void {
    if (!symbol) return;

    // Handle CIMSymbol
    if (symbol.type === 'cim' || (symbol.data && symbol.data.type === 'CIMSymbolReference')) {
      const cimSymbol = symbol.data ? symbol.data.symbol : symbol;
      if (cimSymbol && cimSymbol.symbolLayers) {
        cimSymbol.symbolLayers.forEach((layer: any) => {
          // Handle CIMVectorMarker
          if (layer.type === 'CIMVectorMarker' && layer.markerGraphics) {
            layer.markerGraphics.forEach((graphic: any) => {
              if (graphic.symbol) {
                this.setSymbolOpacity(graphic.symbol, opacity);
              }
            });
          }
          // Handle CIMSolidFill
          if (layer.type === 'CIMSolidFill' && layer.color) {
            // Ensure color is array [r, g, b, a]
            if (Array.isArray(layer.color) && layer.color.length >= 4) {
              layer.color[3] = opacity * 255;
            }
          }
          // Handle CIMSolidStroke
          if (layer.type === 'CIMSolidStroke' && layer.color) {
            if (Array.isArray(layer.color) && layer.color.length >= 4) {
              layer.color[3] = opacity * 255;
            }
          }
          // Handle CIMPictureMarker
          if (layer.type === 'CIMPictureMarker' && layer.tintColor) {
            if (Array.isArray(layer.tintColor) && layer.tintColor.length >= 4) {
              layer.tintColor[3] = opacity * 255;
            }
          }
        });
      }
      return;
    }

    // Handle SimpleMarkerSymbol, SimpleFillSymbol, SimpleLineSymbol
    if (symbol.color) {
      symbol.color.a = opacity;
    }
    if (symbol.outline && symbol.outline.color) {
      symbol.outline.color.a = opacity;
    }
  }

  /**
   * Apply an obvious hover highlight to a symbol — thick bright outline + fuller fill.
   * Used to make the hovered as-built geometry stand out strongly against faded siblings.
   */
  private applyHoverHighlightSymbol(symbol: any): void {
    if (!symbol) return;
    const HL_OUTLINE = [16, 54, 214]; // Brand blue #1036D6
    const HL_WIDTH = 2.5;

    // CIM symbols
    if (symbol.type === 'cim' || (symbol.data && symbol.data.type === 'CIMSymbolReference')) {
      const cimSymbol = symbol.data ? symbol.data.symbol : symbol;
      if (cimSymbol?.symbolLayers) {
        cimSymbol.symbolLayers.forEach((layer: any) => {
          if (layer.type === 'CIMSolidStroke') {
            layer.width = HL_WIDTH;
            layer.color = [...HL_OUTLINE, 230];
          }
        });
      }
      return;
    }

    // SimpleFillSymbol / SimpleLineSymbol / SimpleMarkerSymbol
    if (symbol.outline) {
      symbol.outline.width = HL_WIDTH;
      if (symbol.outline.color) {
        symbol.outline.color.r = HL_OUTLINE[0];
        symbol.outline.color.g = HL_OUTLINE[1];
        symbol.outline.color.b = HL_OUTLINE[2];
        symbol.outline.color.a = 0.9;
      }
    }
  }

  /**
   * Apply selection fade effect based on current view mode.
   * This is called when selection changes - it triggers the appropriate hover fade effect
   * which will check for selections when there's no active hover.
   */
  private applySelectionFadeEffect(): void {
    // Delegate to the appropriate hover method based on current view mode
    // The hover methods will check for selections when hover is null
    switch (this._viewBy) {
      case 'site':
        this.applyHoverFadeEffect(this._hoveredSiteId);
        break;
      case 'bid_item':
        this.applyBidItemHoverFadeEffect(this._hoveredBidItemId);
        break;
      case 'inspector':
        this.applyInspectorHoverFadeEffect(this._hoveredInspectorId);
        break;
      case 'day':
        this.applyDateHoverFadeEffect(this._hoveredDate);
        break;
    }
  }

  /**
   * Rebuild color maps based on the current view mode
   * In 'site' mode: uses siteColorMap
   * In 'bid_item' mode: builds bidItemColorMap from bidItemsWithGeometries (same order as sidenav)
   * In 'day' mode: builds dateColorMap from all available markers and geometries
   */
  private rebuildColorMapsForViewMode(): void {
    if (this._viewBy === 'bid_item') {
      // Build bid item color map from bidItemsWithGeometries (already sorted by item number)
      // This ensures colors match the sidenav list exactly
      this.bidItemColorMap = {};

      this._bidItemsWithGeometries.forEach((bidItem, index) => {
        this.bidItemColorMap[bidItem.id] = this.colorPalette[index % this.colorPalette.length];
      });
    } else if (this._viewBy === 'inspector') {
      // Build inspector color map from availableInspectors
      // This ensures colors match the sidenav list exactly
      this.inspectorColorMap = {};

      this._availableInspectors.forEach((inspector, index) => {
        // Use the inspector's assigned color if available, otherwise use palette
        // Ensure string keys for consistent matching
        const inspectorIdStr = String(inspector.id);
        this.inspectorColorMap[inspectorIdStr] = inspector.color || this.colorPalette[index % this.colorPalette.length];
      });
    } else if (this._viewBy === 'day') {
      // Build date color map from datesWithReports (passed from parent, same as sidenav)
      // This ensures colors match the sidenav list exactly
      this.dateColorMap = {};

      // Use datesWithReports from parent (already sorted chronologically)
      this._datesWithReports.forEach((date, index) => {
        this.dateColorMap[date] = this.colorPalette[index % this.colorPalette.length];
      });
    }
  }

  /**
   * Get the color configuration for the current view mode.
   * Returns the appropriate color map and view mode to be passed to UnifiedMarkerService.
   */
  private getColorConfigForViewMode(): { viewMode: 'site' | 'bid_item' | 'day' | 'inspector'; colorMap: { [key: string]: string } } | undefined {
    switch (this._viewBy) {
      case 'site':
        return { viewMode: 'site', colorMap: this.siteColorMap };
      case 'bid_item':
        return { viewMode: 'bid_item', colorMap: this.bidItemColorMap };
      case 'day':
        return { viewMode: 'day', colorMap: this.dateColorMap };
      case 'inspector':
        return { viewMode: 'inspector', colorMap: this.inspectorColorMap };
      default:
        return undefined;
    }
  }

  /**
   * Apply hover fade effect for bid items (similar to site hover effect)
   * When hovering a bid item:
   * - Keep that bid item's markers and as-builts fully visible
   * - Fade out everything else
   * When not hovering but has selections:
   * - Keep selected bid items visible, fade others
   */
  private applyBidItemHoverFadeEffect(bidItemId: string | null): void {
    if (!this.mapComponentInstance) return;

    // Check if we have selections (used when hover is null)
    const hasSelections = this._selectedBidItemIds.size > 0;

    // Handle graphics layers (site boundaries, as-builts, etc.)
    const mapInstance = this.mapComponentInstance as any;
    const graphicsLayers = [
      mapInstance.mapProjectGraphicsLayer,
      mapInstance.mapMultiGeoGraphicsLayer,
      mapInstance.mapPolygonGraphicsLayer,
    ].filter(layer => layer);

    graphicsLayers.forEach(layer => {
      if (layer && layer.graphics) {
        layer.graphics.forEach(graphic => {
          const graphicBidItemId = graphic.attributes?.bidItemId || graphic.attributes?.bid_item_id;
          const isSiteGeometry = graphic.attributes?.isSiteGeometry;

          // Skip site boundary graphics - always keep them visible
          if (isSiteGeometry) {
            return;
          }

          // Store original symbol if not already stored
          if (!graphic.attributes._originalSymbol && graphic.symbol) {
            graphic.attributes._originalSymbol = graphic.symbol.clone();
          }

          if (bidItemId) {
            // Embedded Form 266 viewer: isolate ONLY the hovered item — the
            // hovered geometry stays full opacity and everything else fades.
            // The viewer passes every drawn bid item in as "selected" (to render
            // in its highlight color), so honoring the selection set during hover
            // would keep all geometries full and the fade would never be visible.
            // The main project map keeps its prior behavior: a hovered graphic
            // stays full if it is hovered OR currently selected.
            const graphicBidItemIdStr = graphicBidItemId ? String(graphicBidItemId) : null;
            const keepFull = this.showEmbeddedControls
              ? graphicBidItemIdStr === bidItemId
              : graphicBidItemIdStr === bidItemId || this._selectedBidItemIds.has(graphicBidItemIdStr);
            if (graphicBidItemId && keepFull) {
              // Keep hovered (or, on the main map, selected) bid item at full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            } else if (graphicBidItemId && !keepFull) {
              // Fade this graphic (not hovered and not kept-full)
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1); // 10% opacity for faded items
                graphic.symbol = newSymbol;
              }
            } else if (!graphicBidItemId) {
              // Graphics without bid item association (e.g., standalone pictures) - fade them
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            }
          } else if (hasSelections) {
            // No hover but has selections - show only selected bid items
            const graphicBidItemIdStr = graphicBidItemId ? String(graphicBidItemId) : null;
            if (graphicBidItemIdStr && this._selectedBidItemIds.has(graphicBidItemIdStr)) {
              // Selected bid item - full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            } else if (graphicBidItemId) {
              // Not selected - fade
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            } else if (!graphicBidItemId) {
              // Graphics without bid item association - fade them when selections active
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            }
          } else {
            // No hover and no selections - restore all to original opacity
            if (graphic.attributes._originalSymbol) {
              const newSymbol = graphic.attributes._originalSymbol.clone();
              graphic.symbol = newSymbol;
            }
          }
        });
      }
    });

    // Force layer refresh
    if (this.mapComponentInstance.mapViewInstance) {
      graphicsLayers.forEach(layer => {
        if (layer) {
          layer.refresh && layer.refresh();
        }
      });
    }

    // Handle unified markers layer
    if (this.unifiedMarkersLayer) {
      // Increment sequence number to track this request
      const currentSequence = ++this.bidItemHoverSequence;

      this.unifiedMarkersLayer.queryFeatures({
        where: '1=1',
        returnGeometry: false,
        outFields: ['*']
      }).then(result => {
        // Check if this is still the latest request - ignore stale results
        if (currentSequence !== this.bidItemHoverSequence) {
          return; // Stale request, ignore
        }

        const updates = result.features.map(feature => {
          const featureBidItemId = feature.attributes.bid_item_id;
          // Convert to string for consistent comparison (IDs may be stored as different types)
          const featureBidItemIdStr = featureBidItemId ? String(featureBidItemId) : null;
          let isVisible: number;

          if (bidItemId) {
            // Embedded viewer: isolate ONLY the hovered item. Main map: keep the
            // hovered item AND any selected items visible (see graphics-layer
            // branch above for the rationale).
            isVisible = (featureBidItemIdStr === bidItemId
              || (!this.showEmbeddedControls && this._selectedBidItemIds.has(featureBidItemIdStr))) ? 1 : 0;
          } else if (hasSelections) {
            // No hover but has selections
            isVisible = this._selectedBidItemIds.has(featureBidItemIdStr) ? 1 : 0;
          } else {
            // No hover and no selections - show all
            isVisible = 1;
          }

          const graphic = feature.clone();
          graphic.attributes.is_hovered_site = isVisible;
          return graphic;
        });

        this.unifiedMarkersLayer.applyEdits({
          updateFeatures: updates as any
        }).then((editResult) => {
          // Check again before refresh - another request may have come in
          if (currentSequence === this.bidItemHoverSequence) {
            this.unifiedMarkersLayer.refresh();
          }
        }).catch(error => {
          console.error('❌ Failed to update bid item hover attributes:', error);
        });
      }).catch(error => {
        console.error('❌ Failed to query features for bid item hover:', error);
      });
    }
  }

  /**
   * Apply hover fade effect for inspectors (similar to bid item hover effect)
   * When hovering an inspector:
   * - Keep that inspector's markers and as-builts fully visible
   * - Fade out everything else
   * When not hovering but has selections:
   * - Keep selected inspectors visible, fade others
   */
  private applyInspectorHoverFadeEffect(inspectorId: string | null): void {
    if (!this.mapComponentInstance) return;

    // Check if we have selections (used when hover is null)
    const hasSelections = this._selectedInspectorIds.size > 0;

    // Handle graphics layers (site boundaries, as-builts, etc.)
    const mapInstance = this.mapComponentInstance as any;
    const graphicsLayers = [
      mapInstance.mapProjectGraphicsLayer,
      mapInstance.mapMultiGeoGraphicsLayer,
      mapInstance.mapPolygonGraphicsLayer,
    ].filter(layer => layer);

    graphicsLayers.forEach(layer => {
      if (layer && layer.graphics) {
        layer.graphics.forEach(graphic => {
          const graphicInspectorId = graphic.attributes?.inspectorId || graphic.attributes?.created_by_id;
          const isSiteGeometry = graphic.attributes?.isSiteGeometry;

          // Skip site boundary graphics - always keep them visible
          if (isSiteGeometry) {
            return;
          }

          // Store original symbol if not already stored
          if (!graphic.attributes._originalSymbol && graphic.symbol) {
            graphic.attributes._originalSymbol = graphic.symbol.clone();
          }

          if (inspectorId) {
            // If hovering, show hovered inspector AND any selected inspectors
            // Convert to string for consistent comparison (IDs may be stored as different types)
            const graphicInspectorIdStr = graphicInspectorId ? String(graphicInspectorId) : null;
            const isHoveredOrSelected = graphicInspectorIdStr === inspectorId || this._selectedInspectorIds.has(graphicInspectorIdStr);
            if (graphicInspectorId && isHoveredOrSelected) {
              // Keep hovered/selected inspector at full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            } else if (graphicInspectorId && !isHoveredOrSelected) {
              // Fade this graphic (not hovered and not selected)
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1); // 10% opacity for faded items
                graphic.symbol = newSymbol;
              }
            } else if (!graphicInspectorId) {
              // Graphics without inspector association - fade them
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            }
          } else if (hasSelections) {
            // No hover but has selections - show only selected inspectors
            const graphicInspectorIdStr = graphicInspectorId ? String(graphicInspectorId) : null;
            if (graphicInspectorIdStr && this._selectedInspectorIds.has(graphicInspectorIdStr)) {
              // Selected inspector - full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            } else if (graphicInspectorId) {
              // Not selected - fade
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            } else if (!graphicInspectorId) {
              // Graphics without inspector association - fade them when selections active
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            }
          } else {
            // No hover and no selections - restore all to original opacity
            if (graphic.attributes._originalSymbol) {
              const newSymbol = graphic.attributes._originalSymbol.clone();
              graphic.symbol = newSymbol;
            }
          }
        });
      }
    });

    // Force layer refresh
    if (this.mapComponentInstance.mapViewInstance) {
      graphicsLayers.forEach(layer => {
        if (layer) {
          layer.refresh && layer.refresh();
        }
      });
    }

    // Handle unified markers layer
    if (this.unifiedMarkersLayer) {
      // Increment sequence number to track this request
      const currentSequence = ++this.inspectorHoverSequence;

      this.unifiedMarkersLayer.queryFeatures({
        where: '1=1',
        returnGeometry: false,
        outFields: ['*']
      }).then(result => {
        // Check if this is still the latest request - ignore stale results
        if (currentSequence !== this.inspectorHoverSequence) {
          return; // Stale request, ignore
        }

        const updates = result.features.map(feature => {
          // Check both camelCase and snake_case for inspector ID
          const featureInspectorId = feature.attributes.createdById || feature.attributes.created_by_id;
          let isVisible: number;

          // Convert to string for consistent comparison (IDs may be stored as different types)
          const featureInspectorIdStr = featureInspectorId ? String(featureInspectorId) : null;

          if (inspectorId) {
            // Show hovered inspector AND any selected inspectors
            isVisible = (featureInspectorIdStr === inspectorId || this._selectedInspectorIds.has(featureInspectorIdStr)) ? 1 : 0;
          } else if (hasSelections) {
            // No hover but has selections
            isVisible = this._selectedInspectorIds.has(featureInspectorIdStr) ? 1 : 0;
          } else {
            // No hover and no selections - show all
            isVisible = 1;
          }

          const graphic = feature.clone();
          graphic.attributes.is_hovered_site = isVisible;
          return graphic;
        });

        this.unifiedMarkersLayer.applyEdits({
          updateFeatures: updates as any
        }).then(() => {
          // Check again before refresh - another request may have come in
          if (currentSequence === this.inspectorHoverSequence) {
            this.unifiedMarkersLayer.refresh();
          }
        }).catch(error => {
          console.error('❌ Failed to update inspector hover attributes:', error);
        });
      }).catch(error => {
        console.error('❌ Failed to query features for inspector hover:', error);
      });
    }
  }

  /**
   * Apply hover fade effect for dates (similar to bid item hover effect)
   * When hovering a date:
   * - Keep that date's markers and as-builts fully visible
   * - Fade out everything else
   * When not hovering but has selections:
   * - Keep selected dates visible, fade others
   */
  private applyDateHoverFadeEffect(date: string | null): void {
    if (!this.mapComponentInstance) return;

    // Check if we have selections (used when hover is null)
    const hasSelections = this._selectedDates.size > 0;

    // Handle graphics layers (site boundaries, as-builts, etc.)
    const mapInstance = this.mapComponentInstance as any;
    const graphicsLayers = [
      mapInstance.mapProjectGraphicsLayer,
      mapInstance.mapMultiGeoGraphicsLayer,
      mapInstance.mapPolygonGraphicsLayer,
    ].filter(layer => layer);

    graphicsLayers.forEach(layer => {
      if (layer && layer.graphics) {
        layer.graphics.forEach(graphic => {
          // Get the geometry's normalized date (set during drawReportGeometries)
          const graphicDate = graphic.attributes?.normalizedDate;
          const isSiteGeometry = graphic.attributes?.isSiteGeometry;

          // Skip site boundary graphics - always keep them visible
          if (isSiteGeometry) {
            return;
          }

          // Store original symbol if not already stored
          if (!graphic.attributes._originalSymbol && graphic.symbol) {
            graphic.attributes._originalSymbol = graphic.symbol.clone();
          }

          if (date) {
            // If hovering, show hovered date AND any selected dates
            // Convert to string for consistent comparison (IDs may be stored as different types)
            const graphicDateStr = graphicDate ? String(graphicDate) : null;
            const isHoveredOrSelected = graphicDateStr === date || this._selectedDates.has(graphicDateStr);
            if (graphicDate && isHoveredOrSelected) {
              // Keep hovered/selected date at full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            } else if (graphicDate && !isHoveredOrSelected) {
              // Fade this graphic (not hovered and not selected)
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1); // 10% opacity for faded items
                graphic.symbol = newSymbol;
              }
            } else if (!graphicDate) {
              // Graphics without date (e.g., site boundaries, undated geometries) - fade them
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            }
          } else if (hasSelections) {
            // No hover but has selections - show only selected dates
            const graphicDateStr = graphicDate ? String(graphicDate) : null;
            if (graphicDateStr && this._selectedDates.has(graphicDateStr)) {
              // Selected date - full opacity
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                graphic.symbol = newSymbol;
              }
            } else if (graphicDate) {
              // Not selected - fade
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            } else if (!graphicDate) {
              // Graphics without date - fade them when selections active
              if (graphic.attributes._originalSymbol) {
                const newSymbol = graphic.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.1);
                graphic.symbol = newSymbol;
              }
            }
          } else {
            // No hover and no selections - restore all to original opacity
            if (graphic.attributes._originalSymbol) {
              const newSymbol = graphic.attributes._originalSymbol.clone();
              graphic.symbol = newSymbol;
            }
          }
        });
      }
    });

    // Force layer refresh
    if (this.mapComponentInstance.mapViewInstance) {
      graphicsLayers.forEach(layer => {
        if (layer) {
          layer.refresh && layer.refresh();
        }
      });
    }

    // Handle unified markers layer
    if (this.unifiedMarkersLayer) {
      this.unifiedMarkersLayer.queryFeatures({
        where: '1=1',
        returnGeometry: false,
        outFields: ['*']
      }).then(result => {
        const updates = result.features.map(feature => {
          // Get the marker's date and normalize it (check both camelCase and snake_case)
          const featureDate = feature.attributes.reportDate || feature.attributes.report_date;
          const normalizedFeatureDate = featureDate ? featureDate.split('T')[0] : null;
          let isVisible: number;

          if (date) {
            // Show hovered date AND any selected dates
            isVisible = (normalizedFeatureDate === date || this._selectedDates.has(normalizedFeatureDate)) ? 1 : 0;
          } else if (hasSelections) {
            // No hover but has selections
            isVisible = this._selectedDates.has(normalizedFeatureDate) ? 1 : 0;
          } else {
            // No hover and no selections - show all
            isVisible = 1;
          }

          const graphic = feature.clone();
          graphic.attributes.is_hovered_site = isVisible;
          return graphic;
        });

        this.unifiedMarkersLayer.applyEdits({
          updateFeatures: updates as any
        }).then(() => {
          this.unifiedMarkersLayer.refresh();
        }).catch(error => {
          console.error('❌ Failed to update date hover attributes:', error);
        });
      }).catch(error => {
        console.error('❌ Failed to query features for date hover:', error);
      });
    }
  }

  /**
   * Handle same-location marker click
   * Opens overlay with all pictures from that location
   */
  private onSameLocationMarkerClick(graphic: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    const attributes = graphic.attributes;

    // First check the drilldown service's map (for drilldown mode same-location pictures)
    // The pictures are stored under the marker's uuid
    let pictures = this.drilldownModeService.getPicturesForMarker(attributes.uuid);

    if (pictures && pictures.length > 0) {
      // Check if pictures have URLs, if not look them up from pictureMarkers
      const firstPic = pictures[0];
      if (!firstPic.original_url && !firstPic.thumb_url) {
        // Picture data is incomplete, look up full data by IDs
        const fullPictures = pictures.map(p => {
          const pictureId = p.id || p.uuid;
          return this.pictureMarkers.find(pm => pm.id === pictureId || pm.uuid === pictureId) || p;
        });
        pictures = fullPictures;
      }
      this.mapService.selectedPicturesData.next(pictures);
      return;
    }

    // Fall back to looking up from pictureMarkers using picture_uuids
    const pictureUuids = attributes.picture_uuids?.split(',') || [];

    // Find all pictures matching these UUIDs
    pictures = this.pictureMarkers.filter(p => pictureUuids.includes(p.uuid) || pictureUuids.includes(p.id));

    if (pictures.length > 0) {
      this.mapService.selectedPicturesData.next(pictures);
    }
  }

  /**
   * Hide the picture hover preview
   * Call this when showing any popup or performing any action that should dismiss the preview
   */
  private hidePictureHoverPreview(): void {
    this.showPictureHoverPreview = false;
    this.pictureHoverPreviewData = [];
  }

  /**
   * Handle picture marker hover for preview tooltip
   * Shows a preview image with optional auto-slideshow for multiple pictures at same location
   */
  private onPictureHover(data: PictureHoverData | null): void {
    if (!data) {
      // Hide preview
      this.hidePictureHoverPreview();
      return;
    }

    // Don't show hover preview if a popup is already open
    if (this.showItemPopup) {
      return;
    }

    // Transform MarkerItem[] to PreviewPicture[] for the preview component
    this.pictureHoverPreviewData = data.pictures.map(pic => ({
      uuid: pic.uuid || pic.id || '',
      thumb_url: pic.thumb_url,
      original_url: pic.original_url,
      created_at: pic.data?.created_at || (pic as any).created_at,
      direction: pic.direction,
      siteName: pic.siteName || (pic as any).site_name,
    }));

    this.pictureHoverScreenPoint = data.screenPoint;
    this.showPictureHoverPreview = true;
  }

  /**
   * Handle picture marker click
   * For clusters: Zoom in first, then show overlay if still clustered
   * For single pictures: Show overlay immediately
   */
  private onPictureMarkerClick(graphic: any, event: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    const attributes = graphic.attributes;


    // ALWAYS try to look up picture data from markerPicturesMap first
    // This handles:
    // 1. Clustered single pictures (cluster_count === 1)
    // 2. Non-clustered pictures when clustering is disabled (cluster_count === undefined)
    // 3. Same-location groups (any count when cluster_count is undefined or 1)
    // 4. Drilldown mode pictures (from drilldown service's map)
    if (attributes.cluster_count === 1 || attributes.cluster_count === undefined) {
      // First check the local markerPicturesMap
      let pictures = this.markerPicturesMap.get(attributes.uuid);

      // If not found, check the drilldown service's map (for drilldown mode pictures)
      if (!pictures || pictures.length === 0) {
        pictures = this.drilldownModeService.getPicturesForMarker(attributes.uuid);
      }

      // If pictures found but missing URLs, look up full data from drilldown service or pictureMarkers
      if (pictures && pictures.length > 0) {
        const firstPic = pictures[0];
        if (!firstPic.original_url && !firstPic.thumb_url) {
          // Picture data is incomplete, look up full data by ID
          const pictureId = firstPic.id || firstPic.uuid || attributes.uuid;

          // First check drilldown service (has complete data in drilldown mode)
          const drilldownPictures = this.drilldownModeService.getPicturesForMarker(pictureId);

          if (drilldownPictures && drilldownPictures.length > 0 && (drilldownPictures[0].thumb_url || drilldownPictures[0].original_url)) {
            pictures = drilldownPictures;
          } else {
            // Fall back to pictureMarkers array
            const fullPicture = this.pictureMarkers.find(p => p.id === pictureId || p.uuid === pictureId);
            if (fullPicture) {
              pictures = [fullPicture];
            }
          }
        }
        this.mapService.selectedPicturesData.next(pictures);
        return;
      }
      // If no pictures found, fall through to other handlers
    }

    // Check if this is a REAL cluster (cluster_count > 1)
    if (attributes.cluster_count !== undefined && attributes.cluster_count > 1) {

      // Zoom in to the cluster location
      const zoomLevel = this.mapComponentInstance.mapViewInstance.zoom + 2; // Zoom in 2 levels

      this.mapComponentInstance.mapViewInstance.goTo({
        target: graphic.geometry,
        zoom: Math.min(zoomLevel, 20) // Max zoom level 20
      }).then(() => {
        // After zoom animation, query the cluster to get all pictures and show overlay

        // Query the cluster to get all picture data
        this.queryClusterPictures(graphic).then(pictures => {
          if (pictures && pictures.length > 0) {
            this.mapService.selectedPicturesData.next(pictures);
          }
        }).catch(error => {
          console.error('Error querying cluster pictures:', error);
        });
      });

      return;
    }

    // Check if this is a same-location marker (multiple pictures at exact same coordinates)
    // Note: When clustered, is_same_location comes from clusterProperties
    const isSameLocation = attributes.is_same_location === 1 || attributes.is_same_location === '1';
    const pictureCount = attributes.count || attributes.total_picture_count || 1;

    if (isSameLocation && pictureCount > 1) {

      // Get the list of picture UUIDs (from clustered or original attributes)
      const uuidsString = attributes.picture_uuids || '';
      const pictureUuids = uuidsString.split(',').map((u: string) => u.trim()).filter((u: string) => u);


      // Find all pictures matching these UUIDs
      const pictures = this.pictureMarkers.filter(pic => pictureUuids.includes(pic.uuid));

      if (pictures.length > 0) {
        this.mapService.selectedPicturesData.next(pictures);
      } else {
        console.warn('Could not find pictures for UUIDs:', pictureUuids);
        console.warn('Available picture markers:', this.pictureMarkers.map(p => p.uuid));
      }

      return;
    }

    // Single picture - open overlay

    // Create picture data object from attributes
    const pictureData = {
      uuid: attributes.uuid,
      thumb_url: attributes.thumb_url,
      original_url: attributes.original_url,
      latitude: attributes.latitude,
      longitude: attributes.longitude,
      direction: attributes.direction,
      site_uuid: attributes.site_uuid,
      site_name: attributes.site_name,
      report_uuid: attributes.report_uuid,
      report_date: attributes.report_date,
      source_type: attributes.source_type,
      source_uuid: attributes.source_uuid,
      source_name: attributes.source_name,
      created_at: attributes.created_at
    };

    this.mapService.selectedPicturesData.next([pictureData]);
  }

  /**
   * Handle bid item marker click to show popup
   * Reuses the popup from daily report map (project-map)
   */
  /**
   * Handle activity marker click
   */
  private onActivityMarkerClick(graphic: any, event: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    // Close picture overlay if open
    this.mapService.selectedPicturesData.next([]);

    // Find the original marker data using the UUID
    const originalMarker = this.activityMarkers.find(m => m.data?.id === graphic.attributes.uuid);

    if (!originalMarker) {
      console.error('❌ Could not find original activity marker data for UUID:', graphic.attributes.uuid);
      return;
    }

    // Create mock event that matches what MapService expects
    const mockEvent = {
      graphic: {
        attributes: {
          category: 'activities',
          data: {
            ...originalMarker.data,
            projectId: this.projectData?.id
          }
        },
        geometry: graphic.geometry
      },
      screenPoint: event.screenPoint
    };


    // Use MapService to format popup data for activities
    this.itemPopupContent = this.mapPopupService.levelOneActivitiesPopupData(mockEvent);
    this.setItemPopupPoint(event.screenPoint);
    this.showItemPopup = true;
  }

  private onDeliveryMarkerClick(graphic: any, event: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    // Close picture overlay if open
    this.mapService.selectedPicturesData.next([]);

    // Find the original marker data using the UUID
    const originalMarker = this.deliveryMarkers.find(m => m.data?.id === graphic.attributes.uuid);

    if (!originalMarker) {
      console.error('❌ Could not find original delivery marker data for UUID:', graphic.attributes.uuid);
      return;
    }

    // Create popup content structure for deliveries
    // Following Figma design: https://www.figma.com/design/Krb8cGxs5bX70BJnoFbbmU/Cons-project?node-id=6399-18570
    const delivery = originalMarker.data;

    // Determine heading - always use "Delivery for [item]"
    let heading: string;
    let subheading1: string;
    let subheading2: string | undefined;

    if (delivery.bid_item?.item) {
      heading = `Delivery for ${delivery.bid_item.item}`;
      // Show material name or description in subheading1
      if (delivery.material?.name) {
        subheading1 = delivery.material.name;
      } else if (delivery.bid_item.description) {
        subheading1 = delivery.bid_item.description;
      } else {
        subheading1 = undefined; // No subheading if no material/description
      }
    } else {
      // Fallback if no bid_item
      heading = 'Material Delivery';
      subheading1 = undefined;
    }

    // Calculate quantity slider values
    const deliveryQty = delivery.quantity || 0;
    const bidItemQty = delivery.bid_item?.quantity || 0;
    const quantityPercent = bidItemQty > 0 ? (deliveryQty / bidItemQty) * 100 : 0;

    this.itemPopupContent = [{
      type: 'deliveries',
      title: 'DELIVERY',
      heading: heading,
      reportDate: delivery.reportDate,
      subheading1: subheading1,
      summary: {
        left: {
          label: 'RECEIVED QTY',
          value: quantityPercent,  // Use percentage for progress bar width
          statistics: bidItemQty > 0 ? `${deliveryQty} / ${bidItemQty}` : `${deliveryQty}`,
        },
        right: null  // No right side - will show comment below
      },
      description: delivery.comment || '',  // Comment section
      pictures_info: `${delivery.pictures?.length || 0} picture${delivery.pictures?.length === 1 ? '' : 's'}`,  // Pictures count below comment
      more_link_text: 'See more',
      data: {
        markerData: delivery,
        type: 'deliveries',
        projectId: this.projectData?.id,
        reportId: delivery.reportId,
        deliveryId: delivery.id
      }
    }];

    this.setItemPopupPoint(event.screenPoint);
    this.showItemPopup = true;
  }

  private onFormMarkerClick(graphic: any, event: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    // Close picture overlay if open
    this.mapService.selectedPicturesData.next([]);

    // Find the original marker data using the UUID
    const originalMarker = this.formMarkers.find(m => m.data?.id === graphic.attributes.uuid);

    if (!originalMarker) {
      console.error('❌ Could not find original form marker data for UUID:', graphic.attributes.uuid);
      return;
    }

    const form = originalMarker.data;

    // Build the popup from the control form's ui_config.summary_display
    this.itemPopupContent = [
      this.mapPopupService.createControlFormPopup(form, event.screenPoint),
    ];

    this.setItemPopupPoint(event.screenPoint);
    this.showItemPopup = true;
  }

  /**
   * Handle multi-item location marker click
   * Shows popover with all items (bid items, activities, deliveries) and pictures at that location
   */
  private onMultiItemLocationClick(graphic: any, event: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    // Close picture overlay if open
    this.mapService.selectedPicturesData.next([]);

    // Get all items at this location from the map
    const items = this.multiItemLocationsMap.get(graphic.attributes.uuid);

    if (!items) {
      console.error('❌ Could not find multi-item location data for UUID:', graphic.attributes.uuid);
      return;
    }


    // Generate popup content for each item type (similar to no-location badge popup)
    const popupItems: any[] = [];

    // Get selected bid item IDs from the map service for filtering
    let selectedBidItemIds: string[] = [];
    this.mapService.selectedBidItemIds.pipe(take(1)).subscribe(ids => {
      selectedBidItemIds = ids || [];
    });
    const hasActiveFilter = selectedBidItemIds.length > 0;

    // Add bid items (filter if needed)
    // Note: bidItem = marker.data, so bid_item UUID is at bidItem.bid_item.id
    const bidItemsToShow = hasActiveFilter
      ? items.bidItems.filter(bidItem => selectedBidItemIds.includes(bidItem.bid_item?.id))
      : items.bidItems;

    bidItemsToShow.forEach(bidItem => {
      // Wrap bidItem in the same structure as normal bid item clicks
      // Note: bidItem is a MarkerItem with { latitude, longitude, data }
      // The popup service expects the raw data (bidItem.data) which contains bid_item
      const mockEvent = {
        graphic: {
          attributes: {
            category: 'bid_items',
            data: bidItem.data
          }
        }
      };
      try {
        const popupData = this.mapPopupService.levelOneBidItemPopupData(mockEvent)[0];
        popupItems.push(popupData);
      } catch (error) {
        console.error('❌ Error generating bid item popup:', error);
      }
    });

    // Add activities
    // Note: activity is a MarkerItem with { latitude, longitude, data }
    items.activities.forEach(activity => {
      const mockEvent = {
        graphic: {
          attributes: {
            category: 'activities', // Plural to match componentTypeOneList
            data: activity.data
          }
        }
      };
      try {
        const popupData = this.mapPopupService.levelOneActivitiesPopupData(mockEvent)[0];
        popupItems.push(popupData);
      } catch (error) {
        console.error('❌ Error generating activity popup:', error);
      }
    });

    // Add deliveries
    // Note: delivery is a MarkerItem with { latitude, longitude, data }
    items.deliveries.forEach(deliveryMarker => {
      const delivery = deliveryMarker.data;
      // Build delivery popup similar to the delivery marker click
      let heading: string;
      let subheading1: string;

      if (delivery?.bid_item?.item) {
        heading = `Delivery for ${delivery.bid_item.item}`;
        if (delivery.material?.name) {
          subheading1 = delivery.material.name;
        } else if (delivery.bid_item.description) {
          subheading1 = delivery.bid_item.description;
        } else {
          subheading1 = undefined;
        }
      } else {
        heading = 'Material Delivery';
        subheading1 = undefined;
      }

      const deliveryQty = delivery?.quantity || 0;
      const bidItemQty = delivery?.bid_item?.quantity || 0;
      const quantityPercent = bidItemQty > 0 ? (deliveryQty / bidItemQty) * 100 : 0;

      popupItems.push({
        type: 'deliveries',
        title: 'DELIVERY',
        heading: heading,
        subheading1: subheading1,
        summary: {
          left: {
            label: 'RECEIVED QTY',
            value: quantityPercent,
            statistics: bidItemQty > 0 ? `${deliveryQty} / ${bidItemQty}` : `${deliveryQty}`,
          },
          right: null
        },
        description: delivery?.comment || '',
        pictures_info: `${delivery?.pictures?.length || 0} picture${delivery?.pictures?.length === 1 ? '' : 's'}`,
        more_link_text: 'See more',
        data: {
          markerData: delivery,
          type: 'deliveries',
          projectId: this.projectData?.id,
          reportId: delivery?.reportId,
          deliveryId: delivery?.id
        }
      });
    });

    // Add control forms
    // Note: form is a MarkerItem with { latitude, longitude, data }
    items.forms?.forEach(formMarker => {
      try {
        popupItems.push(this.mapPopupService.createControlFormPopup(formMarker.data));
      } catch (error) {
        console.error('❌ Error generating form popup:', error);
      }
    });

    // Add pictures as clickable preview items
    // Note: picture is a MarkerItem - some props are on MarkerItem, others on MarkerItem.data
    items.pictures.forEach(pictureMarker => {
      const pictureData = pictureMarker.data || {};
      popupItems.push({
        type: 'picture',
        title: 'PICTURE',
        heading: pictureData.comment || pictureMarker.comment || null,
        thumbnail: pictureData.preview_url || pictureMarker.thumb_url || pictureMarker.original_url || pictureData.thumb_url || pictureData.original_url,
        pictures_info: null,
        more_link_text: 'View',
        data: {
          picture: { ...pictureMarker, ...pictureData },
          type: 'picture'
        }
      });
    });

    this.itemPopupContent = popupItems;
    this.setItemPopupPoint(event.screenPoint);
    this.showItemPopup = true;
  }

  private onBidItemMarkerClick(graphic: any, event: any): void {
    // Hide hover preview when clicking
    this.hidePictureHoverPreview();
    // Don't show popup in non-fullscreen mode
    if (!this.isFullScreen) {
      return;
    }

    // Don't show popup if already in drilldown mode (side panel is open with bid item details)
    if (this.selectedBidItemMarkerId) {
      return;
    }

    // Close picture overlay if open
    this.mapService.selectedPicturesData.next([]);

    // Look up the original marker data using the UUID
    // FeatureLayer can't store complex nested objects, so we need to get the original data
    const originalMarker = this.bidItemMarkers.find(m => m.data?.id === graphic.attributes.uuid);

    if (!originalMarker) {
      console.error('❌ Could not find original marker data for UUID:', graphic.attributes.uuid);
      return;
    }

    // Transform data structure to match what MapService expects
    // MapService expects 'children' array with 'field_values', but fullscreen map has 'sources' with 'source_fields'
    const transformedData = {
      ...originalMarker.data,
      projectId: this.projectData?.id,
      // Transform sources to children format that MapService._getQuantity expects
      ...(originalMarker.data.sources?.length > 0 ? {
        children: originalMarker.data.sources.map((source, idx) => {
          // Transform source_fields to field_values object format
          // source_fields structure: [{field_name: value}, {field_name: value}]
          // Example: [{length_f: 5}, {width_f: 2}]
          // BidService expects: {field_id: value, field_id: value}

          const fieldValuesObj = {};
          const bidItemFields = originalMarker.data.bid_item?.fields || [];

          (source.source_fields || []).forEach((fieldObj, sfIdx) => {
            // Each object has structure {field_name: value}
            const fieldName = Object.keys(fieldObj)[0];  // Get the field name (e.g., "length_f")
            const value = fieldObj[fieldName];  // Get the value


            // Find the field ID by matching the field name
            const field = bidItemFields.find(f => f.name === fieldName);

            if (field && field.id) {
              fieldValuesObj[field.id] = value;
            } else {
            }
          });


          return {
            ...source,
            field_values: fieldValuesObj
          };
        })
      } : {})
    };

    const mockEvent = {
      graphic: {
        attributes: {
          category: 'bid_items',
          data: transformedData
        },
        geometry: graphic.geometry
      },
      screenPoint: event.screenPoint
    };


    // Use existing MapService to format popup data
    // Wrap in try-catch to handle cases where rollup formulas fail
    try {
      this.itemPopupContent = this.mapPopupService.levelOneBidItemPopupData(mockEvent);
      this.setItemPopupPoint(event.screenPoint);
      this.showItemPopup = true;
    } catch (error) {
      console.error('❌ Error generating popup, creating fallback:', error);

      // Create a fallback popup with basic information
      const bidItem = originalMarker.data.bid_item;
      this.itemPopupContent = [{
        type: 'bid_items',
        title: 'BID ITEM',
        heading: bidItem.item,
        subheading1: bidItem.description,
        summary: {
          left: {
            label: 'QUANTITY',
            value: originalMarker.data.quantity || 0,
            statistics: `${originalMarker.data.quantity || 0}${bidItem.uom || ''} / ${bidItem.quantity || 0}${bidItem.uom || ''}`
          }
        },
        more_link_text: 'See more',
        data: {
          markerData: originalMarker.data,
          type: 'bid_items',
          geometry: graphic.geometry,
          screenPoint: event.screenPoint
        }
      }];
      this.setItemPopupPoint(event.screenPoint);
      this.showItemPopup = true;
    }
  }

  /**
   * Handle bid item popup close event
   */
  onBidItemPopupChange(show: boolean): void {
    this.showItemPopup = show;
  }

  /**
   * Sync label visibility: hide labels when a popup is open, restore when closed.
   * Switches between marker labels and as-built centroid labels based on layer settings.
   */
  private syncLabelSources(): void {
    // If popup is open, hide all labels
    if (this._showItemPopup) {
      this.unifiedMarkerService.applyLabels({ enabled: false, labelBy: this._currentLabelConfig.labelBy });
      this.unifiedMarkerService.applyAsBuiltLabels({ enabled: false, labelBy: this._currentLabelConfig.labelBy });
      return;
    }

    const labelsEnabled = this._currentLabelConfig.enabled;
    const asBuiltVisible = this.layerSettings.asBuilt;
    const bidItemVisible = this.layerSettings.bidItem;

    if (labelsEnabled && asBuiltVisible && !bidItemVisible) {
      // As-built only mode: hide marker labels, show centroid labels
      this.unifiedMarkerService.applyLabels({ enabled: false, labelBy: this._currentLabelConfig.labelBy });
      this.updateAsBuiltCentroidLabels();
    } else {
      // Default: use marker labels, hide centroid labels
      this.unifiedMarkerService.applyLabels(this._currentLabelConfig);
      this.unifiedMarkerService.applyAsBuiltLabels({ enabled: false, labelBy: this._currentLabelConfig.labelBy });
    }
  }

  private async updateAsBuiltCentroidLabels(): Promise<void> {
    if (!this.mapComponentInstance?.mapViewInstance || this._currentColoredGeometries.length === 0) {
      return;
    }
    await this.unifiedMarkerService.updateAsBuiltLabelLayer(
      this.mapComponentInstance.mapViewInstance,
      this._currentColoredGeometries,
      this._currentLabelConfig
    );
  }

  /**
   * Embedded Form 266 viewer: render a quantity centroid label for ONLY the
   * hovered bid item's geometries (the main map's label behavior is driven by
   * its own labelConfig via syncLabelSources and is left untouched). On
   * un-hover (bidItemId === null) the label layer is cleared.
   */
  private async updateHoveredBidItemQuantityLabel(bidItemId: string | null): Promise<void> {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) return;

    if (!bidItemId) {
      // Clear the centroid label layer on un-hover.
      await this.unifiedMarkerService.updateAsBuiltLabelLayer(mapView, [], {
        enabled: false,
        labelBy: 'quantity',
      });
      return;
    }

    const hoveredGeometries = this._currentColoredGeometries.filter(g => {
      const gid = g?.bidItemId ?? g?.bid_item_id;
      return gid != null && String(gid) === String(bidItemId);
    });

    await this.unifiedMarkerService.updateAsBuiltLabelLayer(mapView, hoveredGeometries, {
      enabled: true,
      labelBy: 'quantity',
      // One label at the combined centroid of the bid item's shapes — the pieces
      // share one effective quantity, so don't repeat/sum it per geometry.
      aggregateQuantityPerBidItem: true,
    });
  }

  /**
   * Handle "See more" click from bid item popup
   */
  onBidItemSeeMore(event: any): void {

    // Close popup
    this.showItemPopup = false;

    // An Issues tab no-location issue: open it as its pin would.
    const issue = noLocationIssueOf(event);
    if (issue) {
      if (this.isFullScreen) {
        this.mapService.showIssueDetail$.next({ issue });
      } else {
        this.projectsService.issueMapPinSelected.next(issue);
      }
      return;
    }

    // Check if this is a picture click - open picture overlay instead of detail view
    if (event.type === 'picture') {
      this.mapService.selectedPicturesData.next([event.data.picture]);
      return;
    }

    // Get the type from the event data (bid_items, activities, deliveries)
    const itemType = event.type || 'bid_items';


    // Notify map-sidenav to show detail view
    this.mapService.showBidItemDetail.next({
      type: itemType,
      data: event.data.markerData,
      projectId: this.projectData?.id || event.data.markerData.projectId,
      siteId: event.data.markerData.siteId,
      reportId: event.data.markerData.reportId
    });
  }

  /**
   * Lightweight fade: fade all geometries NOT belonging to the active bid item.
   * Called when entering lightweight detail view (geometry click).
   */
  private applyLightweightFade(itemData: any): void {
    if (!this.mapComponentInstance) return;

    const activeBidItemId = itemData?.bid_item?.id || itemData?.bidItemId;
    if (!activeBidItemId) return;

    const mapInstance = this.mapComponentInstance as any;
    const asBuiltLayer = mapInstance?.mapMultiGeoGraphicsLayer;
    if (!asBuiltLayer?.graphics) return;

    asBuiltLayer.graphics.forEach((g: any) => {
      if (g.attributes?.isSiteGeometry) return;

      // Store original visibility
      if (!g.attributes) g.attributes = {};
      if (g.attributes._originalVisible === undefined) {
        g.attributes._originalVisible = g.visible !== false;
      }

      const graphicBidItemId = g.attributes?.bidItemId || g.attributes?.bid_item_id;
      if (String(graphicBidItemId) === String(activeBidItemId)) {
        // Matching bid item - keep visible
        g.visible = true;
      } else {
        // Non-matching - hide completely
        g.visible = false;
      }
    });
  }

  /**
   * Reset lightweight fade: restore all geometries to original opacity.
   */
  private resetLightweightFade(): void {
    if (!this.mapComponentInstance) return;

    const mapInstance = this.mapComponentInstance as any;
    const asBuiltLayer = mapInstance?.mapMultiGeoGraphicsLayer;
    if (!asBuiltLayer?.graphics) return;

    asBuiltLayer.graphics.forEach((g: any) => {
      if (g.attributes?.isSiteGeometry) return;
      // Restore original visibility
      if (g.attributes?._originalVisible !== undefined) {
        g.visible = g.attributes._originalVisible;
        delete g.attributes._originalVisible;
      } else {
        g.visible = true;
      }
    });
  }

  /**
   * Apply persistent highlight for a selected GPS feature.
   * When a feature is selected: full opacity for selected, dimmed for other features in same bid item.
   * When null: restore all same-bid-item features to full opacity (respecting lightweight fade).
   */
  private applySelectedGpsFeatureHighlight(lineItemId: string | null, featureIndex: number | null): void {
    if (!this.mapComponentInstance || !this.selectedItemData?._lightweight) return;

    const mapInstance = this.mapComponentInstance as any;
    const asBuiltLayer = mapInstance?.mapMultiGeoGraphicsLayer;
    if (!asBuiltLayer?.graphics) return;

    const activeBidItemId = this.selectedItemData?.bid_item?.id || this.selectedItemData?.bidItemId;
    if (!activeBidItemId) return;

    asBuiltLayer.graphics.forEach((g: any) => {
      if (g.attributes?.isSiteGeometry) return;

      const graphicBidItemId = g.attributes?.bidItemId || g.attributes?.bid_item_id;
      // Only affect graphics belonging to the active bid item
      if (String(graphicBidItemId) !== String(activeBidItemId)) return;

      if (!g.attributes?._originalSymbol && g.symbol) {
        if (!g.attributes) g.attributes = {};
        g.attributes._originalSymbol = g.symbol.clone();
      }

      if (lineItemId !== null && featureIndex !== null) {
        // A feature is selected - check if this graphic matches
        const graphicLineItemId = g.attributes?.report_bid_item_id;
        const graphicFeatureIdx = g.attributes?.gps_feature_index;
        const isSelected = graphicLineItemId === lineItemId &&
          graphicFeatureIdx !== undefined && graphicFeatureIdx !== null &&
          Number(graphicFeatureIdx) === Number(featureIndex);

        if (isSelected) {
          // Selected feature - full opacity
          if (g.attributes._originalSymbol) {
            g.symbol = g.attributes._originalSymbol.clone();
          }
        } else {
          // Other feature in same bid item - dim
          if (g.attributes._originalSymbol) {
            const newSymbol = g.attributes._originalSymbol.clone();
            this.setSymbolOpacity(newSymbol, 0.35);
            g.symbol = newSymbol;
          }
        }
      } else {
        // No selection - restore all same-bid-item features to full opacity
        if (g.attributes._originalSymbol) {
          g.symbol = g.attributes._originalSymbol.clone();
        }
      }
    });
  }

  /**
   * Set up pointer-move handler for as-built geometry interaction.
   * Hover: highlights the geometry and shows pointer cursor.
   */
  private setupGeometryInteractionHandlers(): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    const asBuiltLayer = (this.mapComponentInstance as any)?.mapMultiGeoGraphicsLayer;
    if (!mapView || !asBuiltLayer) return;

    let hoveredGraphic: any = null;

    mapView.on('pointer-move', (event: any) => {
      // In drilldown mode: hit-test the temp filtered layer and emit hoveredLineItemId
      // so the side panel accordion highlights in sync with the hovered shape.
      if (this.mapService.bidItemDetailViewActive.getValue()) {
        const tempLayer = this.drilldownModeService.getTempFilteredAsBuiltLayer?.();
        if (!tempLayer) return;
        mapView.hitTest(event, { include: [tempLayer] }).then((response: any) => {
          const hit = response.results?.find((r: any) => r.graphic && !r.graphic.attributes?.isSiteGeometry);
          const hoveredId = hit?.graphic?.attributes?.report_bid_item_id || null;
          if (this.mapService.hoveredLineItemId.getValue() !== hoveredId) {
            this.mapService.hoveredLineItemId.next(hoveredId);
            mapView.cursor = hoveredId ? 'pointer' : 'default';
          }
        });
        return;
      }
      if (this.selectedItemData?._lightweight) return;
      if (this._hoveredBidItemId) return;

      mapView.hitTest(event, { include: [asBuiltLayer] }).then((response: any) => {
        const hit = response.results?.find((r: any) => {
          const graphic = r.graphic;
          return graphic && !graphic.attributes?.isSiteGeometry;
        });

        if (hit && hit.graphic) {
          const graphic = hit.graphic;
          if (hoveredGraphic === graphic) return;

          this.resetGeometryHoverHighlight(asBuiltLayer);
          hoveredGraphic = graphic;

          asBuiltLayer.graphics.forEach((g: any) => {
            if (g.attributes?.isSiteGeometry) return;

            if (!g.attributes?._originalSymbol && g.symbol) {
              if (!g.attributes) g.attributes = {};
              g.attributes._originalSymbol = g.symbol.clone();
            }

            if (g === graphic) {
              if (g.attributes._originalSymbol) {
                g.symbol = g.attributes._originalSymbol.clone();
              }
            } else {
              if (g.attributes._originalSymbol) {
                const newSymbol = g.attributes._originalSymbol.clone();
                this.setSymbolOpacity(newSymbol, 0.3);
                g.symbol = newSymbol;
              }
            }
          });

          mapView.cursor = 'pointer';
        } else {
          if (hoveredGraphic) {
            this.resetGeometryHoverHighlight(asBuiltLayer);
            hoveredGraphic = null;
            mapView.cursor = 'default';
          }
        }
      });
    });

    // Pointer leaving the map (e.g. onto the bid item panel) ends a map hover, so the
    // open line's fade comes back instead of the last hovered shape staying highlighted.
    mapView.on('pointer-leave', () => {
      if (this.mapService.bidItemDetailViewActive.getValue() && this.mapService.hoveredLineItemId.getValue()) {
        this.mapService.hoveredLineItemId.next(null);
        mapView.cursor = 'default';
      }
    });

    // Click: emit geometry click for detail view
    mapView.on('click', (event: any) => {
      // Clicks while the polygon tool is active are sketch vertices — don't
      // drill into a geometry that happens to be under one.
      if (this.toolbarState.spatialSelectActive) {
        return;
      }
      // In drilldown mode the as-built layer is hidden and its graphics are
      // cloned onto the temp filtered layer — a hidden layer never hit-tests,
      // so test the temp layer instead (same as the pointer-move handler).
      const inDetailView = this.mapService.bidItemDetailViewActive.getValue();
      const clickLayer = inDetailView
        ? this.drilldownModeService.getTempFilteredAsBuiltLayer?.()
        : asBuiltLayer;
      if (!clickLayer) return;

      // Detail view: a click that lands on nothing (no shape, photo or marker)
      // collapses the open line so the other lines stop being faded.
      if (inDetailView && this.mapService.openLineItemId.getValue()) {
        const clickable = [
          clickLayer,
          this.drilldownModeService.getTempFilteredPictureLayer?.(),
          this.unifiedMarkersLayer,
        ].filter(Boolean);
        mapView.hitTest(event, { include: clickable }).then((response: any) => {
          const hitSomething = response.results?.some((r: any) => r.graphic && !r.graphic.attributes?.isSiteGeometry);
          if (!hitSomething) this.mapService.collapseOpenLine.next();
        });
      }

      mapView.hitTest(event, { include: [clickLayer] }).then((response: any) => {
        const hit = response.results?.find((r: any) => {
          const graphic = r.graphic;
          return graphic && !graphic.attributes?.isSiteGeometry;
        });

        if (hit && hit.graphic) {
          const attrs = hit.graphic.attributes || {};
          console.log('🔵 Geometry click - graphic attributes:', JSON.stringify(attrs, (key, val) => key === '_originalSymbol' ? undefined : val));
          this.geometryClick.emit({
            report_bid_item_id: attrs.report_bid_item_id || null,
            bid_item_id: attrs.bidItemId || attrs.bid_item_id || null,
            gps_feature_index: attrs.gps_feature_index ?? null,
            siteId: attrs.siteId || null,
            reportId: attrs.reportId || null,
          });
        }
      });
    });
  }

  private resetGeometryHoverHighlight(asBuiltLayer: any): void {
    if (!asBuiltLayer?.graphics) return;
    asBuiltLayer.graphics.forEach((g: any) => {
      if (g.attributes?.isSiteGeometry) return;
      if (g.attributes?._originalSymbol) {
        g.symbol = g.attributes._originalSymbol.clone();
      }
    });
  }

  /**
   * Handle click on no-location badge
   * Shows popup with all bid items that don't have location data
   */
  /**
   * Get total count of no-location items (bid items + activities)
   */
  getTotalNoLocationCount(): number {
    return this.bidItemNoLocationCount + this.activityNoLocationCount + this.deliveryNoLocationCount + this.formNoLocationCount;
  }

  onNoLocationBadgeClick(event: MouseEvent): void {

    const totalItems = this.bidItemNoLocationItems.length + this.activityNoLocationItems.length + this.deliveryNoLocationItems.length + this.formNoLocationItems.length;
    if (totalItems === 0) {
      return;
    }

    // Generate popup content for bid items
    const bidItemPopups = this.bidItemNoLocationItems.map(item => {
      // Create a mock event structure for the popup generator
      const mockEvent = {
        graphic: {
          attributes: {
            category: 'bid_items',
            data: {
              bid_item: item.bidItem,
              comment: item.comment,
              site_id: item.siteId,
              siteName: item.siteName,
              report_date: item.reportDate,
              report_id: item.reportId,
              project_id: item.projectId,
              // Add report bid item for quantity calculations
              ...item.reportBidItem,
              // Include allLineItems for drilldown (contains pictures)
              allLineItems: item.allLineItems || []
            }
          }
        }
      };

      // Use existing popup data generator
      return this.mapPopupService.levelOneBidItemPopupData(mockEvent)[0];
    });

    // Generate popup content for activities
    const activityPopups = this.activityNoLocationItems.map(item => {
      // Create a mock event structure for the popup generator
      const mockEvent = {
        graphic: {
          attributes: {
            category: 'activities',
            data: {
              title: item.activity.title || item.activity.name,
              comment: item.comment,
              pictures: item.pictures || [],
              annotations: [], // Activities don't have annotations
              siteName: item.siteName,
              reportDate: item.reportDate,
              ...item.activity
            }
          }
        }
      };

      // Use existing popup data generator
      return this.mapPopupService.levelOneActivitiesPopupData(mockEvent)[0];
    });

    // Generate popup content for deliveries
    const deliveryPopups = this.deliveryNoLocationItems.map(item => {

      // Determine heading - always use "Delivery for [item]"
      let heading: string;
      let subheading1: string;
      let subheading2: string | undefined;

      if (item.bid_item?.item) {
        heading = `Delivery for ${item.bid_item.item}`;
        // Show material name or description in subheading1
        if (item.material?.name) {
          subheading1 = item.material.name;
        } else if (item.bid_item.description) {
          subheading1 = item.bid_item.description;
        } else {
          subheading1 = undefined;
        }
      } else {
        // Fallback if no bid_item
        heading = 'Material Delivery';
        subheading1 = undefined;
      }

      // Calculate quantity slider values
      const deliveryQty = item.quantity || 0;
      const bidItemQty = item.bid_item?.quantity || 0;
      const quantityPercent = bidItemQty > 0 ? (deliveryQty / bidItemQty) * 100 : 0;

      return {
        type: 'deliveries',
        title: 'DELIVERY',
        heading: heading,
        subheading1: subheading1,
        summary: {
          left: {
            label: 'RECEIVED QTY',
            value: quantityPercent,  // Use percentage for progress bar width
            statistics: bidItemQty > 0 ? `${deliveryQty} / ${bidItemQty}` : `${deliveryQty}`,
          },
          right: null  // No right side - will show comment below
        },
        description: item.comment || '',  // Comment section
        pictures_info: `${item.pictures?.length || 0} picture${item.pictures?.length === 1 ? '' : 's'}`,  // Pictures count below comment
        more_link_text: 'See more',
        data: {
          markerData: item,
          type: 'deliveries',
          projectId: this.projectData?.id,
          reportId: item.reportId,
          deliveryId: item.id
        }
      };
    });

    // Generate popup content for control forms
    const formPopups = this.formNoLocationItems.map(item =>
      this.mapPopupService.createControlFormPopup(item)
    );

    // Combine bid items, activities, deliveries, and forms
    this.itemPopupContent = [...bidItemPopups, ...activityPopups, ...deliveryPopups, ...formPopups];

    // Position popup near the badge (badge is at top: 20px, left: 436px)
    const badge = event.currentTarget as HTMLElement;
    const rect = badge.getBoundingClientRect();
    this.itemPopupScreenPoint = {
      x: rect.left,
      y: rect.bottom + 8 // 8px below the badge
    };

    this.showItemPopup = true;
  }

  /**
   * Handle click on drilldown no-location pictures badge
   * Opens picture overlay with only no-location pictures from current drilldown item
   */
  onDrilldownNoLocationPicturesClick(event: MouseEvent): void {

    if (this.drilldownNoLocationPictures.length === 0) {
      return;
    }

    // Open picture overlay with no-location pictures
    this.mapService.selectedPicturesData.next(this.drilldownNoLocationPictures);
  }

  /**
   * Calculate main mode no-location pictures
   * Filters pictureMarkers to find pictures without location (null/undefined latitude or longitude).
   * Note: Outlier coordinates are already nulled by sanitizePictureCoordinates() upstream.
   */
  private calculateMainNoLocationPictures(): void {
    const noLocationPictures = this.pictureMarkers.filter(marker => !marker.latitude || !marker.longitude);
    this.mainNoLocationPictures = noLocationPictures;
    this.mainNoLocationPicturesCount = noLocationPictures.length;
  }

  /**
   * Sanitize picture coordinates by nulling out outliers (> 100 miles from reference point).
   * This ensures outlier pictures are treated as no-location by all downstream logic.
   */
  private sanitizePictureCoordinates(pictures: any[]): any[] {
    if (!pictures || pictures.length === 0) {
      return pictures;
    }

    // References: every filtered site, falling back to project location. Shared
    // with the type-marker location calculators in map-layer-container so the
    // outlier threshold has a single source of truth. A picture is an outlier
    // only when it is far from every site, so wide-spread projects still work.
    const references = computeOutlierReferences(this.filteredSites, this.projectData);

    // If no reference points, return pictures as-is
    if (references.length === 0) {
      return pictures;
    }

    return pictures.map(pic => {
      if (!pic.latitude || !pic.longitude) {
        return pic;
      }

      const safe = rejectOutlierLocation(
        { latitude: parseFloat(pic.latitude), longitude: parseFloat(pic.longitude) },
        references,
      );

      // If outlier, null out coordinates so it becomes a no-location picture
      return safe ? pic : { ...pic, latitude: null, longitude: null };
    });
  }

  /**
   * Handle click on main mode no-location pictures badge
   * Opens picture overlay with all no-location pictures from main mode
   */
  onMainNoLocationPicturesClick(event: MouseEvent): void {

    if (this.mainNoLocationPictures.length === 0) {
      return;
    }

    // Open picture overlay with no-location pictures
    this.mapService.selectedPicturesData.next(this.mainNoLocationPictures);
  }

  /**
   * Query cluster to get all picture data
   * @param clusterGraphic The cluster graphic that was clicked
   * @returns Promise resolving to array of picture data objects
   */
  private async queryClusterPictures(clusterGraphic: any): Promise<any[]> {
    if (!this.pictureFeatureLayer || !clusterGraphic.attributes.cluster_id) {
      return [];
    }

    try {
      // Query the feature layer for all features in this cluster
      const query = this.pictureFeatureLayer.createQuery();
      query.aggregateIds = [clusterGraphic.attributes.cluster_id];

      const result = await this.pictureFeatureLayer.queryFeatures(query);

      // Extract picture data from each feature
      const pictures = result.features.map(feature => {
        try {
          return JSON.parse(feature.attributes.pictureData || '{}');
        } catch (error) {
          console.error('Error parsing picture data from cluster:', error);
          return null;
        }
      }).filter(p => p !== null);

      return pictures;
    } catch (error) {
      console.error('Error querying cluster pictures:', error);
      return [];
    }
  }

  /**
   * Get clustering configuration for unified markers layer
   * Used when re-enabling clustering after zooming out from zoom 19+
   */
  private getClusteringConfig(): any {
    return {
      type: 'cluster',
      clusterRadius: '120px',
      clusterMinSize: 32, // Use integer for crisp rendering
      clusterMaxSize: 60,
      clusterTransitionDuration: 200,
      clusterTransitionDistance: 10,
      filter: {
        where: "markerType <> 'multi_item_location'"
      },
      symbol: {
        type: 'simple-marker',
        style: 'circle',
        size: 32, // Integer pixel size for crisp edges
        color: [16, 54, 214, 255], // #1036D6 as RGBA for precise color
        outline: {
          color: [16, 54, 214, 255], // Same as fill to hide anti-aliased edge
          width: 0.5
        }
      },
      clusterProperties: {
        total_count: {
          onStatisticField: 'count',
          statisticType: 'sum'
        },
        picture_count: {
          onStatisticField: 'count',
          onStatisticExpression: "IIF($feature.markerType == 'picture', $feature.count, 0)",
          statisticType: 'sum'
        },
        bid_item_count: {
          onStatisticField: 'count',
          onStatisticExpression: "IIF($feature.markerType == 'bid_item', $feature.count, 0)",
          statisticType: 'sum'
        }
      },
      labelingInfo: [{
        deconflictionStrategy: 'none',
        labelExpressionInfo: {
          expression: 'Text($feature.cluster_count, "#,###")'
        },
        symbol: {
          type: 'text',
          color: [255, 255, 255, 255],
          haloColor: [255, 255, 255, 255], // White halo for sharper text
          haloSize: 0.5, // Subtle halo to sharpen edges
          font: {
            size: 15, // Slightly larger for better rendering
            family: 'Montserrat',
            weight: 'bold'
          },
          xoffset: 0,
          yoffset: 0
        },
        labelPlacement: 'center-center',
        minScale: 0,
        maxScale: 0
      }],
      popupTemplate: {
        title: 'Cluster Details',
        content: [
          {
            type: 'text',
            text: '<b>{total_count}</b> total items<br>' +
              '{picture_count} pictures<br>' +
              '{bid_item_count} bid items'
          }
        ]
      }
    };
  }

  /**
   * Enable clustering and show layer after features are added
   * This helper ensures the layer becomes visible with clustering enabled in all code paths
   * @param clusteringDisabled - Reference to track if clustering is currently disabled
   * @param initialSetupComplete - Reference to track if initial setup is done
   */
  private enableClusteringAndShowLayer(clusteringDisabled: boolean, initialSetupComplete: boolean): void {
    if (!this.unifiedMarkersLayer) return;

    const currentZoom = this.mapComponentInstance?.mapViewInstance?.zoom || 10;

    if (currentZoom < 21) {
      // Enable clustering
      this.unifiedMarkersLayer.featureReduction = this.getClusteringConfig();
      // Note: clusteringDisabled and initialSetupComplete are passed by value, not reference
      // The caller's variables won't be updated, but that's OK - the zoom watcher handles state
    }

    // Show layer now that clustering is enabled (prevents unclustered flash)
    // Also show in single day mode (daily report) even without fullscreen
    if (this.isFullScreen || this.isSingleDayMode) {
      this.unifiedMarkersLayer.visible = true;
    }
  }

  /**
   * Apply filtering to no-location items based on current bid item filter
   * When bid item filter is active:
   * - Activities are hidden (no bid item link)
   * - Deliveries are filtered to only those linked to selected bid items
   * - Bid items are filtered to only selected ones
   */
  private applyNoLocationFilter(): void {
    const selectedBidItemIds = this.currentBidItemFilter;
    const hasActiveFilter = selectedBidItemIds && selectedBidItemIds.length > 0;

    if (!hasActiveFilter) {
      // No filter - show all original items
      this.bidItemNoLocationItems = [...this.originalBidItemNoLocationItems];
      this.bidItemNoLocationCount = this.bidItemNoLocationItems.length;
      this.activityNoLocationItems = [...this.originalActivityNoLocationItems];
      this.activityNoLocationCount = this.activityNoLocationItems.length;
      this.deliveryNoLocationItems = [...this.originalDeliveryNoLocationItems];
      this.deliveryNoLocationCount = this.deliveryNoLocationItems.length;
      this.formNoLocationItems = [...this.originalFormNoLocationItems];
      this.formNoLocationCount = this.formNoLocationItems.length;
    } else {
      // Filter bid items to only selected ones
      this.bidItemNoLocationItems = this.originalBidItemNoLocationItems.filter(item => {
        const bidItemId = item.bidItem?.id || item.bid_item?.id;
        return bidItemId && selectedBidItemIds.includes(bidItemId);
      });
      this.bidItemNoLocationCount = this.bidItemNoLocationItems.length;

      // Hide all activities (they have no bid item link)
      this.activityNoLocationItems = [];
      this.activityNoLocationCount = 0;

      // Filter deliveries to only those linked to selected bid items
      this.deliveryNoLocationItems = this.originalDeliveryNoLocationItems.filter(item => {
        const deliveryBidItemId = item.bid_item?.id || item.bid_item_id;
        return deliveryBidItemId && selectedBidItemIds.includes(deliveryBidItemId);
      });
      this.deliveryNoLocationCount = this.deliveryNoLocationItems.length;

      // Hide all forms (they have no bid item link, mirroring activities)
      this.formNoLocationItems = [];
      this.formNoLocationCount = 0;
    }

    // Update MapStore with filtered items
    this.mapStore.setBidItemNoLocationItems(this.bidItemNoLocationItems);
    this.mapStore.setActivityNoLocationItems(this.activityNoLocationItems);
    this.mapStore.setDeliveryNoLocationItems(this.deliveryNoLocationItems);
    this.mapStore.setNoLocationCounts({
      bidItems: this.bidItemNoLocationCount,
      activities: this.activityNoLocationCount,
      deliveries: this.deliveryNoLocationCount
    });

    // Trigger change detection to ensure no-location badges update
    this.cdr.detectChanges();
  }

  /**
   * Apply bid item filter to markers, as-built geometry, and pictures
   * @param selectedBidItemIds Array of selected bid item IDs (empty means "all selected")
   */
  private async applyBidItemFilter(selectedBidItemIds: string[]): Promise<void> {

    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) {
      return;
    }

    const showAll = !selectedBidItemIds || selectedBidItemIds.length === 0;

    // Store current filter for persistence across redraws
    this.currentBidItemFilter = showAll ? undefined : selectedBidItemIds;

    // Redraw markers with the filter applied - this will recalculate grouping
    // Pass undefined if showing all, or the filtered IDs if filtering
    await this.drawUnifiedMarkers(showAll ? undefined : selectedBidItemIds);

    // 2. Filter as-built geometry layer
    if (this.mapComponentInstance?.mapMultiGeoGraphicsLayer) {
      const layer = this.mapComponentInstance.mapMultiGeoGraphicsLayer;

      if (showAll) {
        // Show all as-built geometries
        layer.graphics.forEach(graphic => {
          graphic.visible = true;
        });
      } else {
        // Only show as-built for selected bid items
        let visibleCount = 0;
        let hiddenCount = 0;
        let noBidItemIdCount = 0;
        const visibleBidItems = new Set();
        const hiddenBidItems = new Set();

        layer.graphics.forEach(graphic => {
          const bidItemId = graphic.attributes?.bid_item_id;

          if (bidItemId) {
            const isVisible = selectedBidItemIds.includes(bidItemId);
            graphic.visible = isVisible;
            if (isVisible) {
              visibleCount++;
              visibleBidItems.add(bidItemId);
            } else {
              hiddenCount++;
              hiddenBidItems.add(bidItemId);
            }
          } else {
            // Hide geometries without bid_item_id when filtering (these are not associated with bid items)
            graphic.visible = false;
            noBidItemIdCount++;
          }
        });
      }
    }

    // 3. Pictures are already filtered in the unified layer above (included in the filter expression)
    // No separate picture layer to filter

    // 4. Filter no-location items (badge)
    this.applyNoLocationFilter();
  }

  ngOnDestroy() {
    this.openingFitWatches.forEach(handle => handle.remove());
    this.openingFitWatches = [];
    this.stationingSub?.unsubscribe();
    this.stationingAlignmentSub?.unsubscribe();
    this.stationing?.destroy();
    this.stationing = null;
    this.closeReferenceInspection();
    this.inspectionHandles.forEach(handle => handle.remove());
    this.inspectionHandles = [];
    this.inspectionView = null;
    // Deactivate swipe first — MapSwipeService is providedIn: 'root' and holds
    // references to the widget and external FeatureLayers; without this, those
    // references (and their associated DOM/event handlers) leak across routes.
    const mv = this.mapComponentInstance?.mapViewInstance;
    this.overlayLoadGeneration++;
    this.mapReferenceLayerService.deactivate(mv);
    if (mv && this.mapSwipeService.isActive()) {
      this.mapSwipeService.deactivate(mv);
    }

    // Same story for the spatial-select Sketch widget — root-scoped service,
    // so an active widget would survive this component's teardown and leak
    // its sketch GraphicsLayer + handlers on the next route.
    if (mv && this.mapSpatialSelectService.isActive()) {
      this.mapSpatialSelectService.deactivate(mv);
    }
    // The area-summary panel state is also root-scoped; without this reset
    // the sidenav keeps showing the old summary when the user navigates
    // away from the map and back.
    this.projectSpatialSummaryService.setActive(false, '');
    this.lastSpatialPolygon = null;

    // Heatmap contributions layer is also held on a root-scoped service;
    // drop its FeatureLayer before we tear down the MapView.
    if (mv && this.mapHeatmapContributionsService.isActive()) {
      this.mapHeatmapContributionsService.deactivate(mv);
    }

    // Clean up Esri watcher handles (prevents memory leaks)
    if (this.zoomWatchHandle) {
      this.zoomWatchHandle.remove();
      this.zoomWatchHandle = null;
    }
    if (this.interactingWatchHandle) {
      this.interactingWatchHandle.remove();
      this.interactingWatchHandle = null;
    }
    if (this.clusteringZoomWatchHandle) {
      this.clusteringZoomWatchHandle.remove();
      this.clusteringZoomWatchHandle = null;
    }

    // Clean up unified markers layer and ALL handlers (including early hover handler)
    this.unifiedMarkerService.cleanupAllHandlers();
    this.unifiedMarkerService.setHoverContext(null);
    if (this.unifiedMarkersLayer && this.mapComponentInstance?.mapViewInstance) {
      this.mapComponentInstance.mapViewInstance.map.remove(this.unifiedMarkersLayer);
      this.unifiedMarkersLayer = null;
      this.previousUnifiedMarkerUuids.clear(); // Clear UUID cache when layer is destroyed
    }

    // Clean up as-built label layer
    if (this.mapComponentInstance?.mapViewInstance) {
      this.unifiedMarkerService.removeAsBuiltLabelLayer(this.mapComponentInstance.mapViewInstance);
    }

    // Clean up cluster zoom watcher shared by the claim + issue layers
    this.teardownMarkerClusterZoomWatcher();

    // Clean up warranty claim layer + handlers
    if (this.warrantyClaimClickHandler) {
      this.warrantyClaimClickHandler.remove();
      this.warrantyClaimClickHandler = null;
    }
    if (this.warrantyClaimHoverHandler) {
      this.warrantyClaimHoverHandler.remove();
      this.warrantyClaimHoverHandler = null;
    }
    if (this.claimPopupHideTimeout) {
      clearTimeout(this.claimPopupHideTimeout);
      this.claimPopupHideTimeout = null;
    }

    // Clean up warranty issue layer + handlers
    if (this.warrantyIssueClickHandler) {
      this.warrantyIssueClickHandler.remove();
      this.warrantyIssueClickHandler = null;
    }
    if (this.warrantyIssueHoverHandler) {
      this.warrantyIssueHoverHandler.remove();
      this.warrantyIssueHoverHandler = null;
    }
    if (this.issuePopupHideTimeout) {
      clearTimeout(this.issuePopupHideTimeout);
      this.issuePopupHideTimeout = null;
    }

    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  protected _onShowTooltip($event) {
    if ($event) {
      this.showTooltip = true;
      const category = $event?.graphic?.attributes?.category;

      const data = {
        ...$event?.graphic?.attributes?.data,
        markerCategory: category,
      };

      this.screenPoint = $event?.screenPoint;

      const site = $event?.graphic?.attributes?.data;
      if (!site) {
        this.showTooltip = false;
        return;
      }

      // Only show time component for filtered sites (blue markers)
      if (site.isFiltered) {
        this.tooltipContent = `<span class="text-type-15 tooltip-bold" style="color: #060b1e;">${site.site_name}</span> <span class="text-type-15 tooltip-light" style="font-weight: 400;">(${this.dateFormatPipe.transform(site.last_updated_at, 'ago')} ago)</span>`;
      } else if ($event?.graphic?.attributes?.isSiteGeometry && this.formatDateRangeLabel()) {
        // Muted site boundary on the header map: say why it's grey
        this.tooltipContent = `<span class="text-type-15 tooltip-bold" style="color: #060b1e;">${site.site_name}</span> <span class="text-type-15 tooltip-light" style="font-weight: 400;">· No reports ${this.formatDateRangeLabel()}</span>`;
      } else {
        // For non-filtered sites (grey markers), only show the site name
        this.tooltipContent = `<span class="text-type-15 tooltip-bold" style="color: #060b1e;">${site.site_name}</span>`;
      }
    } else {
      this.showTooltip = false;
    }
  }

  /**
   * Perform zoom in operation on the map
   */
  private performZoomIn(): void {
    if (!this.mapComponentInstance?.mapViewInstance) {
      console.warn('⚠️ Cannot zoom in: map view not ready');
      return;
    }

    const currentZoom = this.mapComponentInstance.mapViewInstance.zoom;
    const newZoom = Math.min(currentZoom + 1, 20); // Max zoom level 20

    this.mapComponentInstance.mapViewInstance.goTo({
      zoom: newZoom
    }, {
      duration: 300 // Smooth animation
    });

  }

  /**
   * Perform zoom out operation on the map
   */
  private performZoomOut(): void {
    if (!this.mapComponentInstance?.mapViewInstance) {
      console.warn('⚠️ Cannot zoom out: map view not ready');
      return;
    }

    const currentZoom = this.mapComponentInstance.mapViewInstance.zoom;
    const newZoom = Math.max(currentZoom - 1, 1); // Min zoom level 1

    this.mapComponentInstance.mapViewInstance.goTo({
      zoom: newZoom
    }, {
      duration: 300 // Smooth animation
    });

  }


  /**
   * Enter drilldown mode for an item (bid item, activity, or delivery)
   * - Store current map extent/zoom
   * - Hide all markers except selected item
   * - Force show as-builts and pictures for selected item
   * - Zoom to extent of item elements
   */
  private async enterDrilldownMode(itemData: any): Promise<void> {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }

    // Token to abort a stale drilldown: rapidly tapping a second marker (or
    // exiting) while we're awaiting below must not let the first item's
    // range draw/zoom land over the current one.
    const token = ++this.drilldownToken;

    // Sync marker pictures map with service
    this.drilldownModeService.syncMarkerPicturesMap(this.markerPicturesMap);

    const context: DrilldownContext = {
      mapView: this.mapComponentInstance.mapViewInstance,
      unifiedMarkersLayer: this.unifiedMarkersLayer,
      asBuiltLayer: this.mapComponentInstance.mapMultiGeoGraphicsLayer,
      projectBoundaryLayer: this.mapComponentInstance.mapProjectGraphicsLayer
    };

    const config: DrilldownConfig = {
      isFullScreen: this.isFullScreen,
      layerSettings: this.layerSettings
    };

    await this.drilldownModeService.enterDrilldownMode(itemData, context, config);

    // enterDrilldownMode applies the unified layer's featureEffect filter. Let that
    // filter finish evaluating BEFORE we animate the station-range zoom — otherwise
    // the tile requests fired during the zoom animation race the filter's first
    // pass and log transient [esri … executeQuery] tile errors.
    await this.waitForUnifiedLayerSettled();

    // A newer drilldown (or an exit) superseded us while awaiting — abort so we
    // don't draw/zoom this stale item's range over the current selection.
    if (token !== this.drilldownToken) {
      return;
    }

    // Draw the drilled item's station range into the dedicated layer (if any),
    // then zoom to fit the full range. Station ranges are standalone — they no
    // longer ride the as-built layer / drilldown filter machinery.
    this.drawStationRangeForItem(itemData);

    // The temp layers are new — re-apply the open line's fade on them
    this.applyLineItemHoverEffect(this.mapService.hoveredLineItemId.getValue());

    // Hide labels during drilldown
    this.unifiedMarkerService.applyLabels({ enabled: false, labelBy: this._currentLabelConfig.labelBy });
    this.unifiedMarkerService.applyAsBuiltLabels({ enabled: false, labelBy: this._currentLabelConfig.labelBy });

    // Update local state from service
    const state = this.drilldownModeService.getState();
    this.drilldownNoLocationPictures = state.noLocationPictures;
    this.drilldownNoLocationCount = state.noLocationCount;
    this.mapStore.setDrilldownNoLocationPictures(this.drilldownNoLocationPictures);
  }

  /**
   * Resolve once the unified markers layerView has finished its current update
   * (e.g. after the drilldown featureEffect filter is applied), so a following
   * zoom animation doesn't race the filter's first tile evaluation. Bounded by an
   * 800ms timeout so it can never hang the drilldown.
   */
  private async waitForUnifiedLayerSettled(): Promise<void> {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView || !this.unifiedMarkersLayer) {
      return;
    }
    try {
      const layerView: any = await mapView.whenLayerView(this.unifiedMarkersLayer);
      await Promise.race([
        reactiveUtils.whenOnce(() => layerView && !layerView.updating),
        new Promise(resolve => setTimeout(resolve, 800)),
      ]);
    } catch {
      // LayerView not ready / destroyed — proceed with the zoom regardless.
    }
  }

  /**
   * Ensure the dedicated, always-visible station-range layer exists on the map.
   */
  private ensureStationRangeLayer(mapView: __esri.MapView): void {
    if (!mapView?.map) {
      return;
    }
    if (this.stationRangeLayer && mapView.map.findLayerById(this.stationRangeLayer.id)) {
      return;
    }
    this.stationRangeLayer = new GraphicsLayer({
      id: 'station-range-layer',
      listMode: 'hide' as any,
      visible: true,
    });
    mapView.map.add(this.stationRangeLayer);
  }

  /** Spec §3a: a record's popup opens at the click, with the clicked point's station line when Stationing is on */
  private setItemPopupPoint(screenPoint: { x: number; y: number }): void {
    this.itemPopupScreenPoint = screenPoint;
    const view = this.mapComponentInstance?.mapViewInstance;
    this.itemPopupMapPoint = view && screenPoint ? view.toMap(screenPoint) : null;
    // Automatic takes the nearest to the clicked point, and the pointer leaves it alone while the popup is open
    this.stationing?.focusPoint(this.itemPopupMapPoint);
    this.itemPopupStationLine = this.stationing?.lineAt(this.itemPopupMapPoint) ?? null;
  }

  /**
   * An opening fit's goTo, kept with the size the view measured when it was made (only before the user moves the
   * map). If it is interrupted or rejected while still the latest fit, it is retried once (hardening). The target is
   * computed only once the view has a size (a 0-wide view's side-panel shift is NaN): before that the fit waits for
   * the layout, and a target that is still not usable is neither recorded nor gone to.
   */
  private openingGoTo(mapView: __esri.MapView, target: () => Extent | null, options: object): Promise<unknown> {
    const go = (): Promise<unknown> => {
      const extent = target();
      if (!isUsableFitTarget(extent)) return Promise.reject(new Error('The opening fit has no usable target'));
      // The same fit asked for again while it is still animating joins it: each new goTo restarted the 2 s zoom from
      // wherever the camera was (prod trace: ~9 goTos to one extent per open), so the map zoomed in, stopped and restarted
      const inFlight = this.openingGoToInFlight;
      if (inFlight && mapView.animation && inFlight.extent.equals(extent)) return inFlight.done;
      if (!this.userMovedMapSinceOpen && !this.openingFitRedone) {
        this.openingFit = { extent: extent.clone(), width: mapView.width, height: mapView.height };
      }
      const fit = this.openingFit;
      const flight = { extent: extent.clone(), done: null as Promise<unknown> };
      flight.done = mapView.goTo(extent, options as __esri.GoToOptions2D).then(result => {
        // A goTo superseded by another also resolves: landed only when no animation took over
        setTimeout(() => { if (!mapView.animation) this.openingFitLanded = true; });
        return result;
      }, error => {
        // After the interaction that interrupted it has marked the map as the user's
        setTimeout(() => { if (fit && this.openingFit === fit) this.redoOpeningFit(mapView, { kind: 'goto-failed' }); });
        throw error;
      }).finally(() => {
        if (this.openingGoToInFlight === flight) this.openingGoToInFlight = null;
      });
      this.openingGoToInFlight = flight;
      return flight.done;
    };
    if (hasViewSize(mapView)) return go();
    // Not laid out yet (hard reload, background tab): fit once it is, unless the user has taken the map by then.
    // Resolved now, so callers waiting on the opening fit (marker drawing) are not held for a hidden view.
    void reactiveUtils.whenOnce(() => mapView.destroyed || hasViewSize(mapView)).then(() => {
      if (!mapView.destroyed && !this.userMovedMapSinceOpen) go().catch(() => { /* interrupted by the user */ });
    });
    return Promise.resolve();
  }

  private redoOpeningFit(mapView: __esri.MapView, trigger: OpeningFitTrigger): void {
    const fit = this.openingFit;
    if (!fit || mapView.destroyed) return;
    const action = openingFitAction(fit, trigger, this.userMovedMapSinceOpen, this.openingFitRedone, !!mapView.animation);
    if (action === 'after-animation') {
      // Never interrupt an opening goTo in flight: look again, at the size then, once it has settled
      if (this.openingFitWaiting) return;
      this.openingFitWaiting = true;
      void reactiveUtils.whenOnce(() => mapView.destroyed || !mapView.animation).then(() => {
        this.openingFitWaiting = false;
        if (!mapView.destroyed) {
          this.redoOpeningFit(mapView, trigger.kind === 'resize' ? { kind: 'resize', width: mapView.width, height: mapView.height } : trigger);
        }
      });
      return;
    }
    if (action !== 'redo') return;
    this.openingFitRedone = true;
    mapView.goTo(fit.extent, { animate: false })
      .then(() => { this.openingFitLanded = true; })
      .catch(() => { /* interrupted by the user */ });
  }

  /** 7.2: the Stationing layer follows the project's alignments and the user's Stationing toggle */
  private syncStationing(): void {
    const layer = this.stationing;
    const projectId = this.projectData?.id;
    if (!layer || !projectId) return;
    layer.setAlignments(projectAlignments(this.getAlignmentGeometry()));
    if (this.stationingProjectId !== projectId) {
      this.stationingProjectId = projectId;
      this.stationingSub?.unsubscribe();
      this.stationingSub = this.stationingPreference.visible$(projectId).subscribe(visible => {
        this.stationing?.setVisible(visible);
        this.cdr.detectChanges();
      });
      this.stationingAlignmentSub?.unsubscribe();
      this.stationingAlignmentSub = this.stationingPreference.alignment$(projectId).subscribe(name => this.stationing?.setLocked(name));
    }
  }

  /** §3c: the chip shows with two or more alignments while Stationing is on */
  get stationingChipShown(): boolean {
    return showAlignmentChip(this.stationingAlignmentNames.length, !!this.stationing?.visible);
  }

  /** §3c: lock Stations to an alignment, or (null) back to Automatic; remembered per account and project */
  chooseStationingAlignment(name: string | null): void {
    const projectId = this.projectData?.id;
    if (projectId) this.stationingPreference.setAlignment(projectId, name);
  }

  /**
   * Project alignment geometry used to project station ranges onto the map.
   */
  private getAlignmentGeometry(): any {
    return this.projectData?.alignment_geometry || this.projectData?.alignmentGeometry || null;
  }

  /**
   * Build + draw the given item's station range into the dedicated layer, then
   * zoom to its full extent. Clears any prior range first. No-op if the item has
   * no station range. Mirrors single-day + multi-day (any item type that carries
   * a station_range: bid item / activity / delivery / control form).
   */
  private drawStationRangeForItem(itemData: any): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) {
      return;
    }
    this.ensureStationRangeLayer(mapView);
    this.clearStationRangeLayer();

    const stationRange = itemData?.station_range || itemData?.stationRange;
    if (!stationRange) {
      return;
    }

    const geometries = this.stationRangeMapGeometry.buildStationRangeGeometries(
      stationRange,
      this.getAlignmentGeometry(),
      {},
    );
    if (!geometries?.length) {
      return;
    }

    this.mapService.drawStationRangeGeometries(this.stationRangeLayer, geometries);

    // Keep the range below the unified markers so the pin sits on top of it.
    if (this.unifiedMarkersLayer && mapView.map) {
      const unifiedIndex = mapView.map.layers.indexOf(this.unifiedMarkersLayer);
      if (unifiedIndex > -1) {
        mapView.map.reorder(this.stationRangeLayer, Math.max(0, unifiedIndex));
      }
    }

    this.zoomToStationRangeExtent();
  }

  /**
   * Zoom to fit the full station range (both end caps + the dashed range) with a
   * comfortable margin for context. The station-range layer owns this zoom.
   */
  private zoomToStationRangeExtent(): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView || !this.stationRangeLayer) {
      return;
    }
    const graphics = this.stationRangeLayer.graphics.toArray();
    if (!graphics.length) {
      return;
    }

    let combined: __esri.Extent | null = null;
    graphics.forEach((g: any) => {
      const geom = g.geometry;
      if (!geom) return;

      // The faded full-project alignment line is drawn for CONTEXT only — including
      // its ~miles-long extent here would zoom the map out to fit the whole
      // alignment instead of the station range. Zoom to the range parts only.
      if (g.attributes?.station_range_part === 'alignment') {
        return;
      }

      // Points have no .extent — use their coordinate directly. Polylines/polygons
      // expose .extent. Skip anything degenerate (an empty/collapsed geometry whose
      // extent is the null-island origin 0,0,0,0): one bad geometry would otherwise
      // pull the whole union to (0,0) and zoom the map to the middle of the ocean.
      let ext: __esri.Extent | null = geom.extent || null;
      if (!ext && geom.type === 'point' && isFinite(geom.x) && isFinite(geom.y)) {
        ext = new Extent({
          xmin: geom.x, ymin: geom.y, xmax: geom.x, ymax: geom.y,
          spatialReference: geom.spatialReference,
        });
      }
      if (!this.isUsableExtent(ext)) {
        return;
      }
      combined = combined ? combined.union(ext) : ext.clone();
    });
    if (!this.isUsableExtent(combined)) {
      return;
    }

    // ~1.45 padding so both book-end caps + range show comfortably.
    const expanded = (combined as any).expand ? (combined as any).expand(1.45) : combined;

    // Shift left in fullscreen to account for the side panel.
    let target = expanded;
    if (this.isFullScreen) {
      const sidePanelWidth = 400;
      const viewWidth = mapView.width;
      const extentWidth = expanded.width || (expanded.xmax - expanded.xmin);
      const shiftAmount = (sidePanelWidth / viewWidth) * extentWidth * 0.5;
      const shifted = expanded.clone();
      shifted.xmin -= shiftAmount;
      shifted.xmax -= shiftAmount;
      target = shifted;
    }

    const leftPadding = this.isFullScreen ? 452 : 0;
    mapView.goTo({ target }, {
      duration: 1000,
      easing: 'ease-in-out',
      padding: { top: 120, left: leftPadding, right: 0, bottom: 0 },
    } as any);
  }

  /**
   * An extent is usable for zoom only if all four bounds are finite numbers and it
   * isn't the null-island degenerate (0,0,0,0) extent produced by an empty/collapsed
   * geometry. Filtering these out keeps one bad station-range geometry from dragging
   * the goTo target into the ocean.
   */
  private isUsableExtent(ext: any): boolean {
    if (!ext) {
      return false;
    }
    const { xmin, ymin, xmax, ymax } = ext;
    if (![xmin, ymin, xmax, ymax].every(v => typeof v === 'number' && isFinite(v))) {
      return false;
    }
    return !(xmin === 0 && ymin === 0 && xmax === 0 && ymax === 0);
  }

  /**
   * Clear the dedicated station-range layer (called on drilldown exit).
   */
  private clearStationRangeLayer(): void {
    this.stationRangeLayer?.removeAll();
  }

  /**
   * Exit drilldown mode
   * - Restore previous map extent/zoom
   * - Show all markers again
   * - Reset layer filters
   */
  private async exitDrilldownMode(): Promise<void> {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }

    // Invalidate any in-flight enterDrilldownMode so it can't draw a range after exit.
    this.drilldownToken++;

    const context: DrilldownContext = {
      mapView: this.mapComponentInstance.mapViewInstance,
      unifiedMarkersLayer: this.unifiedMarkersLayer,
      asBuiltLayer: this.mapComponentInstance.mapMultiGeoGraphicsLayer,
      projectBoundaryLayer: this.mapComponentInstance.mapProjectGraphicsLayer
    };

    // When exiting drilldown in non-fullscreen mode, ensure as-builts are hidden
    // Non-fullscreen mode should only show site boundaries
    const effectiveLayerSettings = {
      ...this.layerSettings,
      asBuilt: this.isFullScreen ? this.layerSettings.asBuilt : false
    };

    const config: DrilldownConfig = {
      isFullScreen: this.isFullScreen,
      isSingleDayMode: this.isSingleDayMode,
      layerSettings: effectiveLayerSettings,
      // Skip restoration in non-fullscreen multi-day mode (container redraws map)
      // In single-day mode, always restore since unified markers layer is always visible
      skipRestoration: !this.isFullScreen && !this.isSingleDayMode
    };

    await this.drilldownModeService.exitDrilldownMode(context, config);

    // Clear the dedicated station-range layer — it's drilldown-only.
    this.clearStationRangeLayer();

    // Restore labels after drilldown
    this.syncLabelSources();

    // When exiting drilldown in non-fullscreen mode, clear as-built graphics
    // This matches the behavior of exiting fullscreen without drilldown
    if (!this.isFullScreen && this.mapComponentInstance.mapMultiGeoGraphicsLayer) {
      this.mapComponentInstance.mapMultiGeoGraphicsLayer.removeAll();
    }

    // Clear local drilldown state
    this.drilldownNoLocationPictures = [];
    this.drilldownNoLocationCount = 0;
    this.mapStore.setDrilldownNoLocationPictures([]);
  }

  /**
   * Apply hover effect to highlight specific line item while fading others
   * In drilldown mode, this fades all elements except those belonging to the hovered line item
   * CRITICAL: The main Bid Item Marker (unified markers layer) must NOT fade
   * @param lineItemId - ID of the hovered line item, null to reset
   */
  private applyLineItemHoverEffect(lineItemId: string | null): void {
    if (!this.mapComponentInstance?.mapViewInstance || !this.selectedItemData) {
      return;
    }

    const fullOpacity = 1.0;

    // A hovered line wins; otherwise the line whose accordion is open in the panel
    // stays at full strength and the bid item's other lines fade.
    const openLineItemId = this.mapService.openLineItemId.getValue();
    const isHover = !!lineItemId;
    const emphasizedLineItemId = lineItemId || openLineItemId || null;
    const fadedOpacity = isHover ? 0.25 : 0.3;

    // 1. Apply to as-built geometries (temp filtered layer from drilldown mode)
    this.applyAsBuiltHoverEffect(emphasizedLineItemId, fadedOpacity, fullOpacity, isHover);

    // 2. Apply to pictures (temp filtered picture layer from drilldown mode)
    this.applyPictureHoverEffect(emphasizedLineItemId);

    // 3. CRITICAL: Keep the main Bid Item Marker at full opacity (unified markers layer)
    // The unified markers layer in drilldown mode shows only the selected bid item marker
    // It should NEVER fade when hovering line items
  }

  /**
   * Apply hover fade effect to as-built geometries
   * Uses symbol opacity modification for individual graphic control
   */
  private applyAsBuiltHoverEffect(lineItemId: string | null, fadedOpacity: number, fullOpacity: number, highlight = true): void {
    // Get the temp filtered as-built layer from drilldown service
    const tempAsBuiltLayer = this.drilldownModeService.getTempFilteredAsBuiltLayer?.();

    // Also check the original layer for any remaining graphics
    const originalAsBuiltLayer = this.mapComponentInstance?.mapMultiGeoGraphicsLayer;

    // Process temp layer (created during drilldown mode)
    if (tempAsBuiltLayer?.graphics) {
      tempAsBuiltLayer.graphics.forEach((graphic: any) => {
        const graphicLineItemId = graphic.attributes?.report_bid_item_id || graphic.attributes?.id;

        // Store original symbol if not already stored
        if (!graphic.attributes?._originalSymbol && graphic.symbol) {
          if (!graphic.attributes) graphic.attributes = {};
          graphic.attributes._originalSymbol = graphic.symbol.clone();
        }

        if (lineItemId) {
          // Hovering - fade non-matching graphics, strongly emphasize the matching one
          if (graphicLineItemId !== lineItemId) {
            if (graphic.attributes?._originalSymbol) {
              const fadedSymbol = graphic.attributes._originalSymbol.clone();
              this.setSymbolOpacity(fadedSymbol, fadedOpacity);
              graphic.symbol = fadedSymbol;
            }
          } else {
            // Matching - thick bright outline (hover only) + full opacity fill for strong emphasis
            if (graphic.attributes?._originalSymbol) {
              const highlighted = graphic.attributes._originalSymbol.clone();
              if (highlight) {
                this.applyHoverHighlightSymbol(highlighted);
              }
              graphic.symbol = highlighted;
            }
          }
        } else {
          // Not hovering - restore all to full opacity
          if (graphic.attributes?._originalSymbol) {
            graphic.symbol = graphic.attributes._originalSymbol.clone();
          }
        }
      });

      // Draw the emphasized line's shapes above the faded ones
      if (lineItemId) {
        tempAsBuiltLayer.graphics.toArray()
          .filter((graphic: any) => (graphic.attributes?.report_bid_item_id || graphic.attributes?.id) === lineItemId)
          .forEach((graphic: any) => tempAsBuiltLayer.graphics.reorder(graphic, tempAsBuiltLayer.graphics.length - 1));
      }

      // Force layer refresh
      tempAsBuiltLayer.refresh?.();
    }

    // Process original layer (if visible during drilldown)
    if (originalAsBuiltLayer?.graphics && originalAsBuiltLayer.visible) {
      originalAsBuiltLayer.graphics.forEach((graphic: any) => {
        const graphicLineItemId = graphic.attributes?.report_bid_item_id || graphic.attributes?.id;

        if (!graphic.attributes?._originalSymbol && graphic.symbol) {
          if (!graphic.attributes) graphic.attributes = {};
          graphic.attributes._originalSymbol = graphic.symbol.clone();
        }

        if (lineItemId) {
          if (graphicLineItemId !== lineItemId) {
            if (graphic.attributes?._originalSymbol) {
              const fadedSymbol = graphic.attributes._originalSymbol.clone();
              this.setSymbolOpacity(fadedSymbol, fadedOpacity);
              graphic.symbol = fadedSymbol;
            }
          } else {
            if (graphic.attributes?._originalSymbol) {
              graphic.symbol = graphic.attributes._originalSymbol.clone();
            }
          }
        } else {
          if (graphic.attributes?._originalSymbol) {
            graphic.symbol = graphic.attributes._originalSymbol.clone();
          }
        }
      });

      originalAsBuiltLayer.refresh?.();
    }
  }

  /**
   * Apply GPS feature-level hover effect on the main geometry layer.
   * Uses same pattern as applyBidItemHoverFadeEffect - operates on all graphics layers.
   * When hovering a specific GPS feature in the sidenav, highlight just that feature
   * and fade all other non-site geometries to 10% opacity.
   */
  private applyGpsFeatureHoverEffect(lineItemId: string | null, gpsFeatureIndex: number | null): void {
    if (!this.mapComponentInstance) return;

    const mapInstance = this.mapComponentInstance as any;

    // Include temp filtered layer from drilldown mode (visible in bid item detail view)
    const tempAsBuiltLayer = this.drilldownModeService.getTempFilteredAsBuiltLayer?.();

    const graphicsLayers = [
      tempAsBuiltLayer,
      mapInstance.mapProjectGraphicsLayer,
      mapInstance.mapMultiGeoGraphicsLayer,
      mapInstance.mapPolygonGraphicsLayer,
    ].filter(layer => layer);

    graphicsLayers.forEach(layer => {
      if (!layer?.graphics) return;

      layer.graphics.forEach((graphic: any) => {
        const isSiteGeometry = graphic.attributes?.isSiteGeometry;

        // Skip site boundary graphics
        if (isSiteGeometry) return;

        // Store original symbol if not already stored
        if (!graphic.attributes?._originalSymbol && graphic.symbol) {
          if (!graphic.attributes) graphic.attributes = {};
          graphic.attributes._originalSymbol = graphic.symbol.clone();
        }

        if (lineItemId && gpsFeatureIndex !== null && gpsFeatureIndex !== undefined) {
          const graphicLineItemId = graphic.attributes?.report_bid_item_id;
          const graphicFeatureIdx = graphic.attributes?.gps_feature_index;

          const isMatch = graphicLineItemId === lineItemId &&
                          graphicFeatureIdx !== null && graphicFeatureIdx !== undefined &&
                          graphicFeatureIdx === gpsFeatureIndex;

          if (isMatch) {
            if (graphic.attributes._originalSymbol) {
              graphic.symbol = graphic.attributes._originalSymbol.clone();
            }
          } else {
            if (graphic.attributes._originalSymbol) {
              const newSymbol = graphic.attributes._originalSymbol.clone();
              this.setSymbolOpacity(newSymbol, 0.1);
              graphic.symbol = newSymbol;
            }
          }
        } else {
          // Not hovering a GPS feature - restore all to original opacity
          if (graphic.attributes._originalSymbol) {
            graphic.symbol = graphic.attributes._originalSymbol.clone();
          }
        }
      });
    });

    // Force layer refresh
    if (this.mapComponentInstance.mapViewInstance) {
      graphicsLayers.forEach(layer => {
        if (layer) {
          layer.refresh && layer.refresh();
        }
      });
    }
  }

  /**
   * Fade the drilldown photos that don't belong to the hovered/open line (null restores all)
   */
  private applyPictureHoverEffect(lineItemId: string | null): void {
    // Get the temp filtered picture layer from drilldown service
    const tempPictureLayer = this.drilldownModeService.getTempFilteredPictureLayer?.();

    if (!tempPictureLayer?.loaded) {
      return;
    }

    // featureEffect is ignored on a clustered layer, so drive the layer's opacity
    // visual variables through the `fade` attribute instead (clusters fade too).
    const currentSequence = ++this.pictureFadeSequence;
    const mapView = this.mapComponentInstance?.mapViewInstance;
    // Editing the clustered layer while it is still drawing can leave it stuck half-drawn
    // (blank photos until the next zoom), so wait for the layer view to settle first.
    const settled = mapView
      ? mapView.whenLayerView(tempPictureLayer)
        .then((layerView: any) => reactiveUtils.whenOnce(() => !layerView.updating))
        .catch(() => undefined)
      : Promise.resolve();
    settled.then(() => {
      if (currentSequence !== this.pictureFadeSequence) return null;
      return tempPictureLayer.queryFeatures({
        where: '1=1',
        returnGeometry: false,
        outFields: ['*'],
      });
    }).then(result => {
      if (!result) return;
      if (currentSequence !== this.pictureFadeSequence) return;

      // line_ids is ",id1,id2," — a same-location group stays clear if any photo is on the line
      const updates = result.features
        .map(feature => {
          const fade = lineItemId && !(feature.attributes.line_ids || '').includes(`,${lineItemId},`) ? 1 : 0;
          if (feature.attributes.fade === fade) return null;
          const graphic = feature.clone();
          graphic.attributes.fade = fade;
          return graphic;
        })
        .filter(Boolean);

      if (!updates.length || this.drilldownModeService.getTempFilteredPictureLayer?.() !== tempPictureLayer) return;
      return tempPictureLayer.applyEdits({ updateFeatures: updates });
    }).catch(error => {
      console.error('Failed to update drilldown picture fade:', error);
    });
  }

  /**
   * Draws unified clustering layer combining pictures and bid items
   * This approach eliminates overlapping clusters and provides a single, clearer UX
   * Delegates heavy lifting to UnifiedMarkerService (Phase 4.7 refactor)
   * @param selectedBidItemIds Optional filter - only show/group bid items matching these IDs
   */
  private async drawUnifiedMarkers(selectedBidItemIds?: string[]): Promise<void> {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }

    // Don't recreate layer while in drilldown mode
    const isInDrilldownMode = this.selectedItemData !== null;
    if (isInDrilldownMode) {
      return;
    }

    // CRITICAL: Wait for any active zoom animation to complete before creating the layer
    // This prevents the race condition where clusters are positioned based on intermediate
    // view extent during animation, causing them to appear at wrong locations
    if (this.initialAnimationPromise) {
      await this.initialAnimationPromise;
    }

    // ALSO wait for any active map animation to complete (handles race condition where
    // multiple animations are triggered and one clears the promise while others are still running)
    const mapView = this.mapComponentInstance.mapViewInstance;
    if (mapView.animation) {
      try {
        await mapView.animation.when();
      } catch (e) {
        // Animation was cancelled, which is fine
      }
    }

    // Clean up old separate layers (migration from separate to unified clustering)
    if (this.pictureFeatureLayer) {
      this.mapComponentInstance.mapViewInstance.map.remove(this.pictureFeatureLayer);
      this.pictureFeatureLayer = null;
    }
    if (this.bidItemFeatureLayer) {
      this.mapComponentInstance.mapViewInstance.map.remove(this.bidItemFeatureLayer);
      this.bidItemFeatureLayer = null;
    }

    // Build location mappings
    const sitesToProcess = (this.filteredSites?.length > 0) ? this.filteredSites : (this.sites || []);
    const { siteNameToId, itemIdToSiteId, reportBidItemToBidItemId, deliveryIdToBidItemId } = this.unifiedMarkerService.buildLocationMappings(
      sitesToProcess,
      this.bidItemMarkers || [],
      this.activityMarkers || [],
      this.deliveryMarkers || []
    );

    // Create marker symbols using the service
    // When bid item filter is active:
    // - Activities are hidden (no bid item link)
    // - Deliveries only show if linked to selected bid items
    // - Pictures only show if linked to selected bid items
    // Pass colorConfig to colorize markers based on current view mode
    const colorConfig = this.getColorConfigForViewMode();
    // Spec §7.3 "Only records linked to design features": client-side, by map_feature_link. Pictures
    // never carry a link, so they drop out while the filter is on.
    const keep = <T>(markers: T[]): T[] => this.linkedOnly ? (markers || []).filter(markerIsLinked) : (markers || []);
    const symbolData = await this.unifiedMarkerService.createMarkerSymbols(
      keep(this.pictureMarkers),
      keep(this.bidItemMarkers),
      keep(this.activityMarkers),
      keep(this.deliveryMarkers),
      keep(this.formMarkers),
      itemIdToSiteId,
      reportBidItemToBidItemId,
      selectedBidItemIds,
      deliveryIdToBidItemId,
      colorConfig
    );

    // If no markers, remove layer and return
    if (symbolData.length === 0) {
      if (this.unifiedMarkersLayer) {
        this.mapComponentInstance.mapViewInstance.map.remove(this.unifiedMarkersLayer);
        this.unifiedMarkersLayer = null;
        this.previousUnifiedMarkerUuids.clear();
      }
      return;
    }

    // Sync maps from service
    this.markerPicturesMap.clear();
    symbolData.forEach(s => {
      if (s.markerType === 'picture') {
        const pics = this.unifiedMarkerService.getPicturesForMarker(s.uuid);
        if (pics) this.markerPicturesMap.set(s.uuid, pics);
      }
    });

    this.multiItemLocationsMap.clear();
    symbolData.filter(s => s.markerType === 'multi_item_location').forEach(s => {
      const data = this.unifiedMarkerService.getMultiItemData(s.uuid);
      if (data) this.multiItemLocationsMap.set(s.uuid, data);
    });

    // Update the layer via service
    const config: UnifiedLayerConfig = {
      showPictures: this.layerSettings.picture,
      selectedBidItemIds
    };

    // Now that we wait for animation to complete before creating the layer,
    // clustering can work correctly (positions calculated at final view extent)
    await this.unifiedMarkerService.updateLayer(
      this.mapComponentInstance.mapViewInstance,
      symbolData,
      config,
      false // disableClustering - no longer needed since we wait for animation
    );

    // Keep local reference in sync
    this.unifiedMarkersLayer = this.unifiedMarkerService.getLayer();

    // Apply the marker-type filter to the fresh/updated layer so the initial
    // state reflects the current Pictures / Types toggles — important because
    // the layer is sometimes recreated (e.g., when CIM symbols need to be
    // introduced after enabling Pictures) and a fresh layer has no expression.
    this.applyUnifiedDefinitionExpression();

    // Setup click handlers
    if (this.unifiedMarkersLayer) {
      const clickContext: MarkerClickContext = {
        onPictureClick: (graphic, event) => this.onPictureMarkerClick(graphic, event),
        onSameLocationClick: (graphic) => this.onSameLocationMarkerClick(graphic),
        onBidItemClick: (graphic, event) => this.onBidItemMarkerClick(graphic, event),
        onActivityClick: (graphic, event) => this.onActivityMarkerClick(graphic, event),
        onDeliveryClick: (graphic, event) => this.onDeliveryMarkerClick(graphic, event),
        onFormClick: (graphic, event) => this.onFormMarkerClick(graphic, event),
        onMultiItemClick: (graphic, event) => this.onMultiItemLocationClick(graphic, event),
        onClusterZoom: (graphic) => {
          const currentZoom = this.mapComponentInstance.mapViewInstance.zoom;
          const zoomLevel = currentZoom + 4;
          this.mapComponentInstance.mapViewInstance.goTo({
            target: graphic.geometry,
            zoom: Math.min(zoomLevel, 20)
          });
        }
      };
      this.unifiedMarkerService.setupClickHandlers(this.mapComponentInstance.mapViewInstance, clickContext);

      // Note: Hover context is set up once in onViewLoaded() - no need to set it again here

      // Manage visibility based on fullscreen mode (always show in single day mode)
      if (!this.isFullScreen && !this.isSingleDayMode) {
        this.unifiedMarkersLayer.visible = false;
      } else {
        // Enable clustering if not already enabled and zoom < 21
        const currentZoom = this.mapComponentInstance.mapViewInstance.zoom || 10;
        if (currentZoom < 21 && !this.unifiedMarkersLayer.featureReduction) {
          this.unifiedMarkersLayer.featureReduction = this.unifiedMarkerService.getClusteringConfig();
        }
        this.unifiedMarkersLayer.visible = true;
        this.unifiedMarkersLayer.refresh();

        // Reapply hover effect if currently hovering (layer update resets is_hovered_site to 1)
        if (this._hoveredBidItemId) {
          this.applyBidItemHoverFadeEffect(this._hoveredBidItemId);
        } else if (this._hoveredInspectorId) {
          this.applyInspectorHoverFadeEffect(this._hoveredInspectorId);
        } else if (this._hoveredSiteId) {
          this.applyHoverFadeEffect(this._hoveredSiteId);
        }
      }

      // Auto-zoom to fit markers if not dragging/playing
      // Skip in single day mode - the map is already zoomed to site geometry extent
      // and zooming again for markers causes the cluster to animate away incorrectly
      if (!this.isSliderDragging && !this.isPlaying && !this.isSingleDayMode) {
        const graphics = this.unifiedMarkerService.createGraphics(symbolData);
        const markersForZoom = graphics.map(g => ({
          geometry: {
            longitude: g.attributes.longitude,
            latitude: g.attributes.latitude
          }
        }));
        if (markersForZoom.length > 0) {
          await this.animateToExtent(markersForZoom, true);
        }
      }

      // Apply any render mode captured from URL query params now that the layer
      // exists. Pins is the default, so nothing to do in that case.
      if (this.pendingRenderMode && this.pendingRenderMode !== 'pins') {
        const pendingMode = this.pendingRenderMode;
        this.unifiedMarkerService.setRenderMode(pendingMode);
        // Restore the bid-item contributions heatmap layer alongside the
        // point-based heatmap when coming back from a URL with ?renderMode=heatmap.
        if (pendingMode === 'heatmap') {
          const mvForRestore = this.mapComponentInstance?.mapViewInstance;
          if (mvForRestore) {
            this.mapHeatmapContributionsService.activate(mvForRestore, this.reportGeometries || []);
          }
        }
        this.pendingRenderMode = null;
      }
    }
  }


  /**
   * Handle zoom in button click
   */
  onZoomIn(): void {
    this.userMovedMapSinceOpen = true;
    if (this.mapComponentInstance && this.mapComponentInstance.mapViewInstance) {
      const currentZoom = this.mapComponentInstance.mapViewInstance.zoom;
      this.mapComponentInstance.mapViewInstance.goTo({
        zoom: currentZoom + 1
      }, {
        duration: 300
      });
    }
  }

  /**
   * Handle zoom out button click
   */
  onZoomOut(): void {
    this.userMovedMapSinceOpen = true;
    if (this.mapComponentInstance && this.mapComponentInstance.mapViewInstance) {
      const currentZoom = this.mapComponentInstance.mapViewInstance.zoom;
      this.mapComponentInstance.mapViewInstance.goTo({
        zoom: currentZoom - 1
      }, {
        duration: 300
      });
    }
  }

  /**
   * Handle basemap selection from map controls
   */
  onBasemapChanged(basemap: any): void {
    if (this.mapComponentInstance) {
      // Pass the full basemap object to the selectBasemap method
      this.mapComponentInstance.selectBasemap(basemap);
    }
    this.basemapId = basemap?.id || null;
    const scope = this.basemapScope();
    if (scope && basemap?.id) saveBasemapPreference(scope.accountId, scope.projectId, basemap.id);
  }

  private basemapScope(): { accountId: string; projectId: string } | null {
    const accountId = this.authService.getCurrentUser()?.id;
    const projectId = this.route.snapshot.params['projectId'] || this.route.snapshot.parent?.params['projectId'] || this.projectData?.id;
    return accountId && projectId ? { accountId, projectId } : null;
  }

  /** Once the view and the project are known: apply the basemap this user last chose here (on either map). */
  private restoreBasemapPreference(): void {
    const scope = this.basemapScope();
    if (!scope || !this.mapComponentInstance?.mapViewInstance) return;
    const key = `${scope.accountId}:${scope.projectId}`;
    if (this.restoredBasemapScope === key) return;
    const saved = loadBasemapPreference(scope.accountId, scope.projectId);
    const basemap = resolveBasemap(saved, this.organizationBasemaps || []);
    // An organization basemap that is not loaded yet: try again when base_maps arrive.
    if (saved && !basemap && !this.organizationBasemaps?.length) return;
    this.restoredBasemapScope = key;
    if (!basemap || basemap.id === 'default') { this.basemapId = 'default'; return; }
    this.mapComponentInstance.selectBasemap(basemap);
    this.basemapId = basemap.id;
  }

  /**
   * Handle layer toggle from map controls
   */
  onLayerChange(layers: { siteBoundary: boolean; picture: boolean; asBuilt: boolean }): void {
    this.layerChange.emit(layers);
  }

  // Form-group toggles (form_group:<id>) selected via the floating map controls.
  // Unlike bidItem/activity/delivery they have no layerSettings boolean, so we
  // remember them here to echo back as checked state (see getSelectedTypeFilters).
  private selectedFormGroupTokens: string[] = [];

  /**
   * Handle type filter toggle from map controls
   */
  onTypeFilterChange(typeFilters: string[]): void {
    this.selectedFormGroupTokens = (typeFilters || []).filter(t => t.startsWith('form_group:'));
    this.linkedOnly = (typeFilters || []).includes(LINKED_ONLY_FILTER);
    // Re-emitted unchanged: the containers read only the tokens they know.
    this.typeFilterChange.emit(typeFilters);
  }

  /**
   * The Types-tab `linked_only` token (spec §7.3), bound by the map containers from their type
   * filters (the sidenav emits to them, not to this component). Changing it redraws the markers.
   */
  @Input() set linkedOnly(linkedOnly: boolean) {
    const next = !!linkedOnly;
    if (next === this._linkedOnly) return;
    this._linkedOnly = next;
    void this.drawUnifiedMarkers(this.currentBidItemFilter);
  }
  get linkedOnly(): boolean {
    return this._linkedOnly;
  }
  private _linkedOnly = false;

  /**
   * Get selected type filters for the map-controls checked state. Base types come
   * from layerSettings booleans; form groups have no boolean, so include the
   * remembered form_group:<id> tokens — otherwise their checkboxes snap back to
   * unchecked on the next change-detection pass.
   */
  getSelectedTypeFilters(): string[] {
    const filters: string[] = [];
    if (this.layerSettings.bidItem) filters.push('bidItem');
    if (this.layerSettings.activity) filters.push('activities');
    if (this.layerSettings.delivery) filters.push('delivery');
    return [...filters, ...this.selectedFormGroupTokens, ...(this.linkedOnly ? [LINKED_ONLY_FILTER] : [])];
  }

  /** Keep the claim popup open while the cursor is hovering inside it. */
  onClaimPopupMouseEnter(): void {
    if (this.claimPopupHideTimeout) {
      clearTimeout(this.claimPopupHideTimeout);
      this.claimPopupHideTimeout = null;
    }
  }

  onClaimPopupMouseLeave(): void {
    this.scheduleHideClaimPopup();
  }

  private drawWarrantyClaimMarkers(markers: any[]): void {
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }
    const mapView = this.mapComponentInstance.mapViewInstance;

    if (this.warrantyClaimFeatureLayer) {
      mapView.map.remove(this.warrantyClaimFeatureLayer);
      this.warrantyClaimFeatureLayer = null;
    }
    if (this.warrantyClaimClickHandler) {
      this.warrantyClaimClickHandler.remove();
      this.warrantyClaimClickHandler = null;
    }
    if (this.warrantyClaimHoverHandler) {
      this.warrantyClaimHoverHandler.remove();
      this.warrantyClaimHoverHandler = null;
    }
    this.hideClaimPopup();

    if (this.layerSettings.warrantyClaim) {
      // Record modes keep the site boundaries / markers when the Sites layer is on.
      const showSites = !!this.layerSettings.siteBoundary;
      if (this.unifiedMarkersLayer) this.unifiedMarkersLayer.visible = false;
      if (this.mapComponentInstance.mapProjectGraphicsLayer) this.mapComponentInstance.mapProjectGraphicsLayer.visible = showSites;
      if (this.mapComponentInstance.mapMultiGeoGraphicsLayer) this.mapComponentInstance.mapMultiGeoGraphicsLayer.visible = false;
      if (this.mapComponentInstance.mapSiteMarkersGraphicsLayer) this.mapComponentInstance.mapSiteMarkersGraphicsLayer.visible = showSites;
    } else {
      if (this.unifiedMarkersLayer) this.unifiedMarkersLayer.visible = true;
      if (this.mapComponentInstance.mapProjectGraphicsLayer) this.mapComponentInstance.mapProjectGraphicsLayer.visible = true;
      if (this.mapComponentInstance.mapMultiGeoGraphicsLayer) this.mapComponentInstance.mapMultiGeoGraphicsLayer.visible = true;
      if (this.mapComponentInstance.mapSiteMarkersGraphicsLayer) this.mapComponentInstance.mapSiteMarkersGraphicsLayer.visible = true;
    }

    if (!markers || markers.length === 0) {
      this.syncMarkerClusterZoomWatcher();
      return;
    }

    const layer = new GraphicsLayer({ id: 'warranty-claims-layer' });
    this.populateClaimLayerGraphics(layer, markers);

    mapView.map.add(layer);
    this.warrantyClaimFeatureLayer = layer;
    this.syncMarkerClusterZoomWatcher();

    // Click: in fullscreen open the side detail; in non-fullscreen toggle the popup.
    // Grouped markers (multiple claims at one location) always show the popup so the
    // user can pick which claim to view. Proximity clusters zoom in to spread out.
    this.warrantyClaimClickHandler = mapView.on('click', (event: any) => {
      mapView.hitTest(event, { include: [layer] }).then((response: any) => {
        const hit = response.results?.find((r: any) => r.graphic?.attributes?._markerType === 'warranty_claim');
        if (!hit?.graphic) {
          this.hideClaimPopup();
          return;
        }
        if (hit.graphic.attributes?._isCluster) {
          this.hideClaimPopup();
          this.zoomIntoMarkerCluster(hit.graphic);
          return;
        }
        const grouped = (hit.graphic.attributes?._groupCount || 1) > 1;
        if (this.isFullScreen && !grouped) {
          this.hideClaimPopup();
          const attrs = hit.graphic.attributes;
          if (attrs?._kind === 'punchlist') {
            this.mapService.showPunchlistItemDetail$.next({ item: attrs });
          } else {
            this.mapService.showClaimDetail$.next({ claim: attrs });
          }
        } else {
          this.showClaimPopupAt(hit.graphic, event);
        }
      });
    });

    // Hover: show a small floating popup near the marker, and switch the cursor.
    // Cluster bubbles only switch the cursor; their contents are reached by zooming.
    this.warrantyClaimHoverHandler = mapView.on('pointer-move', (event: any) => {
      mapView.hitTest(event, { include: [layer] }).then((response: any) => {
        const hit = response.results?.find((r: any) => r.graphic?.attributes?._markerType === 'warranty_claim');
        if (hit?.graphic?.attributes?._isCluster) {
          mapView.container.style.cursor = 'pointer';
          if (this.hoveredClaimUuid) this.scheduleHideClaimPopup();
        } else if (hit?.graphic) {
          mapView.container.style.cursor = 'pointer';
          this.showClaimPopupAt(hit.graphic, event, /* fromHover */ true);
        } else if (this.hoveredClaimUuid) {
          mapView.container.style.cursor = '';
          this.scheduleHideClaimPopup();
        }
      });
    });
  }

  /**
   * (Re)build the claim/punchlist-item graphics for the current zoom. Same-location
   * groups keep the pin + count badge; pins that would overlap on screen collapse
   * into a count bubble that zooms in when clicked.
   */
  private populateClaimLayerGraphics(layer: GraphicsLayer, markers: ClusterableMarker[]): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    const radiusPx = this.mapPointClusterService.radiusForZoom(Number(mapView?.zoom));
    const clusters = this.mapPointClusterService.cluster(mapView, markers, radiusPx);
    layer.removeAll();
    clusters.forEach(cluster => {
      const count = cluster.items.length;
      const head = cluster.items[0];
      const grouped = cluster.items.map(g => g.data);
      if (cluster.sameLocation) {
        layer.add(new Graphic({
          geometry: cluster.center,
          symbol: this.buildClaimPinSymbol(count),
          attributes: {
            ...head.data,
            _markerType: 'warranty_claim',
            _groupCount: count,
            _groupedClaims: grouped,
          },
        }));
      } else {
        layer.add(new Graphic({
          geometry: cluster.center,
          symbol: this.mapPointClusterService.buildClusterSymbol(count),
          attributes: {
            _markerType: 'warranty_claim',
            _isCluster: true,
            _groupCount: count,
            _groupedClaims: grouped,
            _clusterBbox: cluster.bbox,
          },
        }));
      }
    });
  }

  /** Mirror of populateClaimLayerGraphics for the warranty/punchlist issue layer. */
  private populateIssueLayerGraphics(layer: GraphicsLayer, markers: ClusterableMarker[]): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    const radiusPx = this.mapPointClusterService.radiusForZoom(Number(mapView?.zoom));
    const clusters = this.mapPointClusterService.cluster(mapView, markers, radiusPx);
    layer.removeAll();
    clusters.forEach(cluster => {
      const count = cluster.items.length;
      const head = cluster.items[0];
      const grouped = cluster.items.map(g => g.data);
      if (cluster.sameLocation) {
        layer.add(new Graphic({
          geometry: cluster.center,
          symbol: this.buildIssuePinSymbol(count),
          attributes: {
            ...head.data,
            _markerType: 'warranty_issue',
            _groupCount: count,
            _groupedIssues: grouped,
          },
        }));
      } else {
        layer.add(new Graphic({
          geometry: cluster.center,
          symbol: this.mapPointClusterService.buildClusterSymbol(count),
          attributes: {
            _markerType: 'warranty_issue',
            _isCluster: true,
            _groupCount: count,
            _groupedIssues: grouped,
            _clusterBbox: cluster.bbox,
          },
        }));
      }
    });
  }

  /** Zoom the view so a cluster's members spread out (capped at the max cluster zoom). */
  private zoomIntoMarkerCluster(graphic: Graphic): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    const bbox = graphic?.attributes?._clusterBbox as MarkerCluster['bbox'] | undefined;
    if (!mapView || !bbox || !graphic?.geometry) return;
    this.userMovedMapSinceOpen = true;
    const cluster: MarkerCluster = {
      center: graphic.geometry as Point,
      items: graphic.attributes?._groupedClaims || graphic.attributes?._groupedIssues || [],
      sameLocation: false,
      bbox,
    };
    const zoom = this.mapPointClusterService.zoomToFit(mapView, cluster);
    mapView.goTo({ center: graphic.geometry as Point, zoom }, { duration: 300 }).catch(() => { /* interrupted by user */ });
  }

  /**
   * Keep exactly one zoom watcher alive while either clustered layer has
   * markers, and none otherwise. Redraws are debounced so a wheel burst or
   * pinch only reclusters once the zoom settles.
   */
  private syncMarkerClusterZoomWatcher(): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    const hasMarkers = (this.warrantyClaimMarkers?.length || 0) > 0 || (this.warrantyIssueMarkers?.length || 0) > 0;
    if (!mapView || !hasMarkers) {
      this.teardownMarkerClusterZoomWatcher();
      return;
    }
    this.markerClusterLastZoom = Number(mapView.zoom);
    if (this.markerClusterZoomWatchHandle) return;
    this.markerClusterZoomWatchHandle = mapView.watch('zoom', (newZoom: number) => {
      if (this.markerClusterRedrawTimeout) clearTimeout(this.markerClusterRedrawTimeout);
      this.markerClusterRedrawTimeout = setTimeout(() => {
        this.markerClusterRedrawTimeout = null;
        if (this.markerClusterLastZoom !== null && Math.abs(newZoom - this.markerClusterLastZoom) < 0.01) return;
        this.markerClusterLastZoom = newZoom;
        this.reclusterMarkerLayers();
      }, 120);
    });
  }

  private teardownMarkerClusterZoomWatcher(): void {
    if (this.markerClusterZoomWatchHandle) {
      this.markerClusterZoomWatchHandle.remove();
      this.markerClusterZoomWatchHandle = null;
    }
    if (this.markerClusterRedrawTimeout) {
      clearTimeout(this.markerClusterRedrawTimeout);
      this.markerClusterRedrawTimeout = null;
    }
    this.markerClusterLastZoom = null;
  }

  /** Rebuild both clustered layers' graphics in place (layers + handlers are kept). */
  private reclusterMarkerLayers(): void {
    if (this.warrantyClaimFeatureLayer && this.warrantyClaimMarkers?.length) {
      this.hideClaimPopup();
      this.populateClaimLayerGraphics(this.warrantyClaimFeatureLayer, this.warrantyClaimMarkers);
    }
    if (this.warrantyIssueFeatureLayer && this.warrantyIssueMarkers?.length) {
      this.hideIssuePopup();
      this.populateIssueLayerGraphics(this.warrantyIssueFeatureLayer, this.warrantyIssueMarkers);
    }
  }

  onToolbarRenderModeChange(mode: RenderMode): void {
    this.toolbarState = { ...this.toolbarState, renderMode: mode };
    this.unifiedMarkerService.setRenderMode(mode);
    // Re-apply the marker-type filter after swapping the renderer so the
    // heatmap respects the current Pictures / Types layer settings.
    this.applyUnifiedDefinitionExpression();
    const mv = this.mapComponentInstance?.mapViewInstance;
    if (mv) {
      if (mode === 'heatmap') {
        this.mapHeatmapContributionsService.activate(mv, this.reportGeometries || []);
        this.mapHeatmapContributionsService.setVisible(this.layerSettings.asBuilt);
      } else {
        this.mapHeatmapContributionsService.deactivate(mv);
      }
      // The heatmap layer is dynamic — if the Swipe widget was built earlier,
      // it won't know about the heatmap layer and will render it on both
      // sides. Rebuild the swipe to capture the new trailing layer set.
      this.refreshSwipeIfActive(mv);
    }
    this.updateUrlParamsForToolbar();
  }

  /**
   * Apply the Swipe widget using the user's currently-selected overlays as
   * the leading side and SiteNotes's native as-built rendering as trailing.
   */
  private applyProjectSwipe(mapView: __esri.MapView): void {
    const visible = new Set(this.toolbarState.visibleOverlayIds);
    // Ensure the selected overlays are on the map and visible; hide others.
    this.mapReferenceLayerService.showOnMap(mapView, e => visible.has(e.summary.id));
    const leading = this.mapReferenceLayerService.getActiveLayers()
      .filter(a => visible.has(a.summary.id))
      .map(a => a.layer);
    const trailing = this.collectAsBuiltLayers(mapView);
    if (!leading.length || !trailing.length) {
      this.mapSwipeService.deactivate(mapView);
      // eslint-disable-next-line no-console
      console.warn('[Swipe] Missing layers for Overlays-vs-As-builts swipe.',
        { planned: leading.length, asbuilt: trailing.length });
      this.applyOverlayVisibility();
      this.toolbarState = { ...this.toolbarState, swipePreset: 'off' };
      return;
    }
    this.mapSwipeService.activateWithLayers(mapView, {
      leadingLayers: leading,
      trailingLayers: trailing,
    });
  }

  /** Invoked by the Overlays popover when user toggles a reference layer's visibility. */
  onToolbarOverlayToggle(layerId: string): void {
    const entry = this.mapReferenceLayerService.getActiveLayers().find(item => item.summary.id === layerId);
    if (!entry) return;
    const current = this.toolbarState.visibleOverlayIds;
    const wasOn = current.includes(layerId);
    const next = wasOn
      ? current.filter(id => id !== layerId)
      : [...current, layerId];
    // swipeAvailable tracks "is there at least one overlay to compare?" —
    // shared with the Swipe button's enabled state.
    this.toolbarState = {
      ...this.toolbarState,
      visibleOverlayIds: next,
      swipeAvailable: next.length > 0,
    };
    if (entry.published) {
      const current = this.captureOverlayOperation();
      this.mapReferenceLayerService.savePublishedViewState(layerId, { visible: !wasOn }).catch(() => {
        if (!current()) return;
        this.toolbarState = { ...this.toolbarState, overlayError: 'Your layer preference could not be saved.' };
        this.restorePublishedOverlayState(layerId);
      });
    } else {
      this.overlayOverrides[layerId] = { ...this.overlayOverrides[layerId], visible: !wasOn };
      this.saveOverlayPreferences();
    }
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) return;

    if (this.toolbarState.swipePreset === 'project-design-vs-asbuilt') {
      // Swipe is live — if we just unchecked the last overlay, auto-disable
      // swipe since there's nothing left to compare. Otherwise rebuild the
      // widget with the new leading set.
      if (!next.length) {
        this.toolbarState = { ...this.toolbarState, swipePreset: 'off' };
        this.mapSwipeService.deactivate(mapView);
        this.applyOverlayVisibility();
      } else {
        this.applyProjectSwipe(mapView);
      }
    } else {
      this.applyOverlayVisibility();
    }

    // On toggle-on, pan/zoom to the overlay if it's off-screen so the user
    // actually sees what they just enabled. Reference layers are often miles
    // from the project site (e.g. a city-wide design KML vs one worksite),
    // and a silently-visible but off-screen layer reads as broken.
    if (!wasOn) this.zoomToOverlayIfOffscreen(layerId);

    this.updateUrlParamsForToolbar();
  }

  private async showDesignLayer(layerId: string): Promise<void> {
    const loaded = () => this.mapReferenceLayerService.getActiveLayers().some(item => item.summary.id === layerId);
    if (!loaded()) await this.refreshOverlays();
    if (!loaded()) return;
    if (this.toolbarState.visibleOverlayIds.includes(layerId)) this.zoomToOverlayIfOffscreen(layerId);
    else this.onToolbarOverlayToggle(layerId);
  }

  zoomToOverlayIfOffscreen(layerId: string): void {
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) return;
    const entry = this.mapReferenceLayerService.getActiveLayers()
      .find(a => a.summary.id === layerId);
    if (!entry) return;

    // Prefer the summary's stored extent (computed server-side from the full
    // FeatureCollection) because GroupLayer doesn't expose a unioned
    // fullExtent and individual child GeoJSONLayers only cover their own
    // geometry-type bucket.
    const saved = entry.summary.extent;
    let target: __esri.Extent | undefined;
    if (saved) {
      if (![saved.xmin, saved.ymin, saved.xmax, saved.ymax].every(Number.isFinite)
        || saved.xmin > saved.xmax || saved.ymin > saved.ymax
        || saved.xmin < -180 || saved.xmax > 180 || saved.ymin < -90 || saved.ymax > 90) return;
      // goTo requires a geometry instance; a plain bounds object can resolve
      // without moving the camera.
      target = new Extent({
        xmin: saved.xmin, ymin: saved.ymin, xmax: saved.xmax, ymax: saved.ymax,
        spatialReference: { wkid: 4326 },
      });
    } else {
      target = (entry.layer as __esri.Layer & { fullExtent?: __esri.Extent }).fullExtent;
    }
    if (!target) return;

    // goTo accepts extents in any spatial reference — it reprojects to the
    // view's SR automatically. We don't try to detect "already on screen" to
    // preserve the user's zoom, because Extent.intersects() compares raw
    // numbers without reprojecting (a WGS84 extent vs a Web Mercator view
    // always reads as "intersects"), so the shortcut is unreliable.
    mapView.goTo(target)
      .then(() => {
        // A published raster rendition (e.g. a GeoPDF sheet) is only
        // prepared for its layer's published zoom range — fitting the full
        // extent can land past maxZoom and make the layer vanish exactly
        // when the user asked to see it. Legacy summaries have no published
        // zoom range, so they're never clamped.
        const clampedZoom = clampZoomToPublishedMax(mapView.zoom, entry.published);
        if (clampedZoom === null) return undefined;
        return mapView.goTo({ center: target.center, zoom: clampedZoom });
      })
      .catch(() => { /* user may have panned */ });
  }

  /** Push current overlay toggle state into MapReferenceLayerService. */
  private applyOverlayVisibility(mapView = this.mapComponentInstance?.mapViewInstance): void {
    if (!mapView) return;
    const visible = new Set(this.toolbarState.visibleOverlayIds);
    this.mapReferenceLayerService.showOnMap(mapView, e => visible.has(e.summary.id));
  }

  /**
   * If the project Swipe is currently active, rebuild the widget so it picks
   * up any newly-added or removed as-built layers (e.g. the heatmap layer
   * that's created lazily when heatmap mode is toggled on).
   */
  private refreshSwipeIfActive(mapView: __esri.MapView): void {
    if (this.toolbarState.swipePreset === 'project-design-vs-asbuilt') {
      this.applyProjectSwipe(mapView);
    }
  }

  /**
   * Collect the SiteNotes-native layers that represent as-built data on the
   * map — unified marker pins (pictures, bid items, activities, deliveries),
   * site boundaries, report-geometry overlays, and the heatmap contributions
   * layer if active. These form the "trailing" side of the project swipe.
   */
  private collectAsBuiltLayers(mapView: __esri.MapView): __esri.Layer[] {
    const layers: __esri.Layer[] = [];
    if (this.unifiedMarkersLayer) layers.push(this.unifiedMarkersLayer);
    const instance = this.mapComponentInstance;
    if (instance?.mapProjectGraphicsLayer) layers.push(instance.mapProjectGraphicsLayer);
    if (instance?.mapMultiGeoGraphicsLayer) layers.push(instance.mapMultiGeoGraphicsLayer);
    if (instance?.mapMarkerGraphicsLayer) layers.push(instance.mapMarkerGraphicsLayer);
    const heatmap = mapView.map.findLayerById('heatmap-contributions-layer');
    if (heatmap) layers.push(heatmap);
    return layers;
  }

  onToolbarSwipePresetChange(preset: SwipePresetId): void {
    this.toolbarState = { ...this.toolbarState, swipePreset: preset };
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) {
      return;
    }
    if (preset === 'off') {
      this.mapSwipeService.deactivate(mapView);
      // Return to whatever overlays the user had toggled on via the Overlays
      // popover (may be empty — in which case everything is hidden).
      this.applyOverlayVisibility();
    } else if (preset === 'project-design-vs-asbuilt') {
      this.applyProjectSwipe(mapView);
    } else {
      this.mapSwipeService.activate(mapView, preset);
    }
    this.updateUrlParamsForToolbar();
  }

  onToolbarSpatialSelectToggle(active: boolean): void {
    this.toolbarState = { ...this.toolbarState, spatialSelectActive: active };
    const mapView = this.mapComponentInstance?.mapViewInstance;
    if (!mapView) {
      return;
    }
    this.projectSpatialSummaryService.setActive(active, this.spatialSummaryDateLabel);
    // sourceLayer may be null on projects/date-ranges with no bid-item markers
    // (e.g. as-built lines only). Still activate the widget so the user can
    // draw — the spatial query just finds nothing and emits [].
    const sourceLayer = this.unifiedMarkerService.getLayer();
    // As-built geometry participates too — markers sit at a group centroid, so
    // markers alone let a polygon drawn around an item's own line miss it.
    const geometryLayer = this.mapComponentInstance?.mapMultiGeoGraphicsLayer || null;
    if (active) {
      this.mapSpatialSelectService.activate(mapView, sourceLayer, geometryLayer);
    } else {
      this.mapSpatialSelectService.deactivate(mapView);
    }
  }

  /**
   * Open the "Add overlay" dialog in response to the `+` button inside the
   * Overlays popover. On success we re-activate reference layers for the
   * project so the new KML surfaces in the popover list and can be toggled
   * onto the map.
   */
  onAddOverlayClick(): void {
    // Prefer the route param (always set in fullscreen direct-URL loads where
    // projectData may still be null) and fall back to projectData otherwise.
    const projectId =
      this.route.snapshot.params['projectId']
      || this.route.snapshot.parent?.params['projectId']
      || this.projectData?.id;
    if (!projectId) return;

    // Pass projectId explicitly — the dialog opens above the fullscreen map
    // route, where its own ActivatedRoute can't resolve the project id.
    this.projectsService.openRightPanel.next({
      component: CreateOrEditMapLayerComponent,
      data: { projectId },
    });
  }

  /**
   * Warranty claim pin. Same 26x36 droplet and thin #E9ECF4 outline glyph as
   * the bid-item and issue pins on this map (assets/icons/map/markers/*.svg),
   * with a document outline (page, folded corner, two text lines) centred in
   * the head. A small Primary Blue count badge is drawn at top-right when
   * multiple claims stack at one location.
   */
  private buildClaimPinSymbol(count: number = 1): PictureMarkerSymbol {
    const fill = '#1036D6';
    const stroke = '#E9ECF4';
    const w = 26;
    const h = 36;

    const pinPath = 'M26 12.4328C26 5.56411 20.1821 0 13 0C5.81876 0 0 5.56411 0 12.4328C0 19.4259 12.9992 36 12.9992 36C12.9992 36 26 19.5237 26 12.4328Z';

    // Outline document, ~10x12.5, centred on (12.875, 13) like the sibling
    // glyphs (the bid-item cube spans ~10.7 units).
    const glyph = `
      <path d="M7.875 6.75H14.875L17.875 9.75V19.25H7.875V6.75Z" stroke="${stroke}" stroke-linejoin="round" fill="none"/>
      <path d="M14.875 6.75V9.75H17.875" stroke="${stroke}" stroke-linejoin="round" fill="none"/>
      <path d="M10.125 13.25H15.625" stroke="${stroke}" stroke-linecap="round" fill="none"/>
      <path d="M10.125 15.75H15.625" stroke="${stroke}" stroke-linecap="round" fill="none"/>
    `;

    const badge = count > 1
      ? `<circle cx="${w - 6}" cy="6" r="6" fill="#1036D6" stroke="#FFFFFF" stroke-width="1.5"/>
         <text x="${w - 6}" y="9" text-anchor="middle" font-family="Montserrat, Arial, sans-serif" font-size="8" font-weight="700" fill="#FFFFFF">${count > 99 ? '99+' : count}</text>`
      : '';

    // Intrinsic size is 4x the drawn size, like the @4x PNGs the bid-item and
    // issue stencils use: ESRI rasterises the image at its natural size and
    // scales down, so a 26x36 SVG comes out soft on retina while 104x144 is crisp.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w * 4}" height="${h * 4}" viewBox="0 0 ${w} ${h}">
        <path d="${pinPath}" fill="${fill}"/>
        ${glyph}
        ${badge}
      </svg>`;
    // Sizes as px strings: a bare number is POINTS to the ArcGIS API (26pt is
    // ~35px), which made this pin a third larger than the bid-item stencil's
    // '26px'/'36px'.
    return new PictureMarkerSymbol({
      url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
      width: `${w}px`,
      height: `${h}px`,
      yoffset: `${h / 2}px`,
    });
  }

  private showClaimPopupAt(graphic: any, event: any, fromHover = false): void {
    if (this.claimPopupHideTimeout) {
      clearTimeout(this.claimPopupHideTimeout);
      this.claimPopupHideTimeout = null;
    }
    // Only one popup at a time — hide the issue popup if open.
    if (this.showIssuePopup) this.hideIssuePopup();
    const claim = graphic.attributes;
    const grouped = claim?._groupedClaims as any[] | undefined;
    const uuid = grouped && grouped.length > 1
      ? `group:${claim?.latitude}:${claim?.longitude}:${grouped.length}`
      : String(claim?.id ?? claim?.claim_number ?? '');
    if (fromHover && uuid && uuid === this.hoveredClaimUuid && this.showClaimPopup) {
      this.claimPopupScreenPoint = { x: event.x, y: event.y };
      return;
    }
    this.hoveredClaimUuid = uuid || null;
    this.claimPopupData = claim;
    this.claimPopupClaims = grouped && grouped.length > 1 ? grouped : null;
    this.claimPopupScreenPoint = { x: event.x, y: event.y };
    this.showClaimPopup = true;
  }

  onClaimPopupSelect(claim: any): void {
    this.hideClaimPopup();
    if (this.isFullScreen) {
      if (claim?._kind === 'punchlist') {
        this.mapService.showPunchlistItemDetail$.next({ item: claim });
      } else {
        this.mapService.showClaimDetail$.next({ claim });
      }
    }
  }

  private scheduleHideClaimPopup(): void {
    if (this.claimPopupHideTimeout) return;
    this.claimPopupHideTimeout = setTimeout(() => {
      this.hideClaimPopup();
    }, 150);
  }

  hideClaimPopup(): void {
    if (this.claimPopupHideTimeout) {
      clearTimeout(this.claimPopupHideTimeout);
      this.claimPopupHideTimeout = null;
    }
    this.showClaimPopup = false;
    this.claimPopupData = null;
    this.claimPopupClaims = null;
    this.hoveredClaimUuid = null;
  }

  // -------------------------------------------------------------------------
  // Warranty issue markers (mirror of warranty claims)
  // -------------------------------------------------------------------------

  private drawWarrantyIssueMarkers(markers: any[]): void {
    // `linkedOnly` (spec §7.3) scopes the unified capture layer only: warranty/punchlist markers come from the warranty endpoint without `map_feature_link`, like their claim siblings.
    if (!this.mapComponentInstance?.mapViewInstance) {
      return;
    }
    const mapView = this.mapComponentInstance.mapViewInstance;

    if (this.warrantyIssueFeatureLayer) {
      mapView.map.remove(this.warrantyIssueFeatureLayer);
      this.warrantyIssueFeatureLayer = null;
    }
    if (this.warrantyIssueClickHandler) {
      this.warrantyIssueClickHandler.remove();
      this.warrantyIssueClickHandler = null;
    }
    if (this.warrantyIssueHoverHandler) {
      this.warrantyIssueHoverHandler.remove();
      this.warrantyIssueHoverHandler = null;
    }
    this.hideIssuePopup();

    if (!markers || markers.length === 0) {
      this.syncMarkerClusterZoomWatcher();
      return;
    }

    const layer = new GraphicsLayer({ id: 'warranty-issues-layer' });
    this.populateIssueLayerGraphics(layer, markers);

    mapView.map.add(layer);
    this.warrantyIssueFeatureLayer = layer;
    this.syncMarkerClusterZoomWatcher();

    this.warrantyIssueClickHandler = mapView.on('click', (event: any) => {
      // Prefer claim/item marker if one is at the same location (e.g., punchlist mode
      // where item + linked issue share coordinates). Let the claim handler take it.
      const claimLayer = this.warrantyClaimFeatureLayer;
      const includeLayers = claimLayer ? [layer, claimLayer] : [layer];
      mapView.hitTest(event, { include: includeLayers }).then((response: any) => {
        const claimHit = response.results?.find((r: any) => r.graphic?.attributes?._markerType === 'warranty_claim');
        if (claimHit) {
          this.hideIssuePopup();
          return;
        }
        const hit = response.results?.find((r: any) => r.graphic?.attributes?._markerType === 'warranty_issue');
        if (!hit?.graphic) {
          this.hideIssuePopup();
          return;
        }
        if (hit.graphic.attributes?._isCluster) {
          this.hideIssuePopup();
          this.zoomIntoMarkerCluster(hit.graphic);
          return;
        }
        const grouped = (hit.graphic.attributes?._groupCount || 1) > 1;
        if (this.isFullScreen && !grouped) {
          this.hideIssuePopup();
          this.mapService.showIssueDetail$.next({ issue: hit.graphic.attributes });
        } else if (!grouped && hit.graphic.attributes?._kind === 'issue') {
          // Issues tab pin on the header map: open it the way its list row does.
          this.hideIssuePopup();
          this.projectsService.issueMapPinSelected.next(hit.graphic.attributes);
        } else {
          this.showIssuePopupAt(hit.graphic, event);
        }
      });
    });

    this.warrantyIssueHoverHandler = mapView.on('pointer-move', (event: any) => {
      const claimLayer = this.warrantyClaimFeatureLayer;
      const includeLayers = claimLayer ? [layer, claimLayer] : [layer];
      mapView.hitTest(event, { include: includeLayers }).then((response: any) => {
        const claimHit = response.results?.find((r: any) => r.graphic?.attributes?._markerType === 'warranty_claim');
        if (claimHit) {
          // Claim hover handler will take over; don't show issue hover.
          if (this.hoveredIssueUuid) this.scheduleHideIssuePopup();
          return;
        }
        const hit = response.results?.find((r: any) => r.graphic?.attributes?._markerType === 'warranty_issue');
        if (hit?.graphic?.attributes?._isCluster) {
          mapView.container.style.cursor = 'pointer';
          if (this.hoveredIssueUuid) this.scheduleHideIssuePopup();
        } else if (hit?.graphic) {
          mapView.container.style.cursor = 'pointer';
          this.showIssuePopupAt(hit.graphic, event, /* fromHover */ true);
        } else if (this.hoveredIssueUuid) {
          mapView.container.style.cursor = '';
          this.scheduleHideIssuePopup();
        }
      });
    });
  }

  /**
   * Issue pin matching the Figma design (issues.svg): 26x36 droplet with a
   * circle-with-exclamation glyph. A small Primary Blue count badge is drawn
   * at top-right when multiple issues stack at one location.
   */
  private buildIssuePinSymbol(count: number = 1): PictureMarkerSymbol {
    const fill = '#1036D6';
    const stroke = '#E9ECF4';
    const w = 26;
    const h = 36;

    const pinPath = 'M26 12.4328C26 5.56411 20.1821 0 13 0C5.81876 0 0 5.56411 0 12.4328C0 19.4259 12.9992 36 12.9992 36C12.9992 36 26 19.5237 26 12.4328Z';

    const glyph = `
      <path d="M12.875 11V13" stroke="${stroke}" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      <path d="M12.875 15V15.6667" stroke="${stroke}" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      <circle cx="12.875" cy="12.9997" r="6.16667" stroke="${stroke}" fill="none"/>
    `;

    const badge = count > 1
      ? `<circle cx="${w - 6}" cy="6" r="6" fill="#1036D6" stroke="#FFFFFF" stroke-width="1.5"/>
         <text x="${w - 6}" y="9" text-anchor="middle" font-family="Montserrat, Arial, sans-serif" font-size="8" font-weight="700" fill="#FFFFFF">${count > 99 ? '99+' : count}</text>`
      : '';

    // Intrinsic size is 4x the drawn size, like the @4x PNGs the bid-item and
    // issue stencils use: ESRI rasterises the image at its natural size and
    // scales down, so a 26x36 SVG comes out soft on retina while 104x144 is crisp.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w * 4}" height="${h * 4}" viewBox="0 0 ${w} ${h}">
        <path d="${pinPath}" fill="${fill}"/>
        ${glyph}
        ${badge}
      </svg>`;
    // px strings, not bare numbers (points) -- see buildClaimPinSymbol.
    return new PictureMarkerSymbol({
      url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
      width: `${w}px`,
      height: `${h}px`,
      yoffset: `${h / 2}px`,
    });
  }

  private showIssuePopupAt(graphic: any, event: any, fromHover = false): void {
    if (this.issuePopupHideTimeout) {
      clearTimeout(this.issuePopupHideTimeout);
      this.issuePopupHideTimeout = null;
    }
    // Only one popup at a time — hide the claim popup if open.
    if (this.showClaimPopup) this.hideClaimPopup();
    const issue = graphic.attributes;
    const grouped = issue?._groupedIssues as any[] | undefined;
    const uuid = grouped && grouped.length > 1
      ? `group:${issue?.latitude}:${issue?.longitude}:${grouped.length}`
      : String(issue?.id ?? '');
    if (fromHover && uuid && uuid === this.hoveredIssueUuid && this.showIssuePopup) {
      this.issuePopupScreenPoint = { x: event.x, y: event.y };
      return;
    }
    this.hoveredIssueUuid = uuid || null;
    this.issuePopupData = issue;
    this.issuePopupIssues = grouped && grouped.length > 1 ? grouped : null;
    this.issuePopupScreenPoint = { x: event.x, y: event.y };
    this.showIssuePopup = true;
  }

  /**
   * The Issues tab's no-location badge: lists the matching issues with no
   * location in the item popup the bid item / activity / delivery badge uses
   * (onNoLocationBadgeClick), one ghost issue card each. "See more" opens the
   * issue (onBidItemSeeMore). The issues come from GET /v1/issue with the
   * for-map query, so the list works without the Issues tab on screen
   * (fullscreen map). Fullscreen only.
   */
  onIssuesNoLocationBadgeClick(event: MouseEvent): void {
    const result = this.issuesMapResult;
    const ids = new Set((result?.meta?.no_location_ids || []).map(id => String(id)));
    // Fullscreen only, like the project map's ghost-location count: on the
    // half-height header map the badge just shows the count.
    if (!this.isFullScreen || !result || !ids.size || this.issuesNoLocationLoading) {
      return;
    }
    const badge = event.currentTarget as HTMLElement;
    const rect = badge.getBoundingClientRect();
    const query = JSON.parse(result.key) as Record<string, string>;
    const categories = this.issueCategoryLabels;
    this.issuesNoLocationLoading = true;
    this.issueService.fetchRecords(new Project({ id: result.projectId }), { pageIndex: 0, pageSize: 1000 }, { ...query })
      .then(resp => {
        if (this.issuesMapResult?.key !== result.key) return;
        const rows = (resp?.result || []).filter(issue => issueHasNoLocation(issue as { id?: string; uuid?: string }, ids));
        if (!rows.length) return;
        this.itemPopupContent = rows.map(issue => issueNoLocationPopup(issue, {
          categoryLabels: categories,
          timezone: (this.projectData as { timezone?: string } | null)?.timezone,
        }));
        // Anchored under the badge like the bid item / activity / delivery list.
        this.itemPopupScreenPoint = { x: rect.left, y: rect.bottom + 8 };
        this.showItemPopup = true;
      })
      .catch(() => { /* the badge stays; nothing to list */ })
      .finally(() => (this.issuesNoLocationLoading = false));
  }

  onIssuePopupSelect(issue: any): void {
    this.hideIssuePopup();
    if (this.isFullScreen) {
      this.mapService.showIssueDetail$.next({ issue });
    } else if (issue?._kind === 'issue' && issue?.id) {
      this.projectsService.issueMapPinSelected.next(issue);
    }
  }

  onIssuePopupMouseEnter(): void {
    if (this.issuePopupHideTimeout) {
      clearTimeout(this.issuePopupHideTimeout);
      this.issuePopupHideTimeout = null;
    }
  }

  onIssuePopupMouseLeave(): void {
    this.scheduleHideIssuePopup();
  }

  private scheduleHideIssuePopup(): void {
    if (this.issuePopupHideTimeout) return;
    this.issuePopupHideTimeout = setTimeout(() => {
      this.hideIssuePopup();
    }, 150);
  }

  hideIssuePopup(): void {
    if (this.issuePopupHideTimeout) {
      clearTimeout(this.issuePopupHideTimeout);
      this.issuePopupHideTimeout = null;
    }
    this.showIssuePopup = false;
    this.issuePopupData = null;
    this.issuePopupIssues = null;
    this.hoveredIssueUuid = null;
  }

  /**
   * Re-fetch reference layers for the current project and refresh the
   * toolbar's overlay list. Reuses activateForProject() so newly-uploaded
   * KMLs are built into ArcGIS layer instances just like the originals.
   * Preserves the user's current visible-overlay selection where it still
   * maps to an existing layer.
   */
  /** Resolves once the view is ready and neither animating nor mid-gesture (bounded to a few seconds). */
  private async waitForCameraToSettle(view: __esri.MapView): Promise<void> {
    try {
      await view.when();
      // Give a queued initial fit a moment to start before checking stillness.
      await new Promise<void>(resolve => setTimeout(resolve, 80));
      await Promise.race([
        reactiveUtils.whenOnce(() => view.stationary && !view.animation),
        new Promise<void>(resolve => setTimeout(resolve, 4000)),
      ]);
    } catch { /* A destroyed view rejects; the caller's generation guard handles it. */ }
  }

  async refreshOverlays(mapView = this.mapComponentInstance?.mapViewInstance): Promise<void> {
    const projectId = this.route.snapshot.params['projectId']
      || this.route.snapshot.parent?.params['projectId'] || this.projectData?.id;
    if (!mapView || !projectId) return;
    const generation = ++this.overlayLoadGeneration;
    const accountId = this.authService.getCurrentUser()?.id;
    const key = layerPreferenceKey(accountId, projectId);
    const current = this.captureOverlayOperation();
    if (key !== this.overlayPreferenceKey) {
      this.overlayPreferenceKey = key;
      this.overlayOverrides = {};
      try {
        const saved = key ? JSON.parse(localStorage.getItem(key) || '{}') : {};
        if (saved && typeof saved === 'object' && !Array.isArray(saved)) this.overlayOverrides = saved;
      } catch { /* Unavailable or invalid personal storage uses project defaults. */ }
      this.toolbarState = { ...this.toolbarState, visibleOverlayIds: [] };
    }
    this.toolbarState = { ...this.toolbarState, overlayLoading: true, overlayError: undefined };
    if (this.toolbarState.swipePreset === 'project-design-vs-asbuilt') {
      this.mapSwipeService.deactivate(mapView);
    }
    try {
      // The map opens on a wide default camera and animates to the sites.
      // Layers added during that flight get their first raster scaled with
      // the animation, so a 9 px marker balloons into a screen-filling
      // shape for a second. Add them once the camera has settled.
      await this.waitForCameraToSettle(mapView);
      if (generation !== this.overlayLoadGeneration || !current()) return;
      const active = await this.mapReferenceLayerService.activateForProject(mapView, projectId, accountId,
        () => this.authService.getCurrentUser()?.id);
      if (generation !== this.overlayLoadGeneration || !current()) return;
      const visible = active.filter(entry => {
        const view = entry.published
          ? this.mapReferenceLayerService.layerState(entry.published)
          : resolveLayerState({ visible: false, opacity: 1, labels: false },
            this.overlayOverrides[entry.summary.id]);
        this.mapReferenceLayerService.setOpacity(entry.summary.id, view.opacity);
        return view.visible;
      }).map(entry => entry.summary.id);
      this.toolbarState = { ...this.toolbarState, visibleOverlayIds: visible,
        swipeAvailable: visible.length > 0, overlayLoading: false };
      this.updateOverlayRows();
      this.applyOverlayVisibility(mapView);
      this.refreshSwipeIfActive(mapView);
      const firstLoadForProject = this.knownPublishedScope !== `${projectId}:${accountId}`;
      this.fitToNewlyPublishedLayer(active, visible, `${projectId}:${accountId}`);
      if (firstLoadForProject) this.widenOpeningExtentForDesignLayers(mapView);
    } catch (error) {
      if (generation !== this.overlayLoadGeneration || !current()) return;
      this.toolbarState = { ...this.toolbarState, overlayLayers: [], visibleOverlayIds: [],
        swipeAvailable: false, overlayLoading: false,
        swipePreset: this.toolbarState.swipePreset === 'project-design-vs-asbuilt'
          ? 'off' : this.toolbarState.swipePreset,
        overlayError: error?.status === 403
          ? 'Project layers are not available for your account.'
          : 'Unable to load project layers. Please try again.' };
    }
  }

  /**
   * A layer that was just published should be on screen: the publisher is
   * usually watching this map from the settings drawer behind it. Only a
   * catalog reload that brings an id the previous one lacked counts, so the
   * first load of a project, a withdrawal and a reorder all leave the camera
   * where it is. zoomToOverlayIfOffscreen() clamps to the layer's published
   * maximum, so the map never lands past the zoom its tiles cover.
   */
  private fitToNewlyPublishedLayer(active: { summary: { id: string }; published?: unknown }[],
    visibleIds: string[], scope: string): void {
    const publishedIds = active.filter(entry => entry.published).map(entry => entry.summary.id);
    const revision = this.mapReferenceLayerService.getPublishedInspectionScope()?.catalogRevision ?? null;
    const sameScope = this.knownPublishedScope === scope;
    const known = sameScope ? this.knownPublishedLayerIds : null;
    const knownRevision = sameScope ? this.knownCatalogRevision : null;
    this.knownPublishedScope = scope;
    this.knownPublishedLayerIds = new Set(publishedIds);
    this.knownCatalogRevision = revision;
    // The overlay load can run more than once while a project opens (an
    // interim load with no catalog yet, then the real one). Only a catalog
    // revision newer than the one already seen can be a publish; anything
    // else is the same catalog arriving again and must leave the camera alone.
    if (!known || knownRevision === null || revision === null || revision <= knownRevision) return;
    // Catalog order puts the newest addition last, and a layer that publishes
    // hidden is left alone: fitting to something invisible reads as a glitch.
    const added = publishedIds.filter(id => !known.has(id) && visibleIds.includes(id));
    if (added.length) this.zoomToOverlayIfOffscreen(added[added.length - 1]);
  }

  /**
   * Union of the visible published design layers' extents (WGS84), or null.
   * The opening fit includes them: a project whose design lives a few streets
   * from the site marker should open showing both, not the marker alone.
   */
  private visibleDesignLayerExtent(): Extent | null {
    const visible = new Set(this.toolbarState.visibleOverlayIds);
    let union: Extent | null = null;
    for (const entry of this.mapReferenceLayerService.getActiveLayers()) {
      const saved = entry.summary.extent;
      if (!entry.published || !visible.has(entry.summary.id) || !saved) continue;
      if (![saved.xmin, saved.ymin, saved.xmax, saved.ymax].every(Number.isFinite)
        || saved.xmin > saved.xmax || saved.ymin > saved.ymax
        || saved.xmin < -180 || saved.xmax > 180 || saved.ymin < -90 || saved.ymax > 90) continue;
      const extent = new Extent({ xmin: saved.xmin, ymin: saved.ymin, xmax: saved.xmax, ymax: saved.ymax,
        spatialReference: { wkid: 4326 } });
      union = union ? union.union(extent) : extent;
    }
    return union;
  }

  /** The opening extent widened to take in the visible design layers. */
  private includeVisibleDesignLayers(extent: Extent): Extent {
    const layers = this.visibleDesignLayerExtent();
    if (!layers) return extent;
    const wgs84 = extent.spatialReference?.isWebMercator
      ? webMercatorUtils.webMercatorToGeographic(extent) as Extent : extent;
    return wgs84.union(layers);
  }

  /**
   * Design layers that finish loading after the map has already settled on
   * its opening extent: widen the view once to take them in, unless the user
   * has moved the map since, in which case the camera is theirs.
   */
  private widenOpeningExtentForDesignLayers(mapView: __esri.MapView): void {
    if (this.userMovedMapSinceOpen || !this.hasPerformedInitialZoom || !mapView?.extent) return;
    const layers = this.visibleDesignLayerExtent();
    if (!layers) return;
    const view = webMercatorUtils.webMercatorToGeographic(mapView.extent) as Extent;
    if (view.contains(layers)) return;
    const goToOptions = {
      duration: 800, easing: 'ease-in-out' as const, animate: true,
      padding: { top: 120, left: this.isFullScreen ? 452 : 0, right: 0, bottom: 0 },
    };
    const widened = view.union(layers).expand(1.15);
    this.openingGoTo(mapView, () => widened, goToOptions).catch(() => { /* interrupted by the user */ });
  }

  private updateOverlayRows(): void {
    this.toolbarState = { ...this.toolbarState, overlayLayers:
      this.mapReferenceLayerService.getActiveLayers().map(entry => ({
        id: entry.summary.id, name: entry.summary.name, opacity: entry.layer.opacity,
        group: entry.published ? this.mapReferenceLayerService.publishedGroupName(entry.published) : undefined,
        labels: entry.published?.assets.some(asset => asset.role === 'labels')
          ? this.mapReferenceLayerService.layerState(entry.published).labels : undefined,
        extent: entry.summary.extent, status: entry.status, error: entry.error, drawing: entry.published?.drawing,
        // Every published layer is from the catalog, so that says nothing; only a
        // legacy overlay needs its source and online-only status called out.
        sourceLabel: entry.published ? undefined : `${entry.summary.source_type === 'arcgis_url' ? 'ArcGIS' :
          entry.summary.source_type === 'kml_url' ? 'KML / KMZ' : entry.summary.source_type === 'image_upload' ? 'Image' : 'GeoJSON'} · Online only`,
      })) };
    this.cdr.markForCheck();
  }

  onOverlayOpacityChange(change: { id: string; opacity: number }): void {
    if (!Number.isFinite(change.opacity) || change.opacity < 0 || change.opacity > 1) return;
    const entry = this.mapReferenceLayerService.getActiveLayers().find(item => item.summary.id === change.id);
    if (!entry) return;
    this.mapReferenceLayerService.setOpacity(change.id, change.opacity);
    if (entry.published) {
      const current = this.captureOverlayOperation();
      this.mapReferenceLayerService.savePublishedViewState(change.id, { opacity: change.opacity }).catch(() => {
        if (!current()) return;
        this.toolbarState = { ...this.toolbarState, overlayError: 'Your layer preference could not be saved.' };
        this.restorePublishedOverlayState(change.id);
      });
    } else {
      this.overlayOverrides[change.id] = { ...this.overlayOverrides[change.id], opacity: change.opacity };
    }
    this.updateOverlayRows();
    if (!entry.published) this.saveOverlayPreferences();
  }

  onOverlayLabelsToggle(layerId: string): void {
    const entry = this.mapReferenceLayerService.getActiveLayers().find(item => item.summary.id === layerId);
    if (!entry?.published?.assets.some(asset => asset.role === 'labels')) return;
    const labels = !this.mapReferenceLayerService.layerState(entry.published).labels;
    this.mapReferenceLayerService.setLabels(layerId, labels);
    const current = this.captureOverlayOperation();
    this.mapReferenceLayerService.savePublishedViewState(layerId, { labels }).catch(() => {
      if (!current()) return;
      this.toolbarState = { ...this.toolbarState, overlayError: 'Your label preference could not be saved.' };
      this.restorePublishedOverlayState(layerId);
    });
    this.updateOverlayRows();
  }

  resetProjectLayerView(): void {
    const current = this.captureOverlayOperation();
    this.overlayOverrides = {};
    if (this.overlayPreferenceKey) {
      try { localStorage.removeItem(this.overlayPreferenceKey); } catch { /* Storage can be disabled. */ }
    }
    this.onToolbarSwipePresetChange('off');
    if (this.mapReferenceLayerService.getActiveLayers().some(entry => !!entry.published)) {
      const reset = this.mapReferenceLayerService.resetPublishedViewState();
      // Published reset is optimistic. Legacy defaults apply immediately too.
      this.reconcileOverlayViewState();
      reset.then(() => {
        if (current()) this.reconcileOverlayViewState();
      }).catch(() => {
        if (!current()) return;
        this.toolbarState = { ...this.toolbarState, overlayError: 'Your published layer preferences could not be reset.' };
        this.reconcileOverlayViewState();
      });
      return;
    }
    this.reconcileOverlayViewState();
  }

  private reconcileOverlayViewState(): void {
    const visible = this.mapReferenceLayerService.getActiveLayers().filter(entry => {
      const state = entry.published ? this.mapReferenceLayerService.layerState(entry.published)
        : resolveLayerState({ visible: false, opacity: 1, labels: false }, this.overlayOverrides[entry.summary.id]);
      this.mapReferenceLayerService.setOpacity(entry.summary.id, state.opacity);
      if (entry.published) this.mapReferenceLayerService.setLabels(entry.summary.id, state.labels);
      return state.visible;
    }).map(entry => entry.summary.id);
    this.toolbarState = { ...this.toolbarState, visibleOverlayIds: visible, swipeAvailable: visible.length > 0 };
    this.updateOverlayRows();
    this.applyOverlayVisibility();
    this.updateUrlParamsForToolbar();
  }

  private saveOverlayPreferences(): void {
    if (!this.overlayPreferenceKey) return;
    try { localStorage.setItem(this.overlayPreferenceKey, JSON.stringify(this.overlayOverrides)); }
    catch { /* Map controls still work if browser storage is disabled. */ }
  }

  private captureOverlayOperation(): () => boolean {
    const generation = this.overlayLoadGeneration;
    const accountId = this.authService.getCurrentUser()?.id;
    const project = () => this.route.snapshot.params['projectId']
      || this.route.snapshot.parent?.params['projectId'] || this.projectData?.id;
    const projectId = project();
    return () => generation === this.overlayLoadGeneration && accountId === this.authService.getCurrentUser()?.id
      && projectId === project();
  }

  /** Reconcile optimistic published controls after a rejected CAS update. */
  private restorePublishedOverlayState(layerId: string): void {
    const entry = this.mapReferenceLayerService.getActiveLayers().find(item => item.summary.id === layerId);
    if (!entry?.published) return;
    const state = this.mapReferenceLayerService.layerState(entry.published);
    this.mapReferenceLayerService.setOpacity(layerId, state.opacity);
    this.mapReferenceLayerService.setLabels(layerId, state.labels);
    const without = this.toolbarState.visibleOverlayIds.filter(id => id !== layerId);
    const visible = state.visible ? [...without, layerId] : without;
    this.toolbarState = { ...this.toolbarState, visibleOverlayIds: visible, swipeAvailable: visible.length > 0 };
    this.updateOverlayRows();
    this.applyOverlayVisibility();
  }

  get canManageProjectLayers(): boolean {
    const projectId = this.route.snapshot.params['projectId']
      || this.route.snapshot.parent?.params['projectId'] || this.projectData?.id;
    return !!projectId && this.ability.can('create', subject('ProjectReferenceLayer', { project_id: projectId }));
  }

  /** Polygon behind the current area summary; cropped into the PDF screenshot. */
  private lastSpatialPolygon: __esri.Polygon | null = null;

  get spatialSummaryDateLabel(): string {
    const r = this._dateRange;
    if (!r?.start || !r?.end) return '';
    const fmt = (d: Date) => `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}`;
    return `${fmt(r.start)} – ${fmt(r.end)}, ${r.end.getFullYear()}`;
  }

  /** Run the spatial summary API against the drawn polygon. */
  private fetchSpatialSummary(polygon: __esri.Polygon): void {
    // Prefer the route param — on direct-URL fullscreen loads projectData can
    // still be null when the first polygon completes, which silently skipped
    // the summary (and left nothing to export).
    const projectId =
      this.route.snapshot.params['projectId']
      || this.route.snapshot.parent?.params['projectId']
      || this.projectData?.id;
    const range = this._dateRange;
    if (!projectId || !range?.start || !range?.end) {
      return;
    }
    // ArcGIS polygons on a WebMercator view come back in Web Mercator; the
    // API expects WGS84 GeoJSON, so convert before serializing.
    const wgs = polygon.spatialReference?.wkid === 4326
      ? polygon
      : webMercatorUtils.webMercatorToGeographic(polygon) as __esri.Polygon;
    const geojson = {
      type: 'Polygon' as const,
      coordinates: wgs.rings,
    };
    const isoDate = (d: Date) => d.toISOString().slice(0, 10);
    this.lastSpatialPolygon = polygon;
    // Drilled into one report's bid item: the summary covers only that item's
    // geometry, so the panel answers "how much of this item is in the area".
    const drilled = this.selectedItemData && !this.selectedItemData._lightweight
      ? this.selectedItemData
      : null;
    const scope = drilled?.reportId && drilled?.bid_item?.id
      ? { reportId: drilled.reportId, bidItemId: drilled.bid_item.id }
      : {};
    this.projectSpatialSummaryService.setLoading();
    this.projectSpatialSummaryService.fetch(projectId, {
      polygon: geojson,
      startDate: isoDate(range.start),
      endDate: isoDate(range.end),
      // Map is rendering the same 3 statuses the fullscreen URL uses.
      statuses: ['submitted-reviewed', 'approved', 'draft'],
      ...scope,
    })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: result => this.projectSpatialSummaryService.setResult(result),
        error: err => this.projectSpatialSummaryService.setError(err?.message || 'Failed to load summary'),
      });
  }

  /**
   * Capture the live map (basemap, drawn polygon, markers — UI widgets are
   * excluded) and hand it to the service, which posts it with the summary
   * request and saves the PDF. A failed screenshot still exports, just
   * without the map.
   */
  private async exportSpatialSummaryPdf(): Promise<void> {
    const mapView = this.mapComponentInstance?.mapViewInstance as __esri.MapView | undefined;
    let mapImage: string | null = null;
    if (mapView) {
      try {
        const area = this.spatialSummaryScreenshotArea(mapView);
        const shot = await mapView.takeScreenshot({ format: 'png', ...(area ? { area } : {}) });
        mapImage = shot?.dataUrl || null;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SpatialSummary] map screenshot failed; exporting without map', err);
      }
    }
    this.projectSpatialSummaryService.exportPdf(mapImage);
  }

  /**
   * Screen-space crop for the export screenshot: the drawn polygon's extent
   * padded by a third on each side, clamped to the view. Returns null when
   * the polygon is off-screen or unknown so the caller falls back to the
   * full view.
   */
  private spatialSummaryScreenshotArea(
    mapView: __esri.MapView,
  ): { x: number; y: number; width: number; height: number } | null {
    const extent = this.lastSpatialPolygon?.extent;
    if (!extent || !mapView.width || !mapView.height) {
      return null;
    }
    const corners = [
      mapView.toScreen({ x: extent.xmin, y: extent.ymin, spatialReference: extent.spatialReference } as __esri.Point),
      mapView.toScreen({ x: extent.xmax, y: extent.ymax, spatialReference: extent.spatialReference } as __esri.Point),
    ];
    if (corners.some(c => !c || !Number.isFinite(c.x) || !Number.isFinite(c.y))) {
      return null;
    }
    const minX = Math.min(corners[0].x, corners[1].x);
    const maxX = Math.max(corners[0].x, corners[1].x);
    const minY = Math.min(corners[0].y, corners[1].y);
    const maxY = Math.max(corners[0].y, corners[1].y);
    const padX = Math.max((maxX - minX) / 3, 80);
    const padY = Math.max((maxY - minY) / 3, 80);
    const x = Math.max(0, Math.floor(minX - padX));
    const y = Math.max(0, Math.floor(minY - padY));
    const width = Math.min(mapView.width, Math.ceil(maxX + padX)) - x;
    const height = Math.min(mapView.height, Math.ceil(maxY + padY)) - y;
    if (width <= 0 || height <= 0) {
      return null;
    }
    return { x, y, width, height };
  }

  /**
   * Serialize the advanced-widgets toolbar state into URL query params so the
   * chosen render mode / swipe preset survives a page reload. "Pins" and "off"
   * are the defaults and are written as null (which strips the param).
   */
  private updateUrlParamsForToolbar(): void {
    const queryParams: Params = {
      renderMode: this.toolbarState.renderMode === 'pins' ? null : this.toolbarState.renderMode,
      swipe: this.toolbarState.swipePreset === 'off' ? null : this.toolbarState.swipePreset,
    };
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
